import { Component, Injector, EventEmitter, Output, Input, Renderer2, ViewChild, ChangeDetectionStrategy, ChangeDetectorRef, ViewChildren, QueryList, NgZone } from '@angular/core';
import { FormGroup, FormControl, Validators, ValidatorFn } from '@angular/forms';
import { FieldGrandTotal, DynamicDataServiceProxy, SaveDynamicDataRequest, RetrieveDynamicDataRequest, DynamicGridStateServiceProxy, GridStateDto, SetDynamicGridStateInput, SetGridStateInput } from '@shared/service-proxies/service-proxies';
import { fromEvent, Observable, of, Subscription } from 'rxjs';
import { finalize, catchError, tap, take } from 'rxjs/operators';
import { ExcelExportData } from '@progress/kendo-angular-excel-export';
import { GroupDescriptor, process, SortDescriptor } from '@progress/kendo-data-query';
import { AppComponentBase } from '@shared/common/app-component-base';
import { GridComponent, DataStateChangeEvent, PageChangeEvent, CellCloseEvent, SelectableMode, SelectableSettings, FilterService } from '@progress/kendo-angular-grid';
import { cloneDeep as _cloneDeep, merge as _merge, uniq as _uniq } from 'lodash-es';
import { DateTime } from 'luxon';
import { AppConsts } from '@shared/AppConsts';
import { ActivatedRoute, Router } from '@angular/router';
import { GridSettings } from './grid-settings.interface';
import { ColumnSettings } from './column-settings.interface';
import { DataSourceItems } from './data-source-items';
import { CustomTemplate } from './custom-template';
import { FileRestrictions } from '@progress/kendo-angular-upload';
import { ControlTypeNames, EnableAddRecord, GridActionType } from '@shared/AppEnums';
import { CacheDataService } from '../../cache-data/cache-data.service';

const matches = (el, selector) => (el.matches || el.msMatchesSelector).call(el, selector);

const tableRow = node => node.tagName.toLowerCase() === 'tr';

const closest = (node, predicate) => {
    while (node && !predicate(node)) {
        node = node.parentNode;
    }

    return node;
};

@Component({
    selector: 'dynamic-grid',
    templateUrl: './dynamic-grid.component.html',
    styleUrls: ['./dynamic-grid.component.scss', '../shared/dynamic-components.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DynamicGridComponent extends AppComponentBase {
    static id = "DynamicGridComponent";
    public defaultItem: { name: string; id: number } = {
        name: this.l("SelectItem"),
        id: null,
    };

    @ViewChildren('gridDetailsDynamicGrid') gridDetailsDynamicGridComponents: QueryList<DynamicGridComponent>;

    @Input() gridStructures: any;
    @Input() formStructures: any;
    @Input() gridStructureDataSourceName: string = "";
    @Input() gridDataDataSourceName: string = "";
    @Input() useParentComponentData: boolean = false;
    @Input() useItemsInputAsData: boolean = false;
    @Input() parentItem: any;
    @Input() childFieldName: any;
    @Input() gridStructure: any;
    @Input() items: any[] = [];
    @Input() previewGridSettings: any = undefined;
    @Input() pageName: string = "";
    @Input() pageLabelKey: string = "";
    @Input() catalogId: number = 0;
    @Input() erpId: number = 0;
    @Input() clientId: number = 0;
    @Input() gridComponentIndex: number = 0;
    @Input() gridComponent: any;
    @Input() gridId: any;
    @Input() gridName: string = "";
    @Input() customTemplates: CustomTemplate[] = [];
    @Input() actionType = AppConsts.actionType.update;
    @Input() retrieveDataBy: string = "";
    @Input() action: string = "";
    @Input() grandTotals: FieldGrandTotal[] = [];
    @Input() dataSourceItems: DataSourceItems[] = [];
    @Input() customAggregates: any[] = [];
    @Input() hasUnsavedChanges: boolean;
    @Input() hasDetail: boolean = true;
    @Input() hasAdditionalDetails: boolean = false;
    @Input() dynamicFields: any[] = [];
    @Input() hasExportToExcel: boolean = true;
    @Input() hasGridFilter: boolean = false;
    @Input() hasToolbar: boolean = false;
    @Input() hasToolbarPreviewButton: boolean = false;
    @Input() hasToolbarAddNewRecordButton: boolean = false;
    @Input() addFieldsToTheTopOfTheGrid: boolean = false;
    @Input() filterable: string = "menu";
    @Input() isFixedWidth: boolean;
    @Input() groupable: any | boolean = { showFooter: true };
    @Input() columnMenu: boolean = true;
    @Input() navigable: boolean = false;
    @Input() allowAdd: boolean = true;
    @Input() allowEdits: boolean = false;
    @Input() allowDelete: boolean = false;
    @Input() applyOverlay: boolean = false;
    @Input() popUpFormEnabled: boolean = false;
    @Input() isAutoSave: boolean = false;
    @Input() hasGrandTotal: boolean = true;
    @Input() isTabstrip: boolean = false;
    @Input() reorderable: boolean = true;
    @Input() resizable: boolean = true;
    @Input() buttonRemoveOrDeleteLabel: string = this.l("Remove");
    @Input() pageable: any = {
        buttonCount: 10,
        pageSizes: [5, 10, 25, 50, 100, 250],
        type: 'numeric'
    };
    @Input() isHidden: boolean = false;
    @Input() masterPageId: string = "";
    @Input() masterPageLabelKey: string = "";
    @Input() attachedRedirectPageId: string = "";
    @Input() isDetailPage: boolean = false;
    @Input() isChildGrid: boolean = false;
    @Input() formIdentifier: string = "";
    @Input() innerGridHeight: number = undefined;
    @Input() hideHeader: boolean = false;
    @Input() isDependentChildGrid: boolean = false;
    @Input() hasDependency: boolean = false;
    @Input() additionalGridData: any[] = undefined;
    @Input() otherData: any = undefined;

    @Output() getDataEmitter: EventEmitter<any> = new EventEmitter<any>();
    @Output() itemsRecordEmitter: EventEmitter<any> = new EventEmitter<any>();
    @Output() itemCheckEmitter: EventEmitter<any> = new EventEmitter<any>();
    @Output() itemsDisplayEmitter: EventEmitter<any> = new EventEmitter<any>();
    @Output() updateItemsEmitter: EventEmitter<any> = new EventEmitter<any>();
    @Output() openUnsavedChangesEmitter: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() saveCurrentEmitter: EventEmitter<any> = new EventEmitter<any>();
    @Output() checkItemEmitter: EventEmitter<any> = new EventEmitter<any>();
    @Output() uncheckItemEmitter: EventEmitter<any> = new EventEmitter<any>();
    @Output() updatePaymentNotesEmitter: EventEmitter<any> = new EventEmitter<any>();
    @Output() openUncheckDialogEmitter: EventEmitter<any> = new EventEmitter<any>();
    @Output() onDocumentClickEmitter: EventEmitter<any> = new EventEmitter<any>();
    @Output() cellClickEmitter: EventEmitter<any> = new EventEmitter<any>();
    @Output() parentPageNameEmitter: EventEmitter<any> = new EventEmitter<any>();
    @Output() moveToParentEmitter: EventEmitter<any> = new EventEmitter<any>();
    @Output() disableActionButtonEmitter: EventEmitter<any> = new EventEmitter<any>();
    @Output() deleteChildItemEmitter: EventEmitter<any> = new EventEmitter<any>();

    readonly controlTypeNames: typeof ControlTypeNames = ControlTypeNames;

    defaultGridSettings: GridSettings;
    gridSettings: GridSettings;
    canCreate: boolean = true;
    canEdit: boolean = true;
    canDelete: boolean = true;
    initialItemsLength = 0;

    highlightedItemIds: string[] = [];

    modalFormStructureDataSourceName: string = "";
    modalFormDataDataSourceName: string = "";
    modalGridStructureDataSourceName: string = "";
    modalGridDataDataSourceName: string = "";
    itemsHasNoRecords: boolean = true;
    fileName: string;
    itemCount: number;
    itemIds: string[];
    hasNumericColumn: boolean;
    docClickSubscription: any;
    dragAndDropSubscription: Subscription;
    dragAndDropSubscriptionSet: boolean = false;
    @ViewChild(GridComponent) public grid: GridComponent;
    formGroup: FormGroup;
    editedRowIndex: number;
    selectedItemIds: string[] = [];
    noItemDetails: boolean = false;
    itemDetailsId: string = "";
    groupInfoList: any[] = [];
    groupDictionary: { [field: string]: string } = {};
    hideGroupHeaderValues: boolean = true;
    scrollEventListenerCounter: number = 0;
    errorInDetail: boolean = false;
    expandedRow: number = -1;
    gridStyleChanged: boolean = false;
    selectedTabTitle: string = "";
    retrievedDetail: boolean = false;
    resizablePdf: boolean = true;
    isAddNewRow: boolean = false;
    dynamicColumns: string[];
    clickedSender: any;
    clickedIsEdited: any;
    clickedRowIndex: any;
    clickedColumnIndex: any;
    clickedDataItem: any;
    cellIsEdited: boolean = false;
    opened: boolean = false;
    comboboxFilterValue: string = "";
    blankDataStructure: any;
    gridDetailGridComponents: any[] = [];
    modalGridComponents: any[] = [];
    modalFormComponents: any[] = [];
    formComponents: any[] = [];
    dialogOpened = false;
    addHandlerAfterViewInitRendered: boolean = false;
    dialogTitle: string;
    usePreviewGrid = false;
    focusedDictionary: string[] = [];
    deletedIds: any[] = [];
    updatedItems: any[] = [];
    addedItems: any[] = [];
    selectedRows: any[] = [];
    detailExpandCount: number = 0;
    uploadSaveUrl = "";
    uploadRemoveUrl = "";
    myRestrictions: FileRestrictions = {
        allowedExtensions: [".jpg", ".png"],
    };
    filterDataSourceItems: DataSourceItems[] = [];
    clients: any[] = [];
    erps: any[] = [];
    selectedClientId: number = 0;
    selectedErpId: number = 0;
    selectedClient: any;
    selectedErp: any;
    selectedFilters: any[] = [];
    disableActionButton: boolean = true;
    applyNumericGridToBaseForm: boolean = false;
    applyDateGridToBaseForm: boolean = false;
    modalGridStructures: any[] = [];
    currentComboBoxValue: string = "";
    rightClickedDataItem: any = undefined;
    enteredDoubleClick: boolean = false;
    isComboBoxDisabled: { [id: string]: boolean } = {};
    comboBoxTitles: { [id: string]: string } = {};
    comboBoxData: { [id: string]: any[] } = {};
    originalComboBoxData: { [id: string]: any[] } = {};
    inputStyles: { [id: string]: string } = {};
    inputToolTips: { [id: string]: string } = {};
    inputFileRestrictions: { [id: string]: any } = {};
    columnCustomTemplates: { [id: string]: any } = {};
    gridContextMenuItems: any[] = [];
    formChangeSubscription: Subscription;
    numericGrandTotals: { [id: string]: any } = {};
    feedbackMade: { [id: string]: boolean } = {};
    focusedInputField: string = undefined;
    gridDependencyLoading: boolean = false;
    dynamicGridLoading: boolean = false;
    gridUnpopulated: boolean = true;
    enableAddRecord: number = EnableAddRecord.GridAdd;
    initAddRow: boolean = false;
    initGrid: boolean = false;
    selectableMode: SelectableMode = 'multiple';
    selectableSettings: SelectableSettings = { mode: 'multiple', enabled: true, checkboxOnly: false };
    selectedTransactionTypeId: string;
    treeViewData: any;

    constructor(
        injector: Injector,
        private renderer: Renderer2,
        private cd: ChangeDetectorRef,
        private _dynamicDataServiceProxy: DynamicDataServiceProxy,
        private _dynamicGridStateServiceProxy: DynamicGridStateServiceProxy,
        private _router: Router,
        private _activatedRoute: ActivatedRoute,
        public zone: NgZone,
        public cacheDataService: CacheDataService
    ) {
        super(injector);

        this.allData = this.allData.bind(this);
    }

    ngOnChanges(): void {
        if (this.pageName === "DynamicPageDesignMaintenance") {
            this.enableAddingOfRecordsAfterShowingTheGridAgain();
        }

        if (this.items && this.gridSettings) {
            this.setCorrectPagination(this.items.length, this.gridSettings);
            this.populateGrid();
            this.checkNumericColumn();

            if (this.expandedRow > -1) {
                let rowIndex = this.expandedRow >= this.gridSettings?.state.skip ? this.expandedRow - this.gridSettings?.state.skip : this.expandedRow;
                while (rowIndex > this.gridSettings.state.take) {
                    rowIndex -= this.gridSettings.state.take;
                }
                document.getElementsByClassName("k-master-row")[rowIndex].scrollIntoView();
                document.getElementsByClassName("k-grid-content")[0].scrollBy(0, -17); // -17 is the length of the group header that needs to be compensated.
            }
        }
    }

    // retrieveGridPopUpComponents(gridId: number): void {
    //     const gridComponent = {
    //         gridComponentLabelKey: "DynamicFields",
    //         gridStructureDataSourceName: "GridStructureDynamicFieldMaintenance",
    //         gridDataDataSourceName: "GridDataDynamicFieldMaintenance",
    //         useParentComponentData: true,
    //         gridName: "DynamicFieldMaintenance",
    //         pageName: "DynamicFormDesignMaintenance",
    //         childFieldName: "dataItem.dynamicFields"
    //     };
    //     this.gridDetailGridComponents.push(gridComponent);

    //     const formComponent = {
    //         formStructureDataSourceName: "FormStructureInputControlTypeOption",
    //         formDataDataSourceName: "FormDataInputControlTypeOption",
    //         useParentComponentData: false,
    //         useDefaultActionButton: false,
    //         childFieldName: "dataItem.dynamicFields"
    //     };
    //     this.formComponents.push(formComponent);
    // }

    retrieveGridDetailComponents(gridId: number): void {
        if (this.gridStructureDataSourceName === 'GridStructureDynamicTabDesignMaintenance') {
            this.gridDetailGridComponents.push({
                gridComponentLabelKey: "AttachDetachDynamicGridComponent",
                gridStructureDataSourceName: "GridStructureAttachDetachDynamicGridComponent",
                gridDataDataSourceName: "GridDataAttachDynamicGrid",
                useParentComponentData: true,
                gridName: "AttachDetachDynamicGridComponent",
                pageName: "DynamicTabDesignMaintenance",
                childFieldName: "attachDetachDynamicGridComponent"
            });
            this.gridDetailGridComponents.push({
                gridComponentLabelKey: "AttachDetachDynamicFormComponent",
                gridStructureDataSourceName: "GridStructureAttachDetachDynamicFormComponent",
                gridDataDataSourceName: "GridDataAttachDynamicForm",
                useParentComponentData: true,
                gridName: "AttachDetachDynamicFormComponent",
                pageName: "DynamicTabDesignMaintenance",
                childFieldName: "attachDetachDynamicFormComponent"
            });
            this.gridDetailGridComponents.push({
                gridComponentLabelKey: "AttachDetachDynamicActionButtonComponent",
                gridStructureDataSourceName: "GridStructureAttachDetachDynamicActionButtonComponent",
                gridDataDataSourceName: "GridDataAttachDynamicActionButton",
                useParentComponentData: true,
                gridName: "AttachDetachDynamicActionButtonComponent",
                pageName: "DynamicTabDesignMaintenance",
                childFieldName: "attachDetachDynamicActionButtonComponent"
            });
        }
        else if (this.gridStructureDataSourceName === 'GridStructureDynamicFormDesignMaintenance') {
            this.gridDetailGridComponents.push({
                gridComponentLabelKey: "DynamicFields",
                gridStructureDataSourceName: "GridStructureDynamicFieldMaintenance",
                gridDataDataSourceName: "GridDataDynamicFieldMaintenance",
                useParentComponentData: true,
                gridName: "DynamicFieldMaintenance",
                pageName: "DynamicFormDesignMaintenance",
                childFieldName: "dynamicFields"
            });

            this.gridDetailGridComponents.push({
                gridComponentLabelKey: "ChildGroupFieldSets",
                gridStructureDataSourceName: "GridStructureDynamicFormChildGroupFieldSets",
                gridDataDataSourceName: "GridDataDynamicFormDesignMaintenance",
                useParentComponentData: true,
                gridName: "DynamicFormDesignMaintenance",
                pageName: "DynamicFormDesignMaintenance",
                childFieldName: "childGroupFieldSets"
            });
        }
        else if (this.gridStructureDataSourceName === 'GridStructureAttachDetachDynamicActionButtonComponent') {
            this.gridDetailGridComponents.push({
                gridComponentLabelKey: "AttachDetachDynamicGridComponent",
                gridStructureDataSourceName: "GridStructureAttachDetachDynamicActionButtonDynamicGridComponent",
                gridDataDataSourceName: "GridDataDynamicPageDesignMaintenance",
                useParentComponentData: true,
                gridName: "AttachDetachDynamicActionButtonDynamicGrid",
                pageName: "DynamicPageDesignMaintenance",
                childFieldName: "dynamicGridComponents"
            });

            this.gridDetailGridComponents.push({
                gridComponentLabelKey: "AttachDetachDynamicFormComponent",
                gridStructureDataSourceName: "GridStructureAttachDetachDynamicActionButtonDynamicFormComponent",
                gridDataDataSourceName: "GridDataDynamicPageDesignMaintenance",
                useParentComponentData: true,
                gridName: "AttachDetachDynamicActionButtonDynamicForm",
                pageName: "DynamicPageDesignMaintenance",
                childFieldName: "dynamicFormComponents"
            });
        }
        else if (this.gridStructureDataSourceName === 'GridStructureDynamicFormChildGroupFieldSets') {
            this.gridDetailGridComponents.push({
                gridComponentLabelKey: "DynamicFields",
                gridStructureDataSourceName: "GridStructureDynamicFieldMaintenance",
                gridDataDataSourceName: "GridDataDynamicFieldMaintenance",
                useParentComponentData: true,
                gridName: "DynamicFieldMaintenance",
                pageName: "DynamicFormDesignMaintenance",
                childFieldName: "dynamicFields"
            });
        }
        else if (this.gridStructureDataSourceName === 'GridStructureDynamicPageMaintenance') {
            this.gridDetailGridComponents.push({
                gridComponentLabelKey: "DynamicGrid",
                gridStructureDataSourceName: "GridStructureAttachDynamicGrid",
                gridDataDataSourceName: "GridDataAttachDynamicGrid",
                useParentComponentData: true,
                gridName: "AttachDynamicGrid",
                pageName: "AttachDynamicGrid",
                childFieldName: "attachedDynamicGridComponents"
            });

            this.gridDetailGridComponents.push({
                gridComponentLabelKey: "DynamicForm",
                gridStructureDataSourceName: "GridStructureAttachDynamicForm",
                gridDataDataSourceName: "GridDataAttachDynamicForm",
                useParentComponentData: true,
                gridName: "AttachDynamicForm",
                pageName: "AttachDynamicForm",
                childFieldName: "attachedDynamicFormComponents"
            });

            this.gridDetailGridComponents.push({
                gridComponentLabelKey: "DynamicActionButton",
                gridStructureDataSourceName: "GridStructureAttachDynamicActionButton",
                gridDataDataSourceName: "GridDataAttachDynamicActionButton",
                useParentComponentData: true,
                gridName: "AttachDynamicActionButton",
                pageName: "AttachDynamicActionButton",
                childFieldName: "attachedDynamicActionButtonComponents"
            });
        }
        else if (this.gridStructureDataSourceName === 'GridStructureDynamicNavigationMaintenance') {
            this.gridDetailGridComponents.push({
                gridComponentLabelKey: "ChildDynamicNavigation",
                gridStructureDataSourceName: "GridStructureDynamicNavigationMaintenance",
                gridDataDataSourceName: "GridDataDynamicNavigationMaintenance",
                useParentComponentData: true,
                gridName: "AttachGridPopUpDynamicNavigation",
                pageName: "AttachGridPopUpDynamicNavigation",
                childFieldName: "gridPopUpAttachedDynamicNavigationComponents"
            });
        }
        else if (this.gridStructureDataSourceName === 'GridStructureDynamicGridMaintenance') {
            this.gridDetailGridComponents.push({
                gridComponentLabelKey: "PopUpDynamicGrid",
                gridStructureDataSourceName: "GridStructureAttachDynamicGrid",
                gridDataDataSourceName: "GridDataGridPopUpAttachDynamicGrid",
                useParentComponentData: true,
                gridName: "AttachGridPopUpDynamicGrid",
                pageName: "AttachGridPopUpDynamicGrid",
                childFieldName: "gridPopUpAttachedDynamicGridComponents"
            });

            this.gridDetailGridComponents.push({
                gridComponentLabelKey: "PopUpDynamicForm",
                gridStructureDataSourceName: "GridStructureAttachDynamicForm",
                gridDataDataSourceName: "GridDataGridPopUpAttachDynamicForm",
                useParentComponentData: true,
                gridName: "AttachGridPopUpDynamicForm",
                pageName: "AttachGridPopUpDynamicForm",
                childFieldName: "gridPopUpAttachedDynamicFormComponents"
            });

            this.gridDetailGridComponents.push({
                gridComponentLabelKey: "PopUpDynamicActionButton",
                gridStructureDataSourceName: "GridStructureAttachDynamicActionButton",
                gridDataDataSourceName: "GridDataAttachDynamicActionButton",
                useParentComponentData: true,
                gridName: "AttachGridPopUpDynamicActionButton",
                pageName: "AttachGridPopUpDynamicActionButton",
                childFieldName: "attachedDynamicActionButtonComponents"
            });

            this.gridDetailGridComponents.push({
                gridComponentLabelKey: "GridDetailsDynamicGrid",
                gridStructureDataSourceName: "GridStructureAttachDynamicGrid",
                gridDataDataSourceName: "GridDataGridDetailsAttachDynamicGrid",
                useParentComponentData: true,
                gridName: "AttachGridDetailsDynamicGrid",
                pageName: "AttachGridDetailsDynamicGrid",
                childFieldName: "gridDetailsAttachedDynamicGridComponents"
            });

            this.gridDetailGridComponents.push({
                gridComponentLabelKey: "GridDetailsDynamicForm",
                gridStructureDataSourceName: "GridStructureAttachDynamicForm",
                gridDataDataSourceName: "GridDataGridDetailsAttachDynamicForm",
                useParentComponentData: true,
                gridName: "AttachGridDetailsDynamicForm",
                pageName: "AttachGridDetailsDynamicForm",
                childFieldName: "gridDetailsAttachedDynamicFormComponents"
            });

            this.gridDetailGridComponents.push({
                gridComponentLabelKey: "GridDetailsDynamicActionButton",
                gridStructureDataSourceName: "GridStructureAttachDynamicActionButton",
                gridDataDataSourceName: "GridDataAttachDynamicActionButton",
                useParentComponentData: true,
                gridName: "AttachGridDetailsDynamicActionButton",
                pageName: "AttachGridDetailsDynamicActionButton",
                childFieldName: "attachedDynamicActionButtonComponents"
            });
        }
        else if (this.gridStructureDataSourceName === 'GridStructureDynamicFormMaintenance') {
            this.gridDetailGridComponents.push({
                gridComponentLabelKey: "DynamicActionButton",
                gridStructureDataSourceName: "GridStructureAttachDynamicActionButton",
                gridDataDataSourceName: "GridDataAttachDynamicActionButton",
                useParentComponentData: true,
                gridName: "AttachDynamicActionButton",
                pageName: "AttachDynamicActionButton",
                childFieldName: "attachedDynamicActionButtonComponents"
            });

            this.gridDetailGridComponents.push({
                gridComponentLabelKey: "PopUpDynamicGrid",
                gridStructureDataSourceName: "GridStructureAttachDynamicGrid",
                gridDataDataSourceName: "GridDataFormPopUpAttachDynamicGrid",
                useParentComponentData: true,
                gridName: "AttachDynamicGrid",
                pageName: "AttachDynamicGrid",
                childFieldName: "formPopUpAttachedDynamicGridComponents"
            });

            this.gridDetailGridComponents.push({
                gridComponentLabelKey: "PopUpDynamicForm",
                gridStructureDataSourceName: "GridStructureAttachDynamicForm",
                gridDataDataSourceName: "GridDataFormPopUpAttachDynamicForm",
                useParentComponentData: true,
                gridName: "AttachDynamicForm",
                pageName: "AttachDynamicForm",
                childFieldName: "formPopUpAttachedDynamicFormComponents"
            });

            this.gridDetailGridComponents.push({
                gridComponentLabelKey: "PopUpDynamicActionButton",
                gridStructureDataSourceName: "GridStructureAttachDynamicActionButton",
                gridDataDataSourceName: "GridDataAttachDynamicActionButton",
                useParentComponentData: true,
                gridName: "AttachDynamicActionButton",
                pageName: "AttachDynamicActionButton",
                childFieldName: "attachedDynamicActionButtonComponents"
            });
        }

        // const formComponent = {
        //     formStructureDataSourceName: "FormStructureInputControlTypeOption",
        //     formDataDataSourceName: "FormDataInputControlTypeOption",
        //     useParentComponentData: false,
        //     useDefaultActionButton: false,
        //     childFieldName: "dynamicFields"
        // };
        // this.formComponents.push(formComponent);
    }

    ngOnInit(): void {
        if (this.hasDropDownTree()) {
            abp.event.on('app.triggerGridUpdate', id => {
                this.selectedTransactionTypeId = id;
                this.addHandler();
                this.getDynamicDataItems();
            });
        }

        this.initGrid = true;
        this.gridIsLoading();
        this.retrieveGridDetailComponents(1);

        this.cleanPreviewColumnSettings();

        if (this.gridStructure?.componentSettings?.gridName === undefined) {
            this.getDataFirstVersion();
        }
        else {
            this.getData();

            this.subChildGridsUpdate();
        }
    }

    cleanPreviewColumnSettings() {
        if (this.hasDetail) {
            this.previewGridSettings?.columnsConfig?.forEach(col => { col.locked = false });
        }
    }

    ngAfterViewInit() {
        if (sessionStorage["collapsedGroups" + this.action] !== undefined) {
            this.groupInfoList = JSON.parse(sessionStorage["collapsedGroups" + this.action]);
            for (var i = 0; i < this.groupInfoList.length; i++) {
                this.grid.collapseGroup(this.groupInfoList[i].groupIndex);
                this.collapsedGroups.push(this.groupInfoList[i].group);
            }
        }

        if (!this.addHandlerAfterViewInitRendered && document.getElementsByTagName("gridster")[0] !== undefined) {
            // This is used to scroll up to the page because this function causes an auto-focus to an input in the grid causing the page to scroll down.
            document.getElementsByTagName("gridster")[0].scrollTo(0, 0);
            this.addHandlerAfterViewInitRendered = true;
        }

        this.dragAndDropSubscription = this.handleDragAndDrop();
    }

    ngAfterViewChecked() {
        //Moved from ngAfterViewInit to ngAfterViewChecked because addEventListener only worked once, if you navigate to different page, addEventListener failed to register in ngAfterViewInit
        this.addScrollEventListenerOnceIfMoreThanOneGroup();
        if (this.dragAndDropSubscriptionSet === false) {
            this.dragAndDropSubscription = this.handleDragAndDrop();
        }

        if (this.initAddRow) {
            this.gridAddRow();
            this.initAddRow = false;
        }

        if (this.pdfViewer) {
            const shown = this.pdfViewer.find(vwr => !!vwr);

            if (shown) {
                if (this.clearedCheckImages) {
                    abp.ui.setBusy('.sls-pdf-viewer');
                    this.clearedCheckImages = null;
                    this.cd.markForCheck();
                }

                if (this.runningDocFetch) {
                    abp.ui.setBusy('.sls-pdf-viewer');
                    this.runningDocFetch = null;
                    this.cd.markForCheck();
                }
            }
        }

        if (this.items && this.items.length > 0) {
            for (let i = 0; i < this.groupInfoList.length; i++) {
                let itemExists = this.items.findIndex(item => item[this.groupInfoList[i].group.field] === this.groupInfoList[i].group.value);

                if (itemExists === -1) {
                    this.grid.expandGroup(this.groupInfoList[i].groupIndex);
                } else {
                    this.grid.collapseGroup(this.groupInfoList[i].groupIndex);
                }
            }
        }

        if (this.expandedRow > -1 && this.retrievedDetail === true) {
            this.retrievedDetail = false;
            let rowIndex = this.expandedRow >= this.gridSettings.state.skip ? this.expandedRow - this.gridSettings.state.skip : this.expandedRow;
            while (rowIndex > this.gridSettings.state.take) {
                rowIndex -= this.gridSettings.state.take;
            }
            if (document.getElementsByClassName("k-master-row")[rowIndex] !== undefined) {
                document.getElementsByClassName("k-master-row")[rowIndex].scrollIntoView();
                document.getElementsByClassName("k-grid-content")[0].scrollBy(0, -17); // -17 is the length of the group header that needs to be compensated.
            }
        }

        if (this.loadingStateService.isLoading) {
            this.gridIsLoading();
        }

        this.cd.markForCheck();
    }

    ngOnDestroy(): void {
        if (this.gridStructure?.componentSettings !== undefined) {
            this.unSubChildGridsUpdate();
        }

        this.dragAndDropSubscription?.unsubscribe();
        if (this.canEdit) {
            // causing an error so commented out for now
            //this.docClickSubscription();
        }

        if (this.formChangeSubscription) {
            this.formChangeSubscription.unsubscribe();
        }

        document.querySelector('.k-grid .k-grid-content')?.removeEventListener('scroll', () => this.gridScroll());
    }

    enableAddingOfRecordsAfterShowingTheGridAgain(): void {
        if (!this.isHidden) {
            if (this.gridStructure?.componentSettings?.gridName === undefined) {
                this.getDataFirstVersion();
            }
        }
    }

    retrieveSort(columnList): SortDescriptor[] {
        let sort: SortDescriptor[] = [];
        for (let column of columnList.filter(c => c.columnSortable === true && c.initialSortPriority !== -1).sort((n1, n2) => n1.initialSortPriority - n2.initialSortPriority)) {
            const direction = column.initialSortOrder.toLowerCase() === "asc" ? "asc" : "desc";
            sort.push({
                field: column.databaseFieldName,
                dir: direction,
            });
        }

        return sort;
    }

    retrieveGroup(columnList): GroupDescriptor[] {
        let group: GroupDescriptor[] = [];
        for (let column of columnList.filter(c => c.initialGroupingPriority !== -1).sort((n1, n2) => n1.initialGroupingPriority - n2.initialGroupingPriority)) {
            const direction = column.initialGroupingOrder.toLowerCase() === "asc" ? "asc" : "desc";
            group.push({
                field: column.databaseFieldName,
                dir: direction
            });
        }

        return group;
    }

    allData(): ExcelExportData {
        let gridSettingState = _cloneDeep(this.gridSettings.state);
        gridSettingState.skip = 0;

        if (this.items.length !== undefined) {
            gridSettingState.take = this.items.length;
        }

        let clonedItems = _cloneDeep(this.items);

        const dropdownColumns = this.gridSettings?.columnsConfig.filter(x => x.dataSourceName !== undefined);
        this.replaceDropdownValues(clonedItems, dropdownColumns);

        const results: ExcelExportData = {
            data: process(clonedItems, gridSettingState).data,
            group: this.gridSettings.state.group
        };

        return results;
    }

    replaceDropdownValues(items: any[], dropdownColumns: any[]) {
        for (let i = 0; i < items.length; i++) {
            if (items !== null && items !== undefined) {
                for (let prop in items[i]) {
                    //NOTE: cycle through dropdown/combobox columns and get their dataSourceName
                    const dataSourceName = dropdownColumns.filter(x => x.field === prop)[0]?.dataSourceName;
                    if (dataSourceName !== undefined && dataSourceName !== null && dataSourceName !== '') {
                        items[i][prop] = this.getItems(dataSourceName)?.filter(x => x.id === items[i][prop])[0]?.name;
                    }
                }
            }
        }
    }

    filterColumnConfigs() {
        return this.gridSettings?.columnsConfig.filter(col => col.title !== this.l("Document"));
    }

    exportToExcel(): void {
        if (this.gridSettings.gridData.data.length > 0) {
            this.grid.saveAsExcel();
        } else {
            this.notify.error(this.l("NoDataFound"));
        }
    }

    getDynamicGridStateObservable(gridSettings: GridSettings): Observable<GridStateDto> {
        return this._dynamicGridStateServiceProxy.getGridState(gridSettings.gridId);
    }

    removeDynamicGridStateObservable(gridSettings: GridSettings): Observable<void> {
        return this._dynamicGridStateServiceProxy.removeGridState(gridSettings.gridId);
    }

    getDataFirstVersion(): void {
        if (this.previewGridSettings !== undefined) {
            this.defaultGridSettings = this.previewGridSettings;
            this.gridSettings = _merge({}, this.defaultGridSettings);
        }
        else {
            let data = {};
            if (this.retrieveDataBy !== "") {
                data['id'] = this.retrieveDataBy;
            }

            let request = new RetrieveDynamicDataRequest();
            request.dataSourceName = this.gridStructureDataSourceName;
            request.data = data;

            this._dynamicDataServiceProxy.retrieveDynamicData(request)
                .pipe(
                    finalize(() => {
                        //this.gridIsLoading(false);
                    })
                ).subscribe(result => {
                    if (result.data !== null) {
                        this.setAdditionalGridSettings(result.data.componentSettings);

                        result.data.items.forEach(item => {
                            item = this.formatItemDateWithoutId(item);
                        });

                        if (result.data?.blankDataStructure) {
                            this.blankDataStructure = result.data?.blankDataStructure;
                        }

                        this.defaultGridSettings = {
                            state: {
                                skip: result.data.componentSettings.skip === undefined ? 0 : result.data.componentSettings.skip,
                                take: result.data.componentSettings.take === undefined ? 100 : result.data.componentSettings.take,

                                // Initial filter descriptor
                                filter: {
                                    logic: 'and',
                                    filters: []
                                },

                                group: result.data.componentSettings.group === undefined ? [] : result.data.componentSettings.group,
                                sort: result.data.componentSettings.sort === undefined ? [] : result.data.componentSettings.sort
                            },
                            columnsConfig: result.data.items,
                            gridName: result.data.componentSettings.gridName,
                            isDefault: true,
                            gridId: result.data.componentSettings.gridId === undefined ? 0 : result.data.componentSettings.gridId
                        };

                        // SharedWithClient property shouldn't exist in Dynamic Navigation Maintenance's child menu items
                        if (this.gridName === "AttachGridPopUpDynamicNavigation") {
                            this.defaultGridSettings.columnsConfig = this.defaultGridSettings.columnsConfig.filter(col => col.field !== "sharedWithClient");
                        }

                        this.gridSettings = _merge({}, this.defaultGridSettings);

                        this.retrieveRequiredData();
                        this.addHandler();
                        this.getDynamicColumns();

                        if (this.hasExportToExcel) {
                            this.fileName = this.getFilename();
                        }

                        if (this.formGroup) {
                            this.formChangeSubscription = this.formGroup.valueChanges.subscribe(x => {
                                this.updateInputToolTips();
                            });
                        }

                        this.gridIsLoading(true);
                        this.cd.markForCheck();
                        this.getDynamicGridStateObservable(this.gridSettings)
                            .pipe(
                                catchError(() => {
                                    this.gridIsLoading(false);
                                    this.cd.markForCheck();
                                    return of(undefined);
                                })
                            ).subscribe(result => {
                                if (this.pageName !== "DynamicGridDesignMaintenance" && this.pageName !== "DynamicFormDesignMaintenance") {
                                    this.setGridState(this.gridSettings, result, this.defaultGridSettings);
                                }

                                if (this.actionType === AppConsts.actionType.read || this.actionType === AppConsts.actionType.update || this.actionType === AppConsts.actionType.create) {
                                    if (this.useParentComponentData) {
                                        if (this.gridName === undefined) {
                                            this.parentItem?.forEach(item => {
                                                item = this.formatItemDateWithoutId(item);

                                                if (this.pageName === "DynamicFormDesignMaintenance") {
                                                    if (item.dynamicFields !== undefined) {
                                                        item.dynamicFields.forEach(item => {
                                                            item = this.formatItemDateWithoutId(item);
                                                        });
                                                    }

                                                    if (item.childGroupFieldSets !== undefined) {
                                                        item.childGroupFieldSets.forEach(item => {
                                                            item = this.formatItemDateWithoutId(item);

                                                            if (item.dynamicFields !== undefined) {
                                                                item.dynamicFields.forEach(item => {
                                                                    item = this.formatItemDateWithoutId(item);
                                                                });
                                                            }
                                                        });
                                                    }
                                                }
                                            });

                                            this.items = this.parentItem;
                                        }
                                        else if (this.gridName === "" && this.parentItem?.length === undefined) {
                                            this.items = this.parentItem?.items;
                                        }
                                        else if (this.gridName === "" && this.parentItem.length > 0) {
                                            this.items = this.parentItem;
                                        }

                                        this.items?.forEach(item => {
                                            item = this.formatItemDateWithoutId(item);
                                        });

                                        this.populateGrid();
                                        this.checkNumericColumn();

                                        this.updateInputStyles();

                                        this.gridIsLoading(false);
                                    }
                                    else {
                                        if (this.pageName !== "DynamicPageMaintenance" && !this.isDependentChildGrid) {
                                            this.getDynamicDataItems();
                                        }
                                    }

                                    this.updateCustomTemplates();

                                    this.updateFileRestrictions();

                                    this.refreshComboBoxData();

                                    if (this.formGroup) {
                                        this.updateInputToolTips();
                                    }
                                }

                                this.cd.markForCheck();
                            });

                    }
                });
        }
    }

    getData(): void {
        if (this.previewGridSettings !== undefined) {
            this.defaultGridSettings = this.previewGridSettings;
            this.gridSettings = _merge({}, this.defaultGridSettings);
        }
        else {
            if (this.gridStructure !== null) {
                this.setAdditionalGridSettings(this.gridStructure.componentSettings);

                this.gridStructure.items.forEach(item => {
                    item = this.formatItemDateWithoutId(item);
                });

                if (this.gridStructure?.blankDataStructure) {
                    this.blankDataStructure = this.gridStructure?.blankDataStructure;
                }

                this.defaultGridSettings = {
                    state: {
                        skip: this.gridStructure.componentSettings.skip === undefined ? 0 : this.gridStructure.componentSettings.skip,
                        take: this.gridStructure.componentSettings.take === undefined ? 100 : this.gridStructure.componentSettings.take,

                        // Initial filter descriptor
                        filter: {
                            logic: 'and',
                            filters: []
                        },

                        group: this.gridStructure.componentSettings.group === undefined ? [] : this.gridStructure.componentSettings.group,
                        sort: this.gridStructure.componentSettings.sort === undefined ? [] : this.gridStructure.componentSettings.sort
                    },
                    columnsConfig: this.gridStructure.items.map(x => {
                        x.title = this.l(x.title);
                        x.hidden = !x.showColumn;
                        return x;
                    }),
                    gridName: this.gridStructure.componentSettings.gridName,
                    isDefault: true,
                    gridId: this.applyGridId()
                };
                this.gridSettings = _merge({}, this.defaultGridSettings);

                this.retrieveRequiredData();

                this.getDynamicColumns();

                if (this.hasExportToExcel) {
                    this.fileName = this.getFilename();
                }

                this.getDynamicGridStateObservable(this.gridSettings)
                    .pipe(
                        catchError(() => {
                            this.gridIsLoading(false);
                            this.cd.markForCheck();
                            return of(undefined);
                        })
                    ).subscribe(result => {
                        this.setGridState(this.gridSettings, result, this.defaultGridSettings);

                        if (this.actionType === AppConsts.actionType.read || this.actionType === AppConsts.actionType.update || this.actionType === AppConsts.actionType.create) {
                            if (this.useParentComponentData) {
                                this.populateGrid();
                                this.checkNumericColumn();
                            }
                            else {
                                if (this.gridDataDataSourceName === "") {
                                    this.gridDataDataSourceName = "GridData";
                                }

                                if (!this.isDependentChildGrid) {
                                    this.getDynamicDataItems();
                                }
                            }
                        }

                        this.updateCustomTemplates();

                        this.updateFileRestrictions();

                        this.addHandler();

                        this.refreshComboBoxData();

                        if (this.formGroup !== undefined) {
                            this.formChangeSubscription = this.formGroup.valueChanges.subscribe(x => {
                                this.updateInputToolTips();
                            });

                            this.updateInputToolTips();
                        }

                        this.cd.markForCheck();
                    });
            }
        }
    }

    private updateFileRestrictions() {
        this.gridSettings.columnsConfig.filter(col => col.controlTypeId === this.controlTypeNames.Document).forEach(col => {
            this.inputFileRestrictions[col.field] = this.retrieveFileRestrictions(col.optionAndValidation);
        });
    }

    private updateCustomTemplates() {
        this.gridSettings.columnsConfig.filter(x => x.custom).forEach(col => {
            this.columnCustomTemplates[col.field] = this.customTemplates.filter(x => (x.columnName === col.field && x.templateName !== undefined))[0]?.templateName;
        });
    }

    private updateInputToolTips() {
        this.gridSettings.columnsConfig.forEach(col => {
            this.inputToolTips[col.field] = this.applyTooltipError(col);
        });
    }

    private applyGridId(): any {
        if (typeof (this.gridStructure?.componentSettings?.gridId) === 'string') {
            return this.gridStructure?.componentSettings?.gridId;
        }
        else {
            return this.gridStructure?.componentSettings?.gridId.literal === undefined ? "" : this.gridStructure?.componentSettings?.gridId.literal;
        }
    }

    getDynamicDataItems(additionalData = undefined, parentGridStructure = undefined, useSearch = false) {
        if (!this.loadingStateService.isLoading && !this.gridDependencyLoading) {
            this.gridIsLoading();
        }

        // const currentGridData = this.cacheDataService.dynamicDataItems.filter(x => {
        //     return x.gridId === this.applyGridId();
        // });

        // if (currentGridData.length > 0) {
        //     this.setDynamicData(currentGridData[0].data);
        // }
        // else {
        let data = {};
        if (this.retrieveDataBy !== "") {
            data['id'] = this.retrieveDataBy;
        }

        if (this.gridStructure !== undefined) {
            data['id'] = this.applyGridId();
        }

        if (this.pageName === "DynamicPageMaintenance") {
            data['clientId'] = this.selectedClientId;
            data['erpId'] = this.selectedErpId;
        }
        else {
            if (this.permission.isGranted('Pages.Administration.Host.Settings')) {
                data['clientId'] = this.clientAndErpService.clientId;
                data['erpId'] = this.clientAndErpService.erpId;
            }
            else {
                data['clientId'] = undefined;
                data['erpId'] = 888; //NOTE: Use erpId in server
            }
        }

        if (this.isDetailPage && this.formIdentifier !== '00000000-0000-0000-0000-000000000000') {
            data['formIdentifier'] = this.formIdentifier;
        }

        if (additionalData !== undefined) {
            let redirectColumns = parentGridStructure?.items?.filter(x => x.redirectIdentifier);

            let gridParams = [];
            if (useSearch) {
                for (var obj of additionalData) {
                    for (const [key, value] of Object.entries(obj)) {
                        gridParams.push({ 'key': key, 'value': value });
                    }
                }
            }
            else if (redirectColumns.length > 0) {
                for (var redirectColumn of redirectColumns) {
                    let additionalDataLength = additionalData.filter(x => x[redirectColumn.field] !== undefined && x[redirectColumn.field] !== "").length;
                    let key = redirectColumn.databaseTableColumnId;
                    let value = "";
                    for (var currentItem of additionalData) {
                        let retrievedValue = currentItem[redirectColumn.field];

                        if (retrievedValue !== undefined || retrievedValue !== "") {
                            value += retrievedValue;
                            if (additionalDataLength > 1) {
                                value += ";";
                            }
                        }
                    }

                    if (value !== "") {
                        gridParams.push({ 'key': key, 'value': value });
                    }

                    key = "";
                    value = "";
                }
            }

            if (gridParams.length > 0) {
                data['gridParameters'] = gridParams;
            }
        }

        if (this.hasDropDownTree()) {
            let gridParams = [];
            gridParams.push({ 'transactionTypeId': this.selectedTransactionTypeId });
            data['gridParameters'] = gridParams;
        }

        let request = new RetrieveDynamicDataRequest();
        request.dataSourceName = this.gridDataDataSourceName;
        request.data = data;

        this._dynamicDataServiceProxy.retrieveDynamicData(request)
            .pipe(
                catchError(() => {
                    this.gridIsLoading(false);
                    this.cd.markForCheck();
                    return of(undefined);
                }),
                finalize(() => {
                    this.gridDependencyLoading = false;
                    this.gridIsLoading(false);
                    this.cd.markForCheck();
                })
            ).subscribe(result => {
                this.setDynamicData(result);

                if (this.gridStructure !== undefined && this.pageLabelKey.includes("UserAssignments")) {
                    let informationalNotification = "";
                    let changesOccurred = false;
                    if (this.gridStructure.componentSettings.gridLabelKey === "UserRights") {
                        if (this.items.length === 0) {
                            this.appSession.emptyUserRights = true;
                        }
                        else {
                            this.appSession.emptyUserRights = false;
                            changesOccurred = true;
                        }
                    }

                    if (this.gridStructure.componentSettings.gridLabelKey === "ClientRights") {
                        if (this.items.length === 0) {
                            this.appSession.emptyClientRights = true;
                        }
                        else {
                            this.appSession.emptyClientRights = false;
                            changesOccurred = true;
                        }
                    }

                    if (this.gridStructure.componentSettings.gridLabelKey === "EntityRights") {
                        if (this.items.length === 0) {
                            this.appSession.emptyEntityRights = true;
                        }
                        else {
                            this.appSession.emptyEntityRights = false;
                            changesOccurred = true;
                        }
                    }

                    if (this.appSession.emptyUserRights) {
                        informationalNotification += "</br>&bull; " + this.l("GrantUserRights");
                    }

                    if (this.appSession.emptyClientRights) {
                        informationalNotification += "</br>&bull; " + this.l("GrantClientRights");
                    }

                    if (this.appSession.emptyEntityRights) {
                        informationalNotification += "</br>&bull; " + this.l("GrantEntityRights");
                    }

                    if (informationalNotification !== "") {
                        this.notify.info(informationalNotification);
                    }
                    else if (!this.appSession.emptyUserRights && !this.appSession.emptyClientRights && !this.appSession.emptyEntityRights && changesOccurred) {
                        this.notify.info(this.l("AllRightsApplied"));
                    }
                }

                if (this.additionalGridData !== undefined) {
                    for (var item of this.additionalGridData) {
                        var retrievedItem = this.items.findIndex(x => x.id === item.id);

                        if (retrievedItem === -1) {
                            this.items.unshift(item);
                            this.addedItems.push(item);
                        }
                        else {
                            for (let prop in item) {
                                this.items[retrievedItem][prop] = item[prop];
                            }

                            this.updatedItems.push(item);
                        }
                    }

                    this.disableActionButton = false;

                    this.populateGrid();
                }

                // this.cacheDataService.dynamicDataItems.push({ gridId: this.applyGridId(), data: result });
                if (this.gridStructure?.componentSettings !== undefined && this.gridStructure?.componentSettings?.dependentDynamicGridId) {
                    abp.event.trigger('app.grid_cell_' + this.gridStructure.componentSettings.dependentDynamicGridId + '.onClick', this.items, this.gridStructure);
                }

                if (this.items.length > 0) {
                    abp.event.trigger('app.clearComponentChanges');
                }
                else {
                    abp.event.trigger('app.clearComponentChanges', true);
                }
            });
        // }
    }

    setDynamicData(result): void {
        if (result.data !== null && result.data.items !== null) {
            result.data.items.forEach(item => {
                item = this.formatItemDateWithoutId(item);
                item = this.formatItemTextToComboBoxData(item, this.originalComboBoxData);
                item = this.formatItemTextToBoolean(item, this.gridStructure);

                if (this.pageName === "DynamicFormDesignMaintenance") {
                    if (item.dynamicFields !== undefined) {
                        item.dynamicFields.forEach(item => {
                            item = this.formatItemDateWithoutId(item);
                        });
                    }

                    if (item.childGroupFieldSets !== undefined) {
                        item.childGroupFieldSets.forEach(item => {
                            item = this.formatItemDateWithoutId(item);

                            if (item.dynamicFields !== undefined) {
                                item.dynamicFields.forEach(item => {
                                    item = this.formatItemDateWithoutId(item);
                                });
                            }
                        });
                    }

                    this.parentPageNameEmitter.emit(result.data.componentSettings.parentPageName);
                }
            });

            this.items = result.data.items;
            this.initialItemsLength = this.items.length;

            if (!this.gridDependencyLoading) {
                this.gridIsLoading();
            }

            this.cd.markForCheck();
            this.populateGrid();
            this.checkNumericColumn();
            this.gridIsLoading(false);
            this.cd.markForCheck();

            this.getDataEmitter.emit({ dataAsOf: DateTime.fromISO(result.data.dataAsOf), items: result.data.items });
        }

        if (result.data !== null && result.data.asyncItems !== null) {
            result.data.asyncItems.forEach(item => {
                item = this.formatItemDateWithoutId(item, this.gridStructure);
                item = this.formatItemTextToComboBoxData(item, this.originalComboBoxData);
                item = this.formatItemTextToBoolean(item, this.gridStructure);

                if (this.hasTreeView() && item['erp_base_unit_hierarchy$parent_base_unit_id'] !== undefined) {
                    item['erp_base_unit_hierarchy$parent_base_unit_id'] = item['erp_base_unit_hierarchy$parent_base_unit_id'] + "_" + item['erp_base_unit_hierarchy$parent_unit_type_code'];
                }
            });

            if (!this.useItemsInputAsData) {
                this.items = result.data.asyncItems;
            }

            this.initialItemsLength = this.items?.length;

            if (!this.gridDependencyLoading) {
                this.gridIsLoading();
            }

            this.cd.markForCheck();
            this.populateGrid();
            this.checkNumericColumn();
            this.gridIsLoading(false);
            this.cd.markForCheck();

            this.getDataEmitter.emit({ dataAsOf: DateTime.fromISO(result.data.dataAsOf), items: this.items });
        }

        if (this.hasGrandTotal) {
            this.getAllGrandTotals();
        }

        if (this.hasTreeView()) {
            this.processTreeView();
            this.combineParents();
        }

        this.refreshComboBoxData();

        this.updateInputStyles();

        this.cd.markForCheck();
    }

    generateRootIfMissing() {
        // Find all ids and all parent ids
        const ids = this.items.map(item => item['erp_base_unit$erp_base_unit_id'] + '_' + item['erp_unit_type$name']);
        const parentIds = this.items.map(item => item['erp_base_unit_hierarchy$parent_base_unit_id']).filter(p => p !== null);

        // Find the missing parent (i.e., a root) that's not in the ids list
        const missingRoots = [...new Set(parentIds.filter(parentId => !ids.includes(parentId)))];

        //// If there is no root, create a virtual root node
        //if (missingRoots.length > 0) {
        //    return [];
        //}
        const missingNodeMap = {};
        missingRoots.forEach(missingRoot => {
            missingNodeMap[missingRoot] = { typeName: missingRoot, children: [] };
        });

        return Object.values(missingNodeMap);
    }

    processTreeView() {
        const nodeMap = {};
        this.items.forEach(item => {
            nodeMap[item['erp_base_unit$erp_base_unit_id'] + '_' + item['erp_unit_type$name']] = { typeName: item['erp_base_unit$name'], children: [] };
        });

        const roots: any[] = this.generateRootIfMissing();

        this.items.forEach(item => {
            if (item['erp_base_unit_hierarchy$parent_base_unit_id'].length === 0) {
                roots.push(nodeMap[item.id]); // Collect root nodes (no parents)
            } else {
                if (nodeMap[item['erp_base_unit_hierarchy$parent_base_unit_id']] !== undefined) {
                    nodeMap[item['erp_base_unit_hierarchy$parent_base_unit_id']].children.push(nodeMap[item['erp_base_unit$erp_base_unit_id'] + '_' + item['erp_unit_type$name']]);
                }
                else {
                    const result = roots.find(x => x.typeName === item['erp_base_unit_hierarchy$parent_base_unit_id']);
                    result.children.push(nodeMap[item['erp_base_unit$erp_base_unit_id'] + '_' + item['erp_unit_type$name']]);
                }
            }
        });

        // loop through roots
        for (var root of roots) {
            const parsedName = this.comboBoxData['erp_base_unit_hierarchy$parent_base_unit_id'].find(x => x.id === root.typeName)?.name;
            if (parsedName !== undefined) {
                root.typeName = parsedName;
            }
        }

        this.treeViewData = {
            transactionTypes: [...roots]
        };

        abp.event.trigger('app.retrieveDataFromGrid', this.treeViewData);
    }

    combineParents() {
        // Step 1: Create a map to group by id
        const resultMap = this.items.reduce((acc, item) => {
            const { erp_base_unit$erp_base_unit_id, erp_base_unit_hierarchy$parent_base_unit_id, ...restProps } = item;

            if (!acc[erp_base_unit$erp_base_unit_id]) {
                // Create a new entry with an empty scores array and include all other properties
                acc[erp_base_unit$erp_base_unit_id] = { erp_base_unit$erp_base_unit_id, erp_base_unit_hierarchy$parent_base_unit_id: [], ...restProps };
            }

            // Append the score to the scores array
            acc[erp_base_unit$erp_base_unit_id].erp_base_unit_hierarchy$parent_base_unit_id.push(erp_base_unit_hierarchy$parent_base_unit_id);

            return acc;
        }, {});

        // Step 2: Convert the map back to an array
        this.items = Object.values(resultMap);
        this.gridSettings.gridData = process(this.items, this.gridSettings.state);
    }

    retrieveDynamicMultipleDataItems(dataSourceName = null) {
        let data = {};
        if (this.retrieveDataBy !== "") {
            data['id'] = this.retrieveDataBy;
        }

        let request = new RetrieveDynamicDataRequest();
        request.dataSourceName = dataSourceName;
        request.data = data;

        this._dynamicDataServiceProxy.retrieveDynamicData(request)
            .pipe(
                finalize(() => {
                    //this.gridIsLoading(false);
                    this.cd.markForCheck();
                })
            ).subscribe(result => {
                if (result.data !== null) {
                    result.data.items.forEach(item => {
                        item = this.formatItemDateWithoutId(item);
                    });

                    this.setItems(dataSourceName, result.data.items);
                }
            });
    }

    getAllDropDownData(dataSourceNames: string[] = null) {
        let data = {};

        data['dataSourceNames'] = dataSourceNames;
        data['catalogId'] = this.catalogId;
        data['erpId'] = this.erpId;

        let request = new RetrieveDynamicDataRequest();
        request.dataSourceName = "GetAllDropDownData";
        request.data = data;

        this._dynamicDataServiceProxy.retrieveDynamicData(request)
            .pipe(
                finalize(() => {
                    //this.gridIsLoading(false);
                    this.cd.markForCheck();
                })
            ).subscribe(result => {
                if (result.data !== null) {
                    result.data.items.forEach(item => {
                        item = this.formatItemDateWithoutId(item);
                        this.setDataItems(item);
                    });

                    if (this.pageName === "DynamicPageMaintenance") {
                        this.clients = this.getItems("KeyValuePairClientList");
                        this.erps = this.getItems("KeyValuePairErpList");

                        // this.filterDataSourceItems.push(this.getItems("KeyValuePairClientList"));
                        // this.filterDataSourceItems.push(this.getItems("KeyValuePairErpList"));

                        this.getLastSelectedGridFilter();
                    }

                    this.refreshComboBoxData();

                    this.updateInputStyles();
                }
            });
    }

    setLastSelectedGridFilter() {
        let data = {};

        let filterNamesAndValues: any[] = [];

        filterNamesAndValues.push({
            filterName: "KeyValuePairClientList",
            filterValue: this.selectedClientId
        });
        filterNamesAndValues.push({
            filterName: "KeyValuePairErpList",
            filterValue: this.selectedErpId
        });

        data['filterNamesAndValues'] = filterNamesAndValues;

        let request = new RetrieveDynamicDataRequest();
        request.dataSourceName = "SetLastSelectedGridFilter";
        request.data = data;

        this._dynamicDataServiceProxy.retrieveDynamicData(request)
            .pipe(
                finalize(() => {
                    //this.gridIsLoading(false);
                    this.cd.markForCheck();
                })
            ).subscribe();
    }

    getLastSelectedGridFilter() {
        let data = {};

        data['filterNames'] = ["KeyValuePairClientList", "KeyValuePairErpList"];

        let request = new RetrieveDynamicDataRequest();
        request.dataSourceName = "GetLastSelectedGridFilter";
        request.data = data;

        this._dynamicDataServiceProxy.retrieveDynamicData(request)
            .pipe(
                finalize(() => {
                    //this.gridIsLoading(false);
                    this.cd.markForCheck();
                })
            ).subscribe(result => {
                if (result.data !== null) {
                    this.selectedFilters = result.data.items;

                    this.selectedClientId = this.selectedFilters.filter(x => x.filterName === "KeyValuePairClientList")[0]?.filterValue;
                    this.selectedErpId = this.selectedFilters.filter(x => x.filterName === "KeyValuePairErpList")[0]?.filterValue;

                    this.selectedClient = this.clients.filter(x => x.id === this.selectedClientId)[0];
                    this.selectedErp = this.erps.filter(x => x.id === this.selectedErpId)[0];

                    this.getDynamicDataItems();
                }
                else {
                    this.gridIsLoading(false);
                    this.cd.markForCheck();
                }
            });
    }

    setAdditionalGridSettings(componentSettings: any = undefined) {
        if (componentSettings !== undefined) {
            if (componentSettings.hasDetail !== undefined) {
                this.hasDetail = componentSettings.hasDetail;
            }

            if (componentSettings.columnMenu !== undefined) {
                this.columnMenu = componentSettings.columnMenu;
            }

            if (componentSettings.groupable !== undefined) {
                this.groupable = componentSettings.groupable;
            }

            if (componentSettings.hasExportToExcel !== undefined || componentSettings.exportToExcel !== undefined) {
                if (componentSettings.hasExportToExcel !== undefined) {
                    this.hasExportToExcel = componentSettings.hasExportToExcel;
                }
                else if (componentSettings.exportToExcel !== undefined) {
                    this.hasExportToExcel = componentSettings.exportToExcel;
                }
            }

            if (componentSettings.allowEdits !== undefined) {
                this.allowEdits = componentSettings.allowEdits;
            }

            if (this.pageName === "DynamicPageMaintenance") {
                this.hasGridFilter = true;
            }

            if (componentSettings.addFieldsToTheTopOfTheGrid !== undefined) {
                this.addFieldsToTheTopOfTheGrid = componentSettings.addFieldsToTheTopOfTheGrid;
            }

            if (componentSettings.enableAddRecord !== undefined) {
                this.enableAddRecord = componentSettings.enableAddRecord;
            }

            if (componentSettings.pageable !== undefined) {
                if (componentSettings.pageable === false) {
                    this.pageable = componentSettings.pageable;
                }
                else {
                    if (componentSettings.pagerType === true) {
                        this.pageable.type = 'input';
                    }
                    else {
                        this.pageable.type = 'numeric';
                    }

                    this.pageable.buttonCount = componentSettings.paginationButtonCount;
                }
            }

            if (componentSettings.filterable !== undefined) {
                this.filterable = componentSettings.filterable;
            }

            if (componentSettings.hasGrandTotal !== undefined) {
                this.hasGrandTotal = componentSettings.hasGrandTotal;
            }

            if (componentSettings.isAutoSave !== undefined) {
                this.isAutoSave = componentSettings.isAutoSave;
            }

            if (componentSettings.isFixedWidth !== undefined) {
                this.isFixedWidth = componentSettings.isFixedWidth;
            }

            if (componentSettings.checkBoxOnly !== undefined) {
                if (componentSettings.multipleRows !== undefined) {
                    if (componentSettings.multipleRows === true) {
                        this.selectableMode = 'multiple';
                    }
                    else {
                        this.selectableMode = 'single';
                    }
                }
            }

            if (componentSettings.allowReorder !== undefined) {
                this.reorderable = componentSettings.allowReorder;
            }

            if (componentSettings.allowResize !== undefined) {
                this.resizable = componentSettings.allowResize;
            }

            //TODO: Add additional grid settings here
        }

        if (this.canEdit) {
            this.docClickSubscription = this.renderer.listen('document', 'click', this.onDocumentClick.bind(this));
        }

        this.selectableSettings = {
            checkboxOnly: componentSettings?.checkBoxOnly !== undefined ? componentSettings.checkBoxOnly : false,
            enabled: true,
            mode: this.selectableMode
        };
    }

    retrieveRequiredData(additionalData = undefined, parentGridStructure = undefined) {
        let dataSourceNames = _uniq(this.defaultGridSettings.columnsConfig.filter(x => x.dataSourceName).map(x => x.dataSourceName));
        //TODO: refetch table columns
        //NOTE: Old Way
        // dataSourceNames.map(dataSourceName => {
        //     this.retrieveDynamicMultipleDataItems(dataSourceName);
        // });

        if (this.pageName !== "DynamicPageDesignMaintenance") {
            if (this.pageName === "DynamicPageMaintenance") {
                dataSourceNames.push("KeyValuePairErpList");
                dataSourceNames.push("KeyValuePairClientList");
            }
            else if (this.pageName === "DynamicGridDesignMaintenance" && this.hasDependency) {
                dataSourceNames.push("GetDependencySource");
            }
            else if (this.pageName === "DynamicGridDesignMaintenance") {
                // Remove DependencySource and DependencySourceColumn
                this.defaultGridSettings.columnsConfig = this.defaultGridSettings.columnsConfig.filter(x => !x.field.includes("dependencySource"));
                this.gridSettings.columnsConfig = this.gridSettings.columnsConfig.filter(x => !x.field.includes("dependencySource"));
            }

            // No need to retrieve this again because the data will not change no matter if there's additional data or not.
            if (additionalData === undefined) {
                this.getAllDropDownData(dataSourceNames);
            }

            if (this.pageName === "") {
                this.getDropdownListValueEagerLoaded(dataSourceNames, additionalData, parentGridStructure);
            }
        }
    }

    getDynamicColumns() {
        this.dynamicColumns = this.defaultGridSettings.columnsConfig.filter(x => x.field !== "command").map(x => x.field);

        this.updateComboBoxTitle();
    }

    private refreshComboBoxData() {
        this.gridSettings?.columnsConfig?.filter(x => x.editor === 'dropdown' || x.editor === 'combobox')?.forEach(col => {
            this.comboBoxData[col.field] = this.retrieveComboBoxData(col.field, col.dynamicGridColumnId, col.dataSourceName, col?.optionAndValidation?.dataDependentTo);
            this.originalComboBoxData[col.field] = _cloneDeep(this.comboBoxData[col.field]);
        });
    }

    private updateComboBoxTitle() {
        this.gridSettings?.columnsConfig?.filter(x => x.editor === 'dropdown' || x.editor === 'combobox')?.forEach(col => {
            const col2 = this.gridSettings.columnsConfig.find(x => x.field === col?.optionAndValidation?.dataDependentTo);
            if (col2 !== undefined && this.disableComboBox(col)) {
                this.comboBoxTitles[col.field] = this.l('PleaseSelectParentToEnableThisField', col2.title);
            }
            else {
                this.comboBoxTitles[col.field] = "";
            }
        });
    }

    updateData(e): void {
        this.closePdfViewer();

        this.pageChange({ skip: 0, take: this.gridSettings.state.take });

        document.getElementsByClassName("k-grid-content")[0].scrollTo(0, 0);

        this.selectedClientId = e.selectedClient.id;

        if (e.selectedErp !== undefined) {
            this.selectedErpId = e.selectedErp.id;
        }

        this.getDynamicDataItems();

        this.setLastSelectedGridFilter();
    }

    openUnsavedChangesForFilter(e): void {
        this.openUnsavedChangesEmitter.emit(e);
    }

    checkHighlightedItems(items: any[]) {
        if (items.length === 1 && this.highlightedItemIds.indexOf(items[0].id) === -1) {
            this.highlightedItemIds.splice(0, this.highlightedItemIds.length);
            this.highlightedItemIds.push(items[0].id);
        }
    }

    gridResetToDefaultView(service: any, gridSettings: GridSettings, defaultGridSettings: GridSettings, items: any[]): void {
        service.close();

        this.dynamicGridLoading = true;

        let input = this.customResetToDefaultView(gridSettings, defaultGridSettings);
        input.gridIdUuid = gridSettings.gridId;

        this.setDynamicGridStateObservable(input).pipe(
            finalize(() => {
                this.closePdfViewer();
                document.getElementsByClassName("k-grid-content")[0].scrollTo(0, 0);
                this.dynamicGridLoading = false;
                this.cd.markForCheck();
            })
        ).subscribe(() => {
            this.customResetToDefaultViewAfter(gridSettings, defaultGridSettings, items);
            this.populateGrid();
        });

        this.logFeatureUsage(event, gridSettings, GridActionType.ResetToDefaultView);
    }

    populateGrid(processTree = false): void {
        this.highlightedItemIds = [];

        if (this.items && this.gridSettings) {
            if (processTree) {
                this.items.forEach(item => {
                    if (this.hasTreeView() && item['erp_base_unit_hierarchy$parent_base_unit_id'] !== undefined) {
                        item['erp_base_unit_hierarchy$parent_base_unit_id'] = item['erp_base_unit_hierarchy$parent_base_unit_id'] + "_" + item['erp_base_unit_hierarchy$parent_unit_type_code'];
                    }
                });
            }

            this.gridSettings.gridData = process(this.items, this.gridSettings.state);
        }

        if (processTree && this.hasTreeView()) {
            this.processTreeView();
            this.combineParents();
        }

        this.gridUnpopulated = false;

        this.cd.markForCheck();
    }

    checkNumericColumn(): void {
        if (this.defaultGridSettings) {
            this.hasNumericColumn = this.defaultGridSettings.columnsConfig.some(x => x.filter === "numeric");
        }
    }

    getAllGrandTotals() {
        this.gridSettings?.columnsConfig?.filter(col => col.filter === "numeric")?.forEach(curr => {
            this.numericGrandTotals[curr.field] = this.getGrandTotal(curr.field);
        });
    }

    getGrandTotal(colField: string): string {
        let grandTotal = 0;

        if (this.grandTotals.length === 0) {
            if (this.items && this.items.length > 0) {
                this.items.map(x => {
                    grandTotal += x[colField];
                });
            }
        } else {
            this.grandTotals.map(x => {
                if (x.fieldName === colField) {
                    grandTotal = x.grandTotal;
                }
            });
        }

        return this.formatNumber(grandTotal);
    }

    updateGrandTotal(items: any[]): FieldGrandTotal[] {
        for (let i = 0; i < this.grandTotals.length; i++) {
            let sum = 0;
            let decimals = 0;

            items.forEach(p => {
                decimals += +(p[this.grandTotals[i].fieldName] - Math.floor(p[this.grandTotals[i].fieldName])).toFixed(3);
                sum = +(sum + Math.floor(p[this.grandTotals[i].fieldName])).toFixed(3);
            });
            sum += decimals;

            this.grandTotals[i].grandTotal = sum;
        }

        return this.grandTotals;
    }

    dataStateChange(state: DataStateChangeEvent) {
        let index = this.getIndexofAddedGroupFromOldState(this.gridSettings.state.group, state.group);
        this.gridSettings.state = state;

        if (this.gridSettings.state && this.gridSettings.state.group) {
            this.gridSettings.state.group.map(group => group.aggregates = this.customAggregates);
            this.gridSettings.state.group.map(group => group.aggregates = this.distinctAggregates);
        }

        // Grouping's total is only limited to 4 columns at the most.
        if (this.gridSettings.state.group.length > 4 && index === -1) {
            this.message.info(this.l("GroupTotalsLimitMessage"), this.l("GroupTotalsLimitTitle"));
        }

        this.groups = [];
        for (var i = 0; i < this.gridSettings.state.group.length; i++) {
            let col = this.gridSettings.columnsConfig.find(col => col.field === this.gridSettings.state.group[i].field);
            this.groups.push({ field: this.gridSettings.state.group[i].field, editor: col.editor });
        }

        this.populateGrid();

        if (this.appSession.sessionToken !== "0") {
            this.saveOneModelGridState(this.gridSettings);
        }

        this.dragAndDropSubscription.unsubscribe();
        this.zone.onStable
            .pipe(take(1))
            .subscribe(() => (this.dragAndDropSubscription = this.handleDragAndDrop()));
    }

    detailCollapse() {
        this.detailExpandCount--;
    }

    detailExpand(e) {
        this.detailExpandCount++;
    }

    onDocumentClick(e: any): void {
        const clickEventCameFromOutsideTheGridOrAnotherEditPaymentAmountLink = (!matches(e.target, '#dynamic-grid tbody *, #dynamic-grid .k-grid-toolbar .k-button') ||
            matches(e.target, 'div.cursor-pointer.link-text.ng-star-inserted'));

        if (clickEventCameFromOutsideTheGridOrAnotherEditPaymentAmountLink) {
            this.onDocumentClickEmitter.emit();
        }
    }

    revert(): void {
        this.closeEditor();
    }

    closeEditor(): void {
        this.grid.closeRow(this.editedRowIndex);
        this.editedRowIndex = undefined;
        this.formGroup = undefined;
        this.cd.markForCheck();
    }

    setItems(dataSourceName: string, items: any): void {
        let dataSourceItemIndex = this.dataSourceItems.findIndex(x => x.dataSourceName === dataSourceName);

        // Updates the value of the data source item when a new item is retrieved.
        if (dataSourceItemIndex > -1 && items.length > 0) {
            this.dataSourceItems.splice(dataSourceItemIndex, 1);
        }

        let dataSourceItems = new DataSourceItems();
        dataSourceItems.dataSourceName = dataSourceName;
        dataSourceItems.dataSourceItems = items;
        this.dataSourceItems.push(dataSourceItems);
    }

    getDropdownListValueEagerLoaded(dataSourceNames: string[] = null, additionalData = undefined, parentGridStructure = undefined) {
        let data = {};

        data['dataSourceNames'] = dataSourceNames;
        data['componentId'] = this.gridStructure?.componentSettings?.gridId;
        data['componentName'] = this.gridStructure?.componentSettings?.gridName;
        data['isGridComponent'] = true;

        if (additionalData !== undefined) {
            let redirectColumns = parentGridStructure.items.filter(x => x.redirectIdentifier);

            let gridParams = [];
            if (redirectColumns.length > 0) {
                for (var redirectColumn of redirectColumns) {
                    let additionalDataLength = additionalData.filter(x => x[redirectColumn.field] !== undefined && x[redirectColumn.field] !== "").length;
                    let key = redirectColumn.databaseTableColumnId;
                    let value = "";
                    for (var currentItem of additionalData) {
                        let retrievedValue = currentItem[redirectColumn.field];

                        if (retrievedValue !== undefined || retrievedValue !== "") {
                            value += retrievedValue;
                            if (additionalDataLength > 1) {
                                value += ";";
                            }
                        }
                    }

                    if (value != "") {
                        gridParams.push({ 'key': key, 'value': value });
                    }

                    key = "";
                    value = "";
                }
            }

            if (gridParams.length > 0) {
                data['gridParameters'] = gridParams;
            }
        }

        let request = new RetrieveDynamicDataRequest();
        request.dataSourceName = "GetDropdownListValueEagerLoaded";
        request.data = data;

        this._dynamicDataServiceProxy.retrieveDynamicData(request)
            .pipe(
                finalize(() => {
                    this.cd.markForCheck();
                })
            ).subscribe(result => {
                if (result.data !== null) {
                    result.data.items.forEach(item => {
                        item = this.formatItemDateWithoutId(item);
                        this.setDataItems(item);
                    });
                }

                this.refreshComboBoxData();

                this.items.forEach(item => {
                    item = this.formatItemTextToComboBoxData(item, this.originalComboBoxData);
                })

                this.updateInputStyles();

                this.cd.markForCheck();
            });
    }

    setDataItems(item: any): void {
        let dataSourceItemIndex = this.dataSourceItems.findIndex(x => x.dataSourceName === item.dataSourceName);

        // Updates the value of the data source item when a new item is retrieved.
        if (dataSourceItemIndex > -1 && item.dataSourceItems.length > 0) {
            this.dataSourceItems.splice(dataSourceItemIndex, 1);
        }

        this.dataSourceItems.push(item);
    }

    getItems(dataSourceName: string = ""): any {
        const isMainItem = dataSourceName === "";

        let result: any[];

        if (isMainItem) {
            result = this.dataSourceItems[0]?.dataSourceItems;
        } else {
            result = this.dataSourceItems.filter(x => x.dataSourceName === dataSourceName).sort((x, y) => y.dataSourceItems.length - x.dataSourceItems.length)[0]?.dataSourceItems;
        }

        return result;
    }

    getItemValue(col: ColumnSettings, dataItem: any): string {
        if (col.dynamicGridColumnId === undefined && col.dataSourceName === undefined) {
            return dataItem;
        }
        else {
            let name = "";
            const isDynamicPages = this.pageName === "";
            const dataSourceName = isDynamicPages ? (col.dynamicGridColumnId ? col.dynamicGridColumnId : col.dataSourceName) : col.dataSourceName;

            if (this.dataSourceItems && this.dataSourceItems.length > 0) {
                const dataSource = this.dataSourceItems.filter(x => x.dataSourceName === dataSourceName && x.dataSourceItems?.length > 0)
                    .sort((x, y) => y.dataSourceItems.length - x.dataSourceItems.length);

                if (dataSource.length > 0) {
                    const item = dataSource[0]?.dataSourceItems.filter(y => y[col.valueField] === dataItem);

                    if (item.length > 0) {
                        name = item[0]?.[col.textField];
                    }
                    else {
                        return dataItem;
                    }
                }
            }

            this.cd.markForCheck();

            return name;
        }
    }

    getFilename(): string {
        const gridName = this.convertPascalCaseToKebabCase(this.defaultGridSettings.gridName);

        return new Date(
            Date.UTC(
                new Date().getUTCFullYear(),
                new Date().getUTCMonth(),
                new Date().getUTCDate())).toISOString()
            .split('T')[0] + "-" + gridName;
    }

    applyValidationSettings(fieldDisplayMaintenance: any) {
        let validationSettings = [];

        if (fieldDisplayMaintenance.validation.required) {
            validationSettings.push(Validators.required);
        }

        if (fieldDisplayMaintenance.validation.requiredTrue) {
            validationSettings.push(Validators.requiredTrue);
        }

        if (fieldDisplayMaintenance.validation.useEmail) {
            const regex = /^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i;
            validationSettings.push(Validators.pattern(regex));
        }

        if (fieldDisplayMaintenance.optionAndValidation.minimumLength > 0) {
            validationSettings.push(Validators.minLength(fieldDisplayMaintenance.optionAndValidation.minimumLength));
        }

        if (fieldDisplayMaintenance.optionAndValidation.maximumLength > 0) {
            validationSettings.push(Validators.maxLength(fieldDisplayMaintenance.optionAndValidation.maximumLength));
        }

        if (fieldDisplayMaintenance.optionAndValidation.minimumValue > 0) {
            validationSettings.push(Validators.min(fieldDisplayMaintenance.optionAndValidation.minimumValue));
        }

        if (fieldDisplayMaintenance.optionAndValidation.maximumValue > 0) {
            validationSettings.push(Validators.max(fieldDisplayMaintenance.optionAndValidation.maximumValue));
        }

        return validationSettings;
    }

    add({ sender }): void {
        this.closeEditor();

        this.formGroup = new FormGroup({});

        this.formGroup.setControl("id", new FormControl(""));

        if (this.defaultGridSettings.columnsConfig.length > 0) {
            this.defaultGridSettings.columnsConfig.map(field => {
                if (field.field === "dateCreated" || field.field === "dateModified") {
                    this.formGroup.setControl(field.field, new FormControl(new Date()));
                }
                else if (field.editor === "combobox") {
                    this.formGroup.setControl(field.field, new FormControl(undefined));
                }
                else if (field.editor === "boolean") {
                    this.formGroup.setControl(field.field, new FormControl({ value: true, disabled: true }));
                }
                else {
                    this.formGroup.setControl(field.field, new FormControl(''));
                }
            });
        }

        sender.addRow(this.formGroup);
    }

    edit({ sender, rowIndex, dataItem }) {
        this.cancel();
        this.isAddNewRow = false;

        this.formGroup = new FormGroup({});

        this.formGroup.setControl("id", new FormControl((dataItem["id"])));

        if (this.defaultGridSettings.columnsConfig.length > 0) {
            this.defaultGridSettings.columnsConfig.map(field => {
                if (field.field === "dateCreated" || field.field === "dateModified") {
                    this.formGroup.setControl(field.field, new FormControl(dataItem[field.field]));
                }
                else if (field.editor === "combobox") {
                    this.formGroup.setControl(field.field, new FormControl(dataItem[field.field]));
                }
                else if (field.editor === "boolean") {
                    this.formGroup.setControl(field.field, new FormControl(dataItem[field.field]));
                }
                else {
                    this.formGroup.setControl(field.field, new FormControl(dataItem[field.field]));
                }
            });
        }

        this.editedRowIndex = rowIndex;

        sender.editRow(rowIndex, this.formGroup);
    }

    cancel() {
        this.closeEditor();
    }

    save({ sender, rowIndex, formGroup, isNew }) {
        const value = formGroup.value;

        if (value.invoiceItem === "") {
            this.message.error(this.l("RequiredField", "Invoice Item"));
            return;
        }

        if (value.amount === null) {
            this.message.error(this.l("RequiredField", "Amount"));
            return;
        }

        if (value.accountId === null) {
            this.message.error(this.l("RequiredField", "Account"));
            return;
        }

        this.populateGrid();
        sender.closeRow(rowIndex);
    }

    remove({ dataItem }) {
        this.cancel();

        this.items = this.items.filter(x => x.Id !== dataItem.Id);

        this.populateGrid();
    }

    pageChange({ skip, take }: PageChangeEvent): void {
        this.gridSettings.state.skip = skip;
        this.gridSettings.state.take = take;

        if (this.appSession.sessionToken !== "0") {
            this.saveDynamicGridState(this.gridSettings);
        }

        this.populateGrid();
    }

    saveDynamicGridState(gridSettings: GridSettings): void {
        const gridConfig = {
            columnsConfig: gridSettings.columnsConfig,
            state: gridSettings.state
        };

        let input = new SetDynamicGridStateInput();

        input.gridId = gridSettings.gridId;
        input.gridName = gridSettings.gridName;
        input.gridConfig = JSON.stringify(gridConfig);

        this._dynamicGridStateServiceProxy.setGridState(input).subscribe(() => {
            gridSettings.isDefault = false;
        });
    }

    setDynamicGridStateObservable(gridSettings: SetGridStateInput): Observable<void> {
        let input = new SetDynamicGridStateInput();

        input.gridId = gridSettings.gridIdUuid;
        input.gridName = gridSettings.gridName;
        input.gridConfig = gridSettings.gridConfig;

        return this._dynamicGridStateServiceProxy.setGridState(input);
    }

    // PDF Viewer related
    closePdfViewer(): void {
        this.pdfSrc = "";
        this.isPdfViewerVisible = false;

        if (this.expandedRow > -1) {
            let grid = document.getElementById("dynamic-grid");
            grid.style.minWidth = null;
            grid.style.maxHeight = null;
            let rowIndex = this.expandedRow >= this.gridSettings.state.skip ? this.expandedRow - this.gridSettings.state.skip : this.expandedRow;
            while (rowIndex > this.gridSettings.state.take) {
                rowIndex -= this.gridSettings.state.take;
            }

            if (document.getElementsByClassName("k-master-row")[rowIndex] !== undefined) {
                document.getElementsByClassName("k-master-row")[rowIndex].scrollIntoView();
            }

            document.getElementsByClassName("k-grid-content")[0].scrollBy(0, -17);
            this.grid.collapseRow(this.expandedRow);
            this.expandedRow = -1;
            this.grid.focus();
        }

        this.cd.markForCheck();
    }

    openDocumentDialog(): void {
        this.pdfSrc = "";
        this.isPdfViewerVisible = true;
        this.isPdfViewerScrollable = false;

        const billNumber = "147017";
        const billDate = "2021-01-29";
        const invoiceAmount = "-31.68";
        const archimedesDocumentId = "";

        this.pdfName = "STREAMLINEA";
        this.pdfSrc = "data:application/pdf;base64,JVBERi0xLjINCjEgMCBvYmoNCjw8DQovQ3JlYXRpb25EYXRlIDxGRUZGMDA0NDAwM0EwMDMyMDAzMDAwMzIwMDMxMDAzMDAwMzEwMDMyMDAzOTAwMzEwMDMzMDAzNDAwMzkwMDMxMDAzNz4NCi9Qcm9kdWNlciA8RkVGRjAwNDMwMDZGMDA2RDAwNzAwMDZGMDA2RTAwNjUwMDZFMDA3NDAwNEYwMDZFMDA2NTAwMjAwMDUwMDA0NDAwNDYwMDIwMDA0NzAwNjUwMDZFMDA2NTAwNzIwMDYxMDA3NDAwNkYwMDcyMDAyMDAwMzEwMDJFMDAzMD4NCj4+DQplbmRvYmoNCjIgMCBvYmoNCjw8DQovTGVuZ3RoIDMgMCBSDQovRmlsdGVyIC9GbGF0ZURlY29kZQ0KPj4NCnN0cmVhbQ0KeJyVmFtv2zYUx98L5DscYMDQBjUt3sm8KTbbeLVlT5KbFOseiiUrOqTrkA0b+u13KEuOSB/ngqI2GP54Pf9zoQvmDBT7j7vPJy+EA+0N0wI4/teSeQt3N/D7yQvuPDPqWK8QTIExklkO2mgmFeCfhOq7C+YN/Ae//AoFXJ+8UAqHOsAZnQUuBTM4o2faR7w5eTF9wxVwAW0cCvFf3Nt5C6qwzIGV0F7DJAJ/w8urV9D+AaHFabtejRM5+AraCeZ137ztp9XDtDiZEN0+rRXj6Tbrul0vS3hfLpfhA1yU9fyyrMN+kTgQz6kNWO2Y9eOxHL+H8fWcJWOkAKt0HHd0sdcwK8GrQrhkpC4YXofljvF08MW6Cmfw8aXRxcdX4DSfFJyb/djuFh0Tfjiw5oYp3LflTPFupl1vnKt8G6BaAx+vLHW8OiP9cMw9Xi43iyrAoqoIXnjmZcpLjzdf3v715c8bqK+JMYWPGki39O3un2+3n+D9p9vbm+/jMRztLAQYXkT7JYNmZQKinCkqveNOFsk9KcuUB25Qn+nACZ6tKNIbNsMwqS1DLRm7s5LpRlShhfk2wLqCOszCYtMet49Cfag4AerSpguLxDCaa2YVCfKp8NOMRp3iCUn6TPkxGl2GBIvRqTvXQeeWYAvFvMqlscSTttu6avD75+2iDlAOZ4eymsNq27TpZJwZCQadNZnpPMDlor1YVCALmJcfmtfQrtfLBnXa9kuU58vEMxUaGz3MFPpA4VW4hKb9AR30PUygKStYlck2lMMoiHrRmc215WOTGRAoKL63mTPxcrWzg4D77ni7yhbcJmqXGCENTc/qMF+0sAqr9dlxbUbn0mDFEEj222zxUi7bMwzlqam0VqhiborMoJmOY2CMmhpcAJ2eo6MZ5obo2PVGH5Boroe8J6YHNCkXue+Mh/VuZnARjax93M/Ga0gvGXeknYtCPnB7uyzDFWfZ7k5PoVytt1ULOzuEOYoNytms+9vpaaoyjD8GZ0HbZVY4uJt03wIblvZEq9Nt78OKUJ2kHSrHjQLLDL0IPeEslVcXP5RnakQ262U8TEJyj55LoBeLTY5axWIYzdf/aX2eL8+5ZFIeopttPbsoGwyC9TzU2T44Iyavw5tQh2oW0uPhbmPcz+k21KsmIRWmFXSTw0tbhvpdQuqYeYg9z8s2wBTaxSo84I1Ka+awVLGZd7Xl1UFo6lGD2TzLptW6mjwwAvO/ykJjsz1Hfy+XFC74wV56aef+Pj4FtqK323GBkS6RqlJayzjeG2aAfkEzrHZ2XP/Kylgb5MGovvnt5su/N9dw/v3IcjE1GvQCTLE6N/2iKpf3a6JvOjxcdDEJOgZKCdLxWLvuissuyt9/xMpyN0Yrw6QfxsRM4oba96H+Jj9nUrHG+G4xJhomMhsusYJK7YeuSIHRKTdhnuRpjJyehDsXS2EeL4JAt6uEwhtz9E6bd9u8/FX0VuehmdWY5xfrtDBEs+sjh9u+fZvHVnwakPutFm2TsIYzqUl2Uy9mYZrAHo1I39km1Gl1Ra8frtpQNeOjxTTCOxYfQl9h14wXiV/di8NhGLrv71rjbi47Ee2Bvp0gyjGtR8iuPUaEKO77Y2PcKdFYQt739+0xEp9NmAvuj7FrJ4jG9D+apW+PkViTytFZ+vYYidqx3WkU1iTZEyEpWaO4CWbCizzfPJGUKmZGigzpiwFTjKen1FkFzJnmJFht22Z6vl7i54+Y8uCybC5CnSoXSwd75B6SZZR18U1BgZhHbCpwt4tSh+g0PWQMZIYmJ5zZtHzERxBGO4qtqfvAEsRl6a0vbHcVezJmJwbRvdPTMioXA8EcEcMTyF4MBEmLgQBpMRDgM8RAjKbFQICYkjwlBgI9EAM940RgtCKlQMCkFPIHwGM6KLLCRVGT5tD67svnM+heWmYqAKDoX8DQXiWFHh4Hy1jli1i6p5echx4KotX2FHKnNook1UaBpNoo8OlqIy+CUhsJskITaqNQQm1HLo1pSm0UTKlNWc/kkwW304JxMUOlJxPU3AT4XN1h4aqyX0p4EuVsV6KSXKY8E58DTwB74REgLTwCpIVHgM8Q3qMbH4RHgKgFKsxR6IHw/DE7iCzO9RbDJKmzYMMVYTGKIy32ONhbjABpixEgbTECfIbFHt34YDECxLcrVaVQ6BGLEaTKipTeYvG35PxnFU1YjOJIiz0O9hYjQNpiBEhbjACfYbFHNz5YjACxriSDO4EesRi1+ji6/w9dIrWIZW5kc3RyZWFtDQplbmRvYmoNCjMgMCBvYmoNCjE2ODkNCmVuZG9iag0KNCAwIG9iag0KPDwNCi9UeXBlIC9Gb250RGVzY3JpcHRvcg0KL0ZvbnROYW1lIC9BcmlhbA0KL0ZsYWdzIDMyDQovRm9udEJCb3ggWy02NjUgLTMyNSAyMDAwIDEwNDBdDQovTWlzc2luZ1dpZHRoIDQ0MQ0KL1N0ZW1WIDcyDQovU3RlbUggNzINCi9JdGFsaWNBbmdsZSAwDQovQ2FwSGVpZ2h0IDUwMA0KL1hIZWlnaHQgMjUwDQovQXNjZW50IDcyOA0KL0Rlc2NlbnQgLTIxMA0KL0xlYWRpbmcgMzMNCi9NYXhXaWR0aCAyNjY1DQovQXZnV2lkdGggNDQxDQo+Pg0KZW5kb2JqDQo1IDAgb2JqDQo8PA0KL1R5cGUgL0ZvbnQNCi9TdWJ0eXBlIC9UcnVlVHlwZQ0KL05hbWUgL0YxNA0KL0Jhc2VGb250IC9BcmlhbA0KL0ZvbnREZXNjcmlwdG9yIDQgMCBSDQovRW5jb2RpbmcgL1dpbkFuc2lFbmNvZGluZw0KL0ZpcnN0Q2hhciAzMA0KL0xhc3RDaGFyIDI1NQ0KL1dpZHRocyBbNzUwIDc1MCAyNzggMjc4IDM1NSA1NTYgNTU2IDg4OSA2NjcgMTkxIA0KMzMzIDMzMyAzODkgNTg0IDI3OCAzMzMgMjc4IDI3OCA1NTYgNTU2IA0KNTU2IDU1NiA1NTYgNTU2IDU1NiA1NTYgNTU2IDU1NiAyNzggMjc4IA0KNTg0IDU4NCA1ODQgNTU2IDEwMTUgNjY3IDY2NyA3MjIgNzIyIDY2NyANCjYxMSA3NzggNzIyIDI3OCA1MDAgNjY3IDU1NiA4MzMgNzIyIDc3OCANCjY2NyA3NzggNzIyIDY2NyA2MTEgNzIyIDY2NyA5NDQgNjY3IDY2NyANCjYxMSAyNzggMjc4IDI3OCA0NjkgNTU2IDMzMyA1NTYgNTU2IDUwMCANCjU1NiA1NTYgMjc4IDU1NiA1NTYgMjIyIDIyMiA1MDAgMjIyIDgzMyANCjU1NiA1NTYgNTU2IDU1NiAzMzMgNTAwIDI3OCA1NTYgNTAwIDcyMiANCjUwMCA1MDAgNTAwIDMzNCAyNjAgMzM0IDU4NCA3NTAgNTU2IDc1MCANCjIyMiA1NTYgMzMzIDEwMDAgNTU2IDU1NiAzMzMgMTAwMCA2NjcgMzMzIA0KMTAwMCA3NTAgNjExIDc1MCA3NTAgMjIyIDIyMiAzMzMgMzMzIDM1MCANCjU1NiAxMDAwIDMzMyAxMDAwIDUwMCAzMzMgOTQ0IDc1MCA1MDAgNjY3IA0KMjc4IDMzMyA1NTYgNTU2IDU1NiA1NTYgMjYwIDU1NiAzMzMgNzM3IA0KMzcwIDU1NiA1ODQgMzMzIDczNyA1NTIgNDAwIDU0OSAzMzMgMzMzIA0KMzMzIDU3NiA1MzcgMzMzIDMzMyAzMzMgMzY1IDU1NiA4MzQgODM0IA0KODM0IDYxMSA2NjcgNjY3IDY2NyA2NjcgNjY3IDY2NyAxMDAwIDcyMiANCjY2NyA2NjcgNjY3IDY2NyAyNzggMjc4IDI3OCAyNzggNzIyIDcyMiANCjc3OCA3NzggNzc4IDc3OCA3NzggNTg0IDc3OCA3MjIgNzIyIDcyMiANCjcyMiA2NjcgNjY3IDYxMSA1NTYgNTU2IDU1NiA1NTYgNTU2IDU1NiANCjg4OSA1MDAgNTU2IDU1NiA1NTYgNTU2IDI3OCAyNzggMjc4IDI3OCANCjU1NiA1NTYgNTU2IDU1NiA1NTYgNTU2IDU1NiA1NDkgNjExIDU1NiANCjU1NiA1NTYgNTU2IDUwMCA1NTYgNTAwIF0NCj4+DQplbmRvYmoNCjYgMCBvYmoNCjw8DQovVHlwZSAvRm9udERlc2NyaXB0b3INCi9Gb250TmFtZSAvQXJpYWwsQm9sZA0KL0ZsYWdzIDE2NDE2DQovRm9udEJCb3ggWy02MjggLTM3NiAyMDAwIDEwNTZdDQovTWlzc2luZ1dpZHRoIDQ3OQ0KL1N0ZW1WIDE0NA0KL1N0ZW1IIDE0NA0KL0l0YWxpY0FuZ2xlIDANCi9DYXBIZWlnaHQgNTAwDQovWEhlaWdodCAyNTANCi9Bc2NlbnQgNzI4DQovRGVzY2VudCAtMjEwDQovTGVhZGluZyAzMw0KL01heFdpZHRoIDI2MjgNCi9BdmdXaWR0aCA0NzkNCj4+DQplbmRvYmoNCjcgMCBvYmoNCjw8DQovVHlwZSAvRm9udA0KL1N1YnR5cGUgL1RydWVUeXBlDQovTmFtZSAvRjE1DQovQmFzZUZvbnQgL0FyaWFsLEJvbGQNCi9Gb250RGVzY3JpcHRvciA2IDAgUg0KL0VuY29kaW5nIC9XaW5BbnNpRW5jb2RpbmcNCi9GaXJzdENoYXIgMzANCi9MYXN0Q2hhciAyNTUNCi9XaWR0aHMgWzc1MCA3NTAgMjc4IDMzMyA0NzQgNTU2IDU1NiA4ODkgNzIyIDIzOCANCjMzMyAzMzMgMzg5IDU4NCAyNzggMzMzIDI3OCAyNzggNTU2IDU1NiANCjU1NiA1NTYgNTU2IDU1NiA1NTYgNTU2IDU1NiA1NTYgMzMzIDMzMyANCjU4NCA1ODQgNTg0IDYxMSA5NzUgNzIyIDcyMiA3MjIgNzIyIDY2NyANCjYxMSA3NzggNzIyIDI3OCA1NTYgNzIyIDYxMSA4MzMgNzIyIDc3OCANCjY2NyA3NzggNzIyIDY2NyA2MTEgNzIyIDY2NyA5NDQgNjY3IDY2NyANCjYxMSAzMzMgMjc4IDMzMyA1ODQgNTU2IDMzMyA1NTYgNjExIDU1NiANCjYxMSA1NTYgMzMzIDYxMSA2MTEgMjc4IDI3OCA1NTYgMjc4IDg4OSANCjYxMSA2MTEgNjExIDYxMSAzODkgNTU2IDMzMyA2MTEgNTU2IDc3OCANCjU1NiA1NTYgNTAwIDM4OSAyODAgMzg5IDU4NCA3NTAgNTU2IDc1MCANCjI3OCA1NTYgNTAwIDEwMDAgNTU2IDU1NiAzMzMgMTAwMCA2NjcgMzMzIA0KMTAwMCA3NTAgNjExIDc1MCA3NTAgMjc4IDI3OCA1MDAgNTAwIDM1MCANCjU1NiAxMDAwIDMzMyAxMDAwIDU1NiAzMzMgOTQ0IDc1MCA1MDAgNjY3IA0KMjc4IDMzMyA1NTYgNTU2IDU1NiA1NTYgMjgwIDU1NiAzMzMgNzM3IA0KMzcwIDU1NiA1ODQgMzMzIDczNyA1NTIgNDAwIDU0OSAzMzMgMzMzIA0KMzMzIDU3NiA1NTYgMzMzIDMzMyAzMzMgMzY1IDU1NiA4MzQgODM0IA0KODM0IDYxMSA3MjIgNzIyIDcyMiA3MjIgNzIyIDcyMiAxMDAwIDcyMiANCjY2NyA2NjcgNjY3IDY2NyAyNzggMjc4IDI3OCAyNzggNzIyIDcyMiANCjc3OCA3NzggNzc4IDc3OCA3NzggNTg0IDc3OCA3MjIgNzIyIDcyMiANCjcyMiA2NjcgNjY3IDYxMSA1NTYgNTU2IDU1NiA1NTYgNTU2IDU1NiANCjg4OSA1NTYgNTU2IDU1NiA1NTYgNTU2IDI3OCAyNzggMjc4IDI3OCANCjYxMSA2MTEgNjExIDYxMSA2MTEgNjExIDYxMSA1NDkgNjExIDYxMSANCjYxMSA2MTEgNjExIDU1NiA2MTEgNTU2IF0NCj4+DQplbmRvYmoNCjggMCBvYmoNCjw8DQovVHlwZSAvRm9udERlc2NyaXB0b3INCi9Gb250TmFtZSAvQXJpYWwsQm9sZEl0YWxpYw0KL0ZsYWdzIDE2NDgwDQovRm9udEJCb3ggWy01NjAgLTM3NiAxMzkwIDEwMThdDQovTWlzc2luZ1dpZHRoIDQ3OQ0KL1N0ZW1WIDE0NA0KL1N0ZW1IIDE0NA0KL0l0YWxpY0FuZ2xlIC0xMg0KL0NhcEhlaWdodCA1MDANCi9YSGVpZ2h0IDI1MA0KL0FzY2VudCA3MjgNCi9EZXNjZW50IC0yMTANCi9MZWFkaW5nIDMzDQovTWF4V2lkdGggMTk1MA0KL0F2Z1dpZHRoIDQ3OQ0KPj4NCmVuZG9iag0KOSAwIG9iag0KPDwNCi9UeXBlIC9Gb250DQovU3VidHlwZSAvVHJ1ZVR5cGUNCi9OYW1lIC9GMTYNCi9CYXNlRm9udCAvQXJpYWwsQm9sZEl0YWxpYw0KL0ZvbnREZXNjcmlwdG9yIDggMCBSDQovRW5jb2RpbmcgL1dpbkFuc2lFbmNvZGluZw0KL0ZpcnN0Q2hhciAzMA0KL0xhc3RDaGFyIDI1NQ0KL1dpZHRocyBbNzUwIDc1MCAyNzggMzMzIDQ3NCA1NTYgNTU2IDg4OSA3MjIgMjM4IA0KMzMzIDMzMyAzODkgNTg0IDI3OCAzMzMgMjc4IDI3OCA1NTYgNTU2IA0KNTU2IDU1NiA1NTYgNTU2IDU1NiA1NTYgNTU2IDU1NiAzMzMgMzMzIA0KNTg0IDU4NCA1ODQgNjExIDk3NSA3MjIgNzIyIDcyMiA3MjIgNjY3IA0KNjExIDc3OCA3MjIgMjc4IDU1NiA3MjIgNjExIDgzMyA3MjIgNzc4IA0KNjY3IDc3OCA3MjIgNjY3IDYxMSA3MjIgNjY3IDk0NCA2NjcgNjY3IA0KNjExIDMzMyAyNzggMzMzIDU4NCA1NTYgMzMzIDU1NiA2MTEgNTU2IA0KNjExIDU1NiAzMzMgNjExIDYxMSAyNzggMjc4IDU1NiAyNzggODg5IA0KNjExIDYxMSA2MTEgNjExIDM4OSA1NTYgMzMzIDYxMSA1NTYgNzc4IA0KNTU2IDU1NiA1MDAgMzg5IDI4MCAzODkgNTg0IDc1MCA1NTYgNzUwIA0KMjc4IDU1NiA1MDAgMTAwMCA1NTYgNTU2IDMzMyAxMDAwIDY2NyAzMzMgDQoxMDAwIDc1MCA2MTEgNzUwIDc1MCAyNzggMjc4IDUwMCA1MDAgMzUwIA0KNTU2IDEwMDAgMzMzIDEwMDAgNTU2IDMzMyA5NDQgNzUwIDUwMCA2NjcgDQoyNzggMzMzIDU1NiA1NTYgNTU2IDU1NiAyODAgNTU2IDMzMyA3MzcgDQozNzAgNTU2IDU4NCAzMzMgNzM3IDU1MiA0MDAgNTQ5IDMzMyAzMzMgDQozMzMgNTc2IDU1NiAzMzMgMzMzIDMzMyAzNjUgNTU2IDgzNCA4MzQgDQo4MzQgNjExIDcyMiA3MjIgNzIyIDcyMiA3MjIgNzIyIDEwMDAgNzIyIA0KNjY3IDY2NyA2NjcgNjY3IDI3OCAyNzggMjc4IDI3OCA3MjIgNzIyIA0KNzc4IDc3OCA3NzggNzc4IDc3OCA1ODQgNzc4IDcyMiA3MjIgNzIyIA0KNzIyIDY2NyA2NjcgNjExIDU1NiA1NTYgNTU2IDU1NiA1NTYgNTU2IA0KODg5IDU1NiA1NTYgNTU2IDU1NiA1NTYgMjc4IDI3OCAyNzggMjc4IA0KNjExIDYxMSA2MTEgNjExIDYxMSA2MTEgNjExIDU0OSA2MTEgNjExIA0KNjExIDYxMSA2MTEgNTU2IDYxMSA1NTYgXQ0KPj4NCmVuZG9iag0KMTAgMCBvYmoNCjw8DQovVHlwZSAvUGFnZXMNCi9Db3VudCAxDQovTWVkaWFCb3ggWzAgMCA2MDkgNzkxXQ0KL1Jlc291cmNlcyAxMSAwIFINCi9LaWRzIFsxMiAwIFIgXQ0KPj4NCmVuZG9iag0KMTEgMCBvYmoNCjw8DQovUHJvY1NldCBbL1BERi9UZXh0XQ0KL0ZvbnQgPDwgL0YxNCA1IDAgUiAvRjE1IDcgMCBSIC9GMTYgOSAwIFIgPj4NCj4+DQplbmRvYmoNCjEyIDAgb2JqDQo8PA0KL1R5cGUgL1BhZ2UNCi9QYXJlbnQgMTAgMCBSDQovQ29udGVudHMgMiAwIFINCi9SZXNvdXJjZXMgMTEgMCBSDQo+Pg0KZW5kb2JqDQoxMyAwIG9iag0KPDwNCi9UeXBlIC9DYXRhbG9nDQovUGFnZXMgMTAgMCBSDQo+Pg0KZW5kb2JqDQp4cmVmDQowIDE0DQowMDAwMDAwMDAwIDY1NTM1IGYNCjAwMDAwMDAwMTAgMDAwMDAgbg0KMDAwMDAwMDI1OSAwMDAwMCBuDQowMDAwMDAyMDI5IDAwMDAwIG4NCjAwMDAwMDIwNTIgMDAwMDAgbg0KMDAwMDAwMjMyMSAwMDAwMCBuDQowMDAwMDAzNDU3IDAwMDAwIG4NCjAwMDAwMDM3MzYgMDAwMDAgbg0KMDAwMDAwNDg3NiAwMDAwMCBuDQowMDAwMDA1MTYzIDAwMDAwIG4NCjAwMDAwMDYzMDkgMDAwMDAgbg0KMDAwMDAwNjQyMCAwMDAwMCBuDQowMDAwMDA2NTE0IDAwMDAwIG4NCjAwMDAwMDY2MDUgMDAwMDAgbg0KdHJhaWxlcg0KPDwNCi9TaXplIDE0DQovSW5mbyAxIDAgUg0KL1Jvb3QgMTMgMCBSDQo+Pg0Kc3RhcnR4cmVmDQo2NjYyDQolJUVPRg0K";
        this.runningDocFetch = null;
        this.isPdfViewerScrollable = true;
        this.gridIsLoading(false);
        this.cd.markForCheck();
    }

    collapseSideBar(): void {
        document.body.classList.add('aside-minimize');
    }

    gridScroll(): void {
        if (document.querySelector('.k-grid .k-grid-content').scrollTop > 0 && this.hideGroupHeaderValues === true) {
            this.hideGroupHeaderValues = false;
            this.cd.markForCheck();
        }
        else if (document.querySelector('.k-grid .k-grid-content').scrollTop === 0 && this.hideGroupHeaderValues === false) {
            this.hideGroupHeaderValues = true;
            this.cd.markForCheck();
        }
    }

    addScrollEventListenerOnceIfMoreThanOneGroup(): void {
        // Calling of addEventListener should happen only once to avoid performance issue
        if (this.scrollEventListenerCounter === 0 && this.gridSettings?.state.group.length > 1) {
            document.querySelector('.k-grid .k-grid-content').addEventListener('scroll', () => this.gridScroll());
            this.scrollEventListenerCounter++;
        }
    }

    onOneModelGridReorder(e: any, gridSettings: GridSettings) {
        let reorderedColumnIndex = gridSettings.columnsConfig.findIndex(c => c.field === e.column.field);
        let reorderedColumn = gridSettings.columnsConfig.splice(reorderedColumnIndex, 1);

        if (e.newIndex === 0) {
            e.newIndex = 1;
        }

        let columnToBeMoved = gridSettings.columnsConfig[e.newIndex - 1];
        let previousColumnIndex = gridSettings.columnsConfig.findIndex(c => c.field === columnToBeMoved.field);

        gridSettings.columnsConfig.splice(previousColumnIndex, 0, ...reorderedColumn);

        this.saveOneModelGridState(gridSettings);
    }

    onOneModelGridResize(e: any, gridSettings: GridSettings) {
        e.forEach(item => {
            var retrievedColumnConfig = gridSettings.columnsConfig.find(col => col.field === item.column.field);
            if (retrievedColumnConfig !== undefined) {
                retrievedColumnConfig._width = item.newWidth;
            }
        });

        gridSettings.columnsConfig.forEach(
            col => {
                if (col.filter === "text" || col.filter === "date") {
                    col.width = 216;
                }
                else if (col.filter === "numeric") {
                    col.width = 108;
                }
            })

        this.cd.markForCheck();

        //this.saveOneModelGridState(gridSettings);
    }

    onOneModelGridVisibilityChange(e: any, gridSettings: GridSettings) {
        e.columns.forEach(column => {
            gridSettings.columnsConfig.find(col => col.field === column.field).hidden = column.hidden;
        });

        this.saveOneModelGridState(gridSettings);
    }

    oneModelGridFilterChange(event: any, gridSettings: GridSettings) {
        //Cleared up collapsed groups as it's causing conflict with grouping expand.
        this.collapsedGroups = [];
        sessionStorage.removeItem("collapsedGroups" + this.action);

        this.saveOneModelGridState(gridSettings);
        this.logFeatureUsage(event, gridSettings, GridActionType.FilterChange);

        this.highlightedItemIds = [];

        this.closePdfViewer();
    }

    oneModelGridGroupChange(event: any, gridSettings: GridSettings) {
        //Cleared up collapsed groups as it's causing conflict with grouping expand.
        this.collapsedGroups = [];
        sessionStorage.removeItem("collapsedGroups" + this.action);

        this.highlightedItemIds = [];

        this.closePdfViewer();

        this.saveOneModelGridState(gridSettings, true);
        this.logFeatureUsage(event, gridSettings, GridActionType.GroupChange);
    }

    onOneModelGridSortChange(event: any, gridSettings: GridSettings) {
        this.highlightedItemIds = [];

        this.closePdfViewer();

        this.saveOneModelGridState(gridSettings);
        this.logFeatureUsage(event, gridSettings, GridActionType.SortChange);
    }

    saveOneModelGridState(gridSettings: GridSettings, isDefault: boolean = false) {
        const gridConfig = {
            columnsConfig: gridSettings.columnsConfig,
            state: gridSettings.state
        };

        let input = new SetDynamicGridStateInput();

        input.gridId = gridSettings.gridId;
        input.gridName = gridSettings.gridName;
        input.gridConfig = JSON.stringify(gridConfig);

        this._dynamicGridStateServiceProxy.setGridState(input).subscribe(() => {
            gridSettings.isDefault = isDefault;
            this.cd.markForCheck();
        });
    }

    groupCollapse(event: any, gridSettings: GridSettings) {
        this.collapsedGroups.push(event.group);
        this.groupInfoList.push(event);

        sessionStorage.setItem("collapsedGroups" + this.action, JSON.stringify(this.groupInfoList));
        this.logFeatureUsage(event, gridSettings, GridActionType.GroupCollapse);
    }

    groupExpand(event: any, gridSettings: GridSettings) {
        let index = this.collapsedGroups.findIndex(g => g.field === event.group.field && g.value === event.group.value);
        if (index > -1) {
            this.collapsedGroups.splice(index, 1);
            this.groupInfoList.splice(index, 1);
            sessionStorage.setItem("collapsedGroups" + this.action, JSON.stringify(this.groupInfoList));
        }
        if (this.collapsedGroups.length === 0) {
            sessionStorage.removeItem("collapsedGroups" + this.action);
            this.groupInfoList = [];
        }

        this.logFeatureUsage(event, gridSettings, GridActionType.GroupExpand);
    }

    appendGroupHeaders(groupField: string, groupValue: string): any {
        if (this.groups[0].field === groupField) {
            // re-initialize the dictionary
            this.groupDictionary = {};
        }

        this.groupDictionary[groupField] = groupValue;

        let result = "";
        let fieldIndex = this.groups.findIndex(g => g.field === groupField);
        let statusIndex = this.groups.findIndex(g => g.editor === "boolean");

        for (var i = 0; i < fieldIndex; i++) {
            let column = this.gridSettings.columnsConfig.find((x: { field: string; }) => x.field === this.groups[i].field);
            if (statusIndex === i) {
                let span = document.createElement("span");
                span.className = this.applyIconToGroup(column.field, this.groupDictionary[column.field], column.editor);
                result += span.outerHTML + column.title + ": " + this.formatDateNumericOrString(column.field, this.groupDictionary[column.field], this.gridSettings.columnsConfig, this.comboBoxData);
            }
            else {
                result += column.title + ": " + this.formatDateNumericOrString(column.field, this.groupDictionary[column.field], this.gridSettings.columnsConfig, this.comboBoxData);
            }

            result += "&nbsp;->&nbsp;";
        }

        return result;
    }

    public onTabSelect(e) {
        this.selectedTabTitle = e.title;
    }

    public selectionChange(e) {
        if (e.selectedRows.length > 0 && this.selectedRows.find(x => x.index === e.selectedRows[0].index) === undefined) {
            this.selectedRows.push(e.selectedRows[0]);
        }
        else if (e.deselectedRows.length > 0 && this.selectedRows.find(x => x.index === e.deselectedRows[0].index) !== undefined) {
            this.selectedRows = this.selectedRows.filter(x => x.index !== e.deselectedRows[0].index);
        }
    }

    keyup($event) {
    }

    click($event) {
    }

    showGridDetails(dataItem: any) {
        return dataItem.sharedWithClient === undefined || (typeof dataItem.sharedWithClient === 'boolean' && dataItem?.id); // Add another input for another property contained in navigation
    }

    onSelect($event): void {
        this.usePreviewGrid = false;
        this.modalFormComponents = [];
        this.modalGridComponents = [];

        if ($event.item === this.l('GoToPage')) {
            this.redirectToPage($event.dataItem.id);
        }
        else if ($event.item === this.l('GoToDynamicPageDesignMaintenance')) {
            if (this.pageName === "DynamicPageMaintenance") {
                const id = ($event.dataItem.id !== undefined) ? $event.dataItem.id : this.parentItem.id;
                this.gotoPage("dynamic-page-design-maintenance", id, "DynamicPageMaintenance");
            }
        }
        else if ($event.item === this.l('GoToDynamicTabDesignMaintenance')) {
            if (this.pageName === "DynamicTabMaintenance") {
                const id = ($event.dataItem.id !== undefined) ? $event.dataItem.id : this.parentItem.id;
                this.gotoPage("dynamic-tab-design-maintenance", id);
            }
        }
        else if ($event.item === this.l('GoToDynamicFormDesignMaintenance')) {
            if (this.pageName === "DynamicFormMaintenance") {
                const id = ($event.dataItem.id !== undefined) ? $event.dataItem.id : this.parentItem.id;
                this.gotoPage("dynamic-form-design-maintenance", id, "DynamicFormMaintenance");
            }
            else if (this.pageName === "DynamicPageDesignMaintenance") {
                const parentDetailPageId = this._activatedRoute.snapshot["_urlSegment"].segments[4]?.path;

                const id = ($event.dataItem.dynamicFormId !== undefined) ? $event.dataItem.dynamicFormId : this.parentItem.id;
                this.gotoPage("dynamic-form-design-maintenance", id, "DynamicPageDesignMaintenance", parentDetailPageId);
            }
        }
        else if ($event.item === this.l('GoToDynamicGridDesignMaintenance')) {
            if (this.pageName === "DynamicGridMaintenance") {
                const id = ($event.dataItem.id !== undefined) ? $event.dataItem.id : this.parentItem.id;
                this.gotoPage("dynamic-grid-design-maintenance", id, "DynamicGridMaintenance");
            }
            else if (this.pageName === "DynamicPageDesignMaintenance") {
                const parentDetailPageId = this._activatedRoute.snapshot["_urlSegment"].segments[4]?.path;

                const id = ($event.dataItem.dynamicGridId !== undefined) ? $event.dataItem.dynamicGridId : this.parentItem.id;
                this.gotoPage("dynamic-grid-design-maintenance", id, "DynamicPageDesignMaintenance", parentDetailPageId);
            }
        }
        else if ($event.item === this.l('GoToPageLayoutDefinitions')) {
            const id = ($event.dataItem.id !== undefined) ? $event.dataItem.id : this.parentItem.id;
            this.gotoPageLayoutDefinitions($event.dataItem.pageLabelKey, $event.dataItem.id);
        }
        else if ($event.item === this.l("DeleteGrid") ||
            $event.item === this.l("DeleteForm") ||
            $event.item === this.l("DeleteActionButton") ||
            $event.item === this.l("DeleteMenu") ||
            $event.item === this.l("DeleteGroupFieldSet") ||
            $event.item === this.l("DeleteChildGroupFieldSet") ||
            $event.item === this.l("DeletePage") ||
            $event.item === this.l("DetachGrid") ||
            $event.item === this.l("DetachForm") ||
            $event.item === this.l("DetachActionButton") ||
            $event.item === this.l("DetachTab") ||
            $event.item === this.l("DeleteField") ||
            $event.item === this.l("DeleteItem")
        ) {
            this.dynamicGridLoading = true;

            let request = new SaveDynamicDataRequest();
            let data = {};
            let deleteColumnField = this.gridSettings.columnsConfig.find(x => x.deleteIdentifier)?.field
            if (deleteColumnField !== undefined) {
                data['id'] = $event.dataItem[deleteColumnField];
            }
            else {
                data['id'] = $event.dataItem.id;
            }

            if (data['id'] === "" || data['id'] === null || data['id'] === undefined) {
                const index = this.items.indexOf($event.dataItem);
                this.items.splice(index, 1);

                //NOTE: Remove item from updatedItems if the item is there
                const updatedItemsIndex = this.updatedItems.indexOf($event.dataItem);
                const deletedItemIsInUpdatedItems = updatedItemsIndex !== -1;
                if (deletedItemIsInUpdatedItems) {
                    this.updatedItems.splice(updatedItemsIndex, 1);
                }

                //NOTE: Remove item from addedItems if the item is there
                const addedItemsIndex = this.addedItems.indexOf($event.dataItem);
                const deletedItemIsInAddedItems = addedItemsIndex !== -1;
                if (deletedItemIsInAddedItems) {
                    this.addedItems.splice(addedItemsIndex, 1);
                }

                if (this.deletedIds.length === 0 && this.addedItems.length === 0 && this.updatedItems.length === 0) {
                    this.disableActionButton = true;
                }
                else {
                    this.disableActionButton = false;
                }

                this.disableActionButtonEmitter.emit(this.disableActionButton);

                this.gridSettings.gridData = process(this.items, this.gridSettings.state);

                this.dynamicGridLoading = false;
            }
            else {
                if (this.isAutoSave) {
                    request.dataSourceName = this.gridDataDataSourceName;
                    request.action = "delete";
                    request.data = data;

                    this._dynamicDataServiceProxy.saveDynamicData(request)
                        .pipe(
                            finalize(() => {
                                this.dynamicGridLoading = false;
                                this.cd.markForCheck();
                            })
                        ).subscribe(() => {
                            const index = this.items.indexOf($event.dataItem);
                            this.items.splice(index, 1);
                            this.gridSettings.gridData = process(this.items, this.gridSettings.state);
                        });
                }
                else {
                    request.dataSourceName = this.gridDataDataSourceName;
                    request.action = "validate";
                    request.data = data;

                    this._dynamicDataServiceProxy.saveDynamicData(request)
                        .pipe(
                            finalize(() => {
                                this.dynamicGridLoading = false;
                                this.cd.markForCheck();
                            })
                        ).subscribe(result => {
                            if (result.data !== null && result.data?.validationMessage?.length > 0) {
                                this.notify.warn(this.l(result.data.validationMessage));
                            }
                            else {
                                let deleteColumnField = this.gridSettings.columnsConfig.find(x => x.deleteIdentifier)?.field
                                if (deleteColumnField !== undefined) {
                                    if ($event.dataItem[deleteColumnField] !== "") {
                                        this.deletedIds.push($event.dataItem[deleteColumnField]);
                                    }
                                }
                                else {
                                    if ($event.dataItem.id !== "") {
                                        this.deletedIds.push($event.dataItem.id);
                                    }
                                }

                                if ($event.item === this.l("DeleteMenu") && this.isChildGrid === true) {
                                    this.deleteChildItemEmitter.emit({ isSuccess: true, itemToDelete: $event.dataItem.id });
                                }

                                const index = this.items.indexOf($event.dataItem);
                                this.items.splice(index, 1);

                                //NOTE: Remove item from updatedItems if the item is there
                                const updatedItemsIndex = this.updatedItems.indexOf($event.dataItem);
                                const deletedItemIsInUpdatedItems = updatedItemsIndex !== -1;
                                if (deletedItemIsInUpdatedItems) {
                                    this.updatedItems.splice(updatedItemsIndex, 1);
                                }

                                //NOTE: Remove item from addedItems if the item is there
                                const addedItemsIndex = this.addedItems.indexOf($event.dataItem);
                                const deletedItemIsInAddedItems = addedItemsIndex !== -1;
                                if (deletedItemIsInAddedItems) {
                                    this.addedItems.splice(addedItemsIndex, 1);
                                }

                                this.disableActionButton = false;
                                this.disableActionButtonEmitter.emit(this.disableActionButton);

                                this.gridSettings.gridData = process(this.items, this.gridSettings.state);
                            }
                        });
                }
            }
        }
        else if ($event.item === this.l('FormControlTypeOptionAndValidation')) {
            if ($event.dataItem.controlTypeId === this.controlTypeNames.TextBox/*FormInput*/) {
                if (!($event.dataItem.controlType && $event.dataItem.controlType?.textBoxType)) {
                    $event.dataItem.controlType = {
                        textBoxType: {
                            options: {
                                isRequired: false,
                                syncedWithErp: false,
                                feedbackMessage: ""
                            },
                            isPassword: false,
                            passwordMatch: false,
                            maximumLength: 0,
                            minimumLength: 0,
                            width: 0,
                            fieldType: "input",
                            regexValidation: ""
                        }
                    };
                }

                const type = $event.dataItem.controlType.textBoxType;

                const dataParent = {
                    isRequired: type.options.isRequired,
                    syncedWithErp: type.options.syncedWithErp,
                    feedbackMessage: type.options.feedbackMessage,
                    isPassword: type.isPassword,
                    passwordMatch: type.passwordMatch,
                    maximumLength: type.maximumLength,
                    minimumLength: type.minimumLength,
                    width: type.width,
                    fieldType: type.fieldType,
                    regexValidation: type.regexValidation
                };

                this.modalFormComponents.push({
                    modalFormStructureDataSourceName: "FormStructureInputControlTypeOption",
                    modalFormDataDataSourceName: "",
                    useParentComponentData: true,
                    parentItem: dataParent
                });
            }
            else if ($event.dataItem.controlTypeId === this.controlTypeNames.TextArea/*FormTextArea*/) {
                if (!($event.dataItem.controlType && $event.dataItem.controlType?.textAreaType)) {
                    $event.dataItem.controlType = {
                        textAreaType: {
                            options: {
                                isRequired: false,
                                syncedWithErp: false,
                                feedbackMessage: ""
                            },
                            maximumLength: 1000,
                            minimumLength: 0,
                            fieldType: "textarea",
                            rows: 3,
                            columns: 50,
                            resizeOption: "resizeInBothDirections",
                        }
                    };
                }

                const type = $event.dataItem.controlType.textAreaType;

                const dataParent = {
                    isRequired: type.options.isRequired,
                    syncedWithErp: type.options.syncedWithErp,
                    feedbackMessage: type.options.feedbackMessage,
                    maximumLength: type.maximumLength,
                    minimumLength: type.minimumLength,
                    fieldType: type.fieldType,
                    rows: type.rows,
                    columns: type.columns,
                    resizeOption: type.resizeOption,
                };

                this.modalFormComponents.push({
                    modalFormStructureDataSourceName: "FormStructureTextAreaControlTypeOption",
                    modalFormDataDataSourceName: "",
                    useParentComponentData: true,
                    parentItem: dataParent
                });
            }
            else if ($event.dataItem.controlTypeId === this.controlTypeNames.Numeric/*FormNumeric*/) {
                if (!($event.dataItem.controlType && $event.dataItem.controlType?.numericType)) {
                    $event.dataItem.controlType = {
                        numericType: {
                            options: {
                                isRequired: false,
                                syncedWithErp: false,
                                feedbackMessage: ""
                            },
                            maximumLength: 0,
                            minimumLength: 0,
                            fieldType: "numeric",
                            numericGrid: [],
                        }
                    };
                }

                const type = $event.dataItem.controlType.numericType;

                const dataParent = {
                    isRequired: type.options.isRequired,
                    syncedWithErp: type.options.syncedWithErp,
                    feedbackMessage: type.options.feedbackMessage,
                    maximumLength: type.maximumLength,
                    minimumLength: type.minimumLength,
                    fieldType: type.fieldType
                };

                const gridParent = {
                    items: type.numericGrid,
                    numericSelection: this.items.filter(x => x.controlTypeId === AppConsts.formControlTypes.formNumeric && x.databaseFieldId !== $event.dataItem.databaseFieldId)
                };

                this.modalFormComponents.push({
                    modalFormStructureDataSourceName: "FormStructureNumericControlTypeOption",
                    modalFormDataDataSourceName: "",
                    useParentComponentData: true,
                    parentItem: dataParent
                });

                this.modalGridComponents.push({
                    modalGridStructureDataSourceName: "GridStructureNumericControlTypeValidation",
                    modalGridDataDataSourceName: "",
                    useParentComponentData: true,
                    parentItem: gridParent,
                    childFieldName: "numericGrid"
                });
            }
            else if ($event.dataItem.controlTypeId === this.controlTypeNames.Date/*FormDate*/) {
                if (!($event.dataItem.controlType && $event.dataItem.controlType?.dateType)) {
                    $event.dataItem.controlType = {
                        dateType: {
                            options: {
                                isRequired: false,
                                syncedWithErp: false,
                                feedbackMessage: ""
                            },
                            mask: "",
                            defaultDate: "",
                            dateGrid: [],
                        }
                    };
                }

                const type = $event.dataItem.controlType.dateType;

                const dataParent = {
                    isRequired: type.options.isRequired,
                    syncedWithErp: type.options.syncedWithErp,
                    feedbackMessage: type.options.feedbackMessage,
                    mask: type.mask,
                    defaultDate: type.defaultDate,
                };

                const gridParent = {
                    items: type.dateGrid,
                    numericSelection: this.items.filter(x => x.controlTypeId === AppConsts.formControlTypes.formDate && x.databaseFieldId !== $event.dataItem.databaseFieldId)
                };

                this.modalFormComponents.push({
                    modalFormStructureDataSourceName: "FormStructureDateControlTypeOption",
                    modalFormDataDataSourceName: "",
                    useParentComponentData: true,
                    parentItem: dataParent
                });

                this.modalGridComponents.push({
                    modalGridStructureDataSourceName: "GridStructureDateControlTypeValidation",
                    modalGridDataDataSourceName: "",
                    useParentComponentData: true,
                    parentItem: gridParent
                });
            }
            else if ($event.dataItem.controlTypeId === this.controlTypeNames.ComboBox/*FormCombobox*/) {
                if (!($event.dataItem.controlType && $event.dataItem.controlType?.comboBoxType)) {
                    $event.dataItem.controlType = {
                        comboBoxType: {
                            options: {
                                isRequired: false,
                                syncedWithErp: false,
                                feedbackMessage: ""
                            },
                            isAutoComplete: false,
                            isMultiSelect: false,
                            dataDependency: false,
                            dependentColumnName: "",
                        }
                    };
                }

                const type = $event.dataItem.controlType.comboBoxType;

                const dataParent = {
                    isRequired: type.options.isRequired,
                    syncedWithErp: type.options.syncedWithErp,
                    feedbackMessage: type.options.feedbackMessage,
                    isAutoComplete: type.isAutoComplete,
                    isMultiSelect: type.isMultiSelect,
                    dataDependency: type.dataDependency,
                    dependentColumnName: type.dependentColumnName,
                };

                this.modalFormComponents.push({
                    modalFormStructureDataSourceName: "FormStructureComboboxControlTypeOption",
                    modalFormDataDataSourceName: "",
                    useParentComponentData: true,
                    parentItem: dataParent
                });
            }
            else if ($event.dataItem.controlTypeId === this.controlTypeNames.DropDown/*FormDropdown*/) {
                if (!($event.dataItem.controlType && $event.dataItem.controlType?.dropdownType)) {
                    $event.dataItem.controlType = {
                        dropdownType: {
                            options: {
                                isRequired: false,
                                syncedWithErp: false,
                                feedbackMessage: ""
                            },
                            dataDependency: false,
                            dependentColumnName: "",
                        }
                    };
                }

                const type = $event.dataItem.controlType.dropdownType;

                const dataParent = {
                    isRequired: type.options.isRequired,
                    syncedWithErp: type.options.syncedWithErp,
                    feedbackMessage: type.options.feedbackMessage,
                    dataDependency: type.dataDependency,
                    dependentColumnName: type.dependentColumnName,
                };

                this.modalFormComponents.push({
                    modalFormStructureDataSourceName: "FormStructureDropdownControlTypeOption",
                    modalFormDataDataSourceName: "",
                    useParentComponentData: true,
                    parentItem: dataParent
                });
            }
            else if ($event.dataItem.controlTypeId === this.controlTypeNames.CheckBox/*FormCheckbox*/) {
                if (!($event.dataItem.controlType && $event.dataItem.controlType?.checkboxType)) {
                    $event.dataItem.controlType = {
                        checkboxType: {
                            options: {
                                isRequired: false,
                                syncedWithErp: false,
                                feedbackMessage: ""
                            },
                            indeterminateOption: false,
                            setDefault: false,
                            rightSidedLabel: false
                        }
                    };
                }

                const type = $event.dataItem.controlType.checkboxType;

                const dataParent = {
                    isRequired: type.options.isRequired,
                    syncedWithErp: type.options.syncedWithErp,
                    feedbackMessage: type.options.feedbackMessage,
                    indeterminateOption: type.indeterminateOption,
                    setDefault: type.setDefault,
                    rightSidedLabel: type.rightSidedLabel
                };

                this.modalFormComponents.push({
                    modalFormStructureDataSourceName: "FormStructureCheckboxControlTypeOption",
                    modalFormDataDataSourceName: "",
                    useParentComponentData: true,
                    parentItem: dataParent
                });
            }
            else if ($event.dataItem.controlTypeId === this.controlTypeNames.RadioButton/*FormRadioButton*/) {
                if (!($event.dataItem.controlType && $event.dataItem.controlType?.radioButtonType)) {
                    $event.dataItem.controlType = {
                        radioButtonType: {
                            options: {
                                isRequired: false,
                                syncedWithErp: false,
                                feedbackMessage: ""
                            }
                        }
                    };
                }

                const type = $event.dataItem.controlType.radioButtonType;

                let dataParent = {};

                if (type.options !== null) {
                    dataParent = {
                        isRequired: type.options.isRequired,
                        syncedWithErp: type.options.syncedWithErp,
                        feedbackMessage: type.options.feedbackMessage,
                    };
                }

                this.modalFormComponents.push({
                    modalFormStructureDataSourceName: "FormStructureRadioButtonControlTypeOption",
                    modalFormDataDataSourceName: "",
                    useParentComponentData: true,
                    parentItem: dataParent
                });

                this.modalGridComponents.push({
                    modalGridStructureDataSourceName: "GridStructureRadioButtonControlTypeValidation",
                    modalGridDataDataSourceName: "",
                    useParentComponentData: true,
                    parentItem: type.choices
                });
            }
            else if ($event.dataItem.controlTypeId === this.controlTypeNames.FileUpload/*FormFileUpload*/) {
                if (!($event.dataItem.controlType && $event.dataItem.controlType?.fileUploadType)) {
                    $event.dataItem.controlType = {
                        fileUploadType: {
                            documentType: "",
                            fileSize: 0
                        }
                    };
                }

                const type = $event.dataItem.controlType.fileUploadType;

                const dataParent = {
                    documentType: type.documentType,
                    fileSize: type.fileSize,
                };

                this.modalFormComponents.push({
                    modalFormStructureDataSourceName: "FormStructureFileUploadDocumentControlTypeOption",
                    modalFormDataDataSourceName: "",
                    useParentComponentData: true,
                    parentItem: dataParent
                });
            }
            else if ($event.dataItem.controlTypeId === this.controlTypeNames.DisplayImage/*FormDisplayImageLogo*/) {
                if (!($event.dataItem.controlType && $event.dataItem.controlType?.displayImageType)) {
                    $event.dataItem.controlType = {
                        displayImageType: {
                            imageName: "",
                        }
                    };
                }

                const type = $event.dataItem.controlType.displayImageType;

                const dataParent = {
                    imageName: type.imageName,
                };

                this.modalFormComponents.push({
                    modalFormStructureDataSourceName: "FormStructureDisplayImageLogoControlTypeOption",
                    modalFormDataDataSourceName: "",
                    useParentComponentData: true,
                    parentItem: dataParent
                });
            }
            else if ($event.dataItem.controlTypeId === this.controlTypeNames.InformationOnly/*FormInformationOnly*/) {
                if (!($event.dataItem.controlType && $event.dataItem.controlType?.informationType)) {
                    $event.dataItem.controlType = {
                        informationType: {
                            dataDependency: false,
                            dependentColumnName: "",
                            textDisplayOption: "wrap",
                        }
                    };
                }

                const type = $event.dataItem.controlType.informationType;

                const dataParent = {
                    dataDependency: type.dataDependency,
                    dependentColumnName: type.dependentColumnName,
                    textDisplayOption: type.textDisplayOption,
                };

                this.modalFormComponents.push({
                    modalFormStructureDataSourceName: "FormStructureInformationOnlyControlTypeOption",
                    modalFormDataDataSourceName: "",
                    useParentComponentData: true,
                    parentItem: dataParent
                });
            }
            else if ($event.dataItem.controlTypeId === this.controlTypeNames.StaticText/*FormStaticText*/) {
                if (!($event.dataItem.controlType && $event.dataItem.controlType?.staticType)) {
                    $event.dataItem.controlType = {
                        staticType: {
                            labelKey: "",
                            link: "",
                            textDisplayOption: "wrap",
                        }
                    };
                }

                const type = $event.dataItem.controlType.staticType;

                const dataParent = {
                    labelKey: type.labelKey,
                    link: type.link,
                    textDisplayOption: type.textDisplayOption,
                };

                this.modalFormComponents.push({
                    modalFormStructureDataSourceName: "FormStructureStaticTextControlTypeOption",
                    modalFormDataDataSourceName: "",
                    useParentComponentData: true,
                    parentItem: dataParent
                });
            }
            else if ($event.dataItem.controlTypeId === this.controlTypeNames.Spacer/*FormSpacer*/) {
                if (!($event.dataItem.controlType && $event.dataItem.controlType?.spacerType)) {
                    $event.dataItem.controlType = {
                        spacerType: {
                            height: ""
                        }
                    };
                }

                const type = $event.dataItem.controlType.spacerType;

                const dataParent = {
                    height: type.height,
                };

                this.modalFormComponents.push({
                    modalFormStructureDataSourceName: "FormStructureSpacerControlTypeOption",
                    modalFormDataDataSourceName: "",
                    useParentComponentData: true,
                    parentItem: dataParent
                });
            }
            else if ($event.dataItem.controlTypeId === this.controlTypeNames.FormArray/*FormArrayData*/) {
                if (!($event.dataItem.controlType && $event.dataItem.controlType?.formArrayDataControlType)) {
                    $event.dataItem.controlType = {
                        formArrayDataControlType: {
                            formArrayFieldLabelKeys: [],
                        }
                    };
                }

                let dataParent = $event.dataItem.controlType?.formArrayDataControlType.formArrayDataFields;

                this.modalGridComponents.push({
                    modalGridStructureDataSourceName: "GridStructureFormArrayDataControlTypeValidation",
                    modalGridDataDataSourceName: "",
                    useParentComponentData: true,
                    parentItem: dataParent
                });
            }

            this.parentItem = {
                action: $event.item,
                dataItem: $event.dataItem,
                dataSource: this.gridDataDataSourceName
            };

            this.dialogTitle = $event.item + " - " + this.getFormControlTypeName($event.dataItem.controlTypeId);

            this.dialogOpened = true;
        }
        else if ($event.item === this.l('GridControlTypeOptionAndValidation')) {
            if ($event.dataItem.controlTypeId === this.controlTypeNames.TextBox/*GridInput*/) {
                if (!($event.dataItem.controlType && $event.dataItem.controlType?.textBoxType)) {
                    $event.dataItem.controlType = {
                        textBoxType: {
                            options: {
                                isRequired: false,
                                syncedWithErp: false,
                                feedbackMessage: ""
                            },
                            isPassword: false,
                            passwordMatch: false,
                            maximumLength: 0,
                            minimumLength: 0,
                            width: 0,
                            fieldType: "input",
                            regexValidation: ""
                        }
                    };
                }

                const type = $event.dataItem.controlType.textBoxType;

                const dataParent = {
                    isRequired: type.options.isRequired,
                    syncedWithErp: type.options.syncedWithErp,
                    feedbackMessage: type.options.feedbackMessage,
                    isPassword: type.isPassword,
                    passwordMatch: type.passwordMatch,
                    maximumLength: type.maximumLength,
                    minimumLength: type.minimumLength,
                    width: type.width,
                    fieldType: type.fieldType,
                    regexValidation: type.regexValidation
                };

                this.modalFormComponents.push({
                    modalFormStructureDataSourceName: "FormStructureInputControlTypeOptionForGrid",
                    modalFormDataDataSourceName: "",
                    useParentComponentData: true,
                    parentItem: dataParent
                });
            }
            else if ($event.dataItem.controlTypeId === this.controlTypeNames.Numeric/*GridNumeric*/) {
                if (!($event.dataItem.controlType && $event.dataItem.controlType?.numericType)) {
                    $event.dataItem.controlType = {
                        numericType: {
                            options: {
                                isRequired: false,
                                syncedWithErp: false,
                                feedbackMessage: ""
                            },
                            maximumLength: 0,
                            minimumLength: 0,
                            fieldType: "numeric",
                            format: "",
                            numericGrid: [],
                        }
                    };
                }

                const type = $event.dataItem.controlType.numericType;

                const dataParent = {
                    isRequired: type.options.isRequired,
                    syncedWithErp: type.options.syncedWithErp,
                    feedbackMessage: type.options.feedbackMessage,
                    maximumLength: type.maximumLength,
                    minimumLength: type.minimumLength,
                    fieldType: type.fieldType,
                    format: type.format
                };

                const gridParent = {
                    items: type.numericGrid,
                    numericSelection: this.items.filter(x => x.controlTypeId === AppConsts.gridControlTypes.gridNumeric && x.databaseFieldId !== $event.dataItem.databaseFieldId)
                };

                this.modalFormComponents.push({
                    modalFormStructureDataSourceName: "FormStructureNumericControlTypeOption",
                    modalFormDataDataSourceName: "",
                    useParentComponentData: true,
                    parentItem: dataParent
                });

                this.modalGridComponents.push({
                    modalGridStructureDataSourceName: "GridStructureNumericControlTypeValidation",
                    modalGridDataDataSourceName: "",
                    useParentComponentData: true,
                    parentItem: gridParent,
                    childFieldName: "numericGrid"
                });
            }
            else if ($event.dataItem.controlTypeId === this.controlTypeNames.Date/*GridDate*/) {
                if (!($event.dataItem.controlType && $event.dataItem.controlType?.dateType)) {
                    $event.dataItem.controlType = {
                        dateType: {
                            options: {
                                isRequired: false,
                                syncedWithErp: false,
                                feedbackMessage: ""
                            },
                            mask: "",
                            defaultDate: "",
                            dateGrid: [],
                        }
                    };
                }

                const type = $event.dataItem.controlType.dateType;

                const dataParent = {
                    isRequired: type.options.isRequired,
                    syncedWithErp: type.options.syncedWithErp,
                    feedbackMessage: type.options.feedbackMessage,
                    mask: type.mask,
                    defaultDate: type.defaultDate,
                };

                const gridParent = {
                    items: type.dateGrid,
                    dateSelection: this.items.filter(x => x.controlTypeId === AppConsts.gridControlTypes.gridDate && x.databaseFieldId !== $event.dataItem.databaseFieldId)
                };

                this.modalFormComponents.push({
                    modalFormStructureDataSourceName: "FormStructureDateControlTypeOption",
                    modalFormDataDataSourceName: "",
                    useParentComponentData: true,
                    parentItem: dataParent
                });

                this.modalGridComponents.push({
                    modalGridStructureDataSourceName: "GridStructureDateControlTypeValidation",
                    modalGridDataDataSourceName: "",
                    useParentComponentData: true,
                    parentItem: gridParent
                });
            }
            else if ($event.dataItem.controlTypeId === this.controlTypeNames.ComboBox/*GridCombobox*/) {
                if (!($event.dataItem.controlType && $event.dataItem.controlType?.comboBoxType)) {
                    $event.dataItem.controlType = {
                        comboBoxType: {
                            options: {
                                isRequired: false,
                                syncedWithErp: false,
                                feedbackMessage: ""
                            },
                            isAutoComplete: false,
                            isMultiSelect: false,
                            dataDependency: false,
                            dependentColumnName: "",
                        }
                    };
                }

                const type = $event.dataItem.controlType.comboBoxType;

                const dataParent = {
                    isRequired: type.options.isRequired,
                    syncedWithErp: type.options.syncedWithErp,
                    feedbackMessage: type.options.feedbackMessage,
                    isAutoComplete: type.isAutoComplete,
                    isMultiSelect: type.isMultiSelect,
                    dataDependency: type.dataDependency,
                    dependentColumnName: type.dependentColumnName,
                };

                this.modalFormComponents.push({
                    modalFormStructureDataSourceName: "FormStructureComboboxControlTypeOption",
                    modalFormDataDataSourceName: "",
                    useParentComponentData: true,
                    parentItem: dataParent
                });
            }
            else if ($event.dataItem.controlTypeId === this.controlTypeNames.DropDown/*GridDropdown*/) {
                if (!($event.dataItem.controlType && $event.dataItem.controlType?.dropdownType)) {
                    $event.dataItem.controlType = {
                        dropdownType: {
                            options: {
                                isRequired: false,
                                syncedWithErp: false,
                                feedbackMessage: ""
                            },
                            dataDependency: false,
                            dependentColumnName: "",
                        }
                    };
                }

                const type = $event.dataItem.controlType.dropdownType;

                const dataParent = {
                    isRequired: type.options.isRequired,
                    syncedWithErp: type.options.syncedWithErp,
                    feedbackMessage: type.options.feedbackMessage,
                    dataDependency: type.dataDependency,
                    dependentColumnName: type.dependentColumnName,
                };

                this.modalFormComponents.push({
                    modalFormStructureDataSourceName: "FormStructureDropdownControlTypeOption",
                    modalFormDataDataSourceName: "",
                    useParentComponentData: true,
                    parentItem: dataParent
                });
            }
            else if ($event.dataItem.controlTypeId === this.controlTypeNames.CheckBox/*GridCheckbox*/) {
                if (!($event.dataItem.controlType && $event.dataItem.controlType?.checkboxType)) {
                    $event.dataItem.controlType = {
                        checkboxType: {
                            options: {
                                isRequired: false,
                                syncedWithErp: false,
                                feedbackMessage: ""
                            },
                            indeterminateOption: false,
                            setDefault: false
                        }
                    };
                }

                const type = $event.dataItem.controlType.checkboxType;

                const dataParent = {
                    isRequired: type.options.isRequired,
                    syncedWithErp: type.options.syncedWithErp,
                    feedbackMessage: type.options.feedbackMessage,
                    indeterminateOption: type.indeterminateOption,
                    setDefault: type.setDefault,
                };

                this.modalFormComponents.push({
                    modalFormStructureDataSourceName: "FormStructureCheckboxControlTypeOption",
                    modalFormDataDataSourceName: "",
                    useParentComponentData: true,
                    parentItem: dataParent
                });
            }
            else if ($event.dataItem.controlTypeId === this.controlTypeNames.InformationOnly/*GridInformationOnly*/) {
                if (!($event.dataItem.controlType && $event.dataItem.controlType?.informationOnlyType)) {
                    $event.dataItem.controlType = {
                        informationOnlyType: {
                            format: "",
                            informationOnlyGrid: [],
                        }
                    };
                }

                const type = $event.dataItem.controlType.informationOnlyType;

                const gridParent = {
                    items: type.informationOnlyGrid,
                    numericSelection: this.items.filter(x => x.controlTypeId === AppConsts.gridControlTypes.gridNumeric && x.databaseFieldId !== $event.dataItem.databaseFieldId)
                };

                //TODO: Add Definition of Array Fields Data Control Type
                this.modalGridComponents.push({
                    modalGridStructureDataSourceName: "GridStructureInformationOnlyControlTypeValidation",
                    modalGridDataDataSourceName: "",
                    useParentComponentData: true,
                    parentItem: gridParent,
                    childFieldName: "informationOnlyGrid"
                });
            }
            else if ($event.dataItem.controlTypeId === this.controlTypeNames.DisplayImage/*GridDisplayImageLogo*/) {
                if (!($event.dataItem.controlType && $event.dataItem.controlType?.displayImageType)) {
                    $event.dataItem.controlType = {
                        displayImageType: {
                            imageName: "",
                        }
                    };
                }

                const type = $event.dataItem.controlType.displayImageType;

                const dataParent = {
                    imageName: type.imageName,
                };

                this.modalFormComponents.push({
                    modalFormStructureDataSourceName: "FormStructureDisplayImageLogoControlTypeOption",
                    modalFormDataDataSourceName: "",
                    useParentComponentData: true,
                    parentItem: dataParent
                });
            }
            else if ($event.dataItem.controlTypeId === this.controlTypeNames.FileUpload/*FormFileUpload*/) {
                if (!($event.dataItem.controlType && $event.dataItem.controlType?.fileUploadType)) {
                    $event.dataItem.controlType = {
                        fileUploadType: {
                            documentType: "",
                            fileSize: ""
                        }
                    };
                }

                const type = $event.dataItem.controlType.fileUploadType;

                const dataParent = {
                    documentType: type.documentType,
                    fileSize: type.fileSize,
                };

                this.modalFormComponents.push({
                    modalFormStructureDataSourceName: "FormStructureMaskedTextBoxControlTypeOption",
                    modalFormDataDataSourceName: "",
                    useParentComponentData: true,
                    parentItem: dataParent
                });
            }

            this.parentItem = {
                action: $event.item,
                dataItem: $event.dataItem,
                gridData: this.items,
                dataSource: this.gridDataDataSourceName
            };

            this.dialogTitle = $event.item + " - " + this.getFormControlTypeName($event.dataItem.controlTypeId);

            this.dialogOpened = true;
        }
        else if ($event.item === this.l("CloneGrid")) {
            this.modalFormComponents.push({
                modalFormStructureDataSourceName: "FormStructureCloneGrid",
                modalFormDataDataSourceName: "FormDataCloneGrid",
                useParentComponentData: false
            });

            this.dialogTitle = $event.item;
            this.parentItem = {
                action: $event.item,
                dataItem: $event.dataItem,
                dataSource: this.gridDataDataSourceName
            };

            this.dialogOpened = true;
        }
        else if ($event.item === this.l("CloneForm")) {
            this.modalFormComponents.push({
                modalFormStructureDataSourceName: "FormStructureCloneForm",
                modalFormDataDataSourceName: "FormDataCloneForm",
                useParentComponentData: false
            });

            this.dialogTitle = $event.item;
            this.parentItem = {
                action: $event.item,
                dataItem: $event.dataItem,
                dataSource: this.gridDataDataSourceName
            };

            this.dialogOpened = true;
        }
        else if ($event.item === this.l("CloneActionButton")) {
            this.modalFormComponents.push({
                modalFormStructureDataSourceName: "FormStructureCloneActionButton",
                modalFormDataDataSourceName: "FormDataCloneActionButton",
                useParentComponentData: false
            });

            this.dialogTitle = $event.item;
            this.parentItem = {
                action: $event.item,
                dataItem: $event.dataItem,
                dataSource: this.gridDataDataSourceName
            };

            this.dialogOpened = true;
        }
        else if ($event.item === this.l("CloneMenu")) {
            this.modalFormComponents.push({
                modalFormStructureDataSourceName: "FormStructureCloneMenu",
                modalFormDataDataSourceName: "FormDataCloneMenu",
                useParentComponentData: false
            });

            this.dialogTitle = $event.item;
            this.parentItem = {
                action: $event.item,
                dataItem: $event.dataItem,
                dataSource: this.gridDataDataSourceName
            };

            this.dialogOpened = true;
        }
        else if ($event.item === this.l("ClonePage")) {
            this.modalFormComponents.push({
                modalFormStructureDataSourceName: "FormStructureClonePage",
                modalFormDataDataSourceName: "FormDataClonePage",
                useParentComponentData: false
            });

            this.dialogTitle = $event.item;
            this.parentItem = {
                action: $event.item,
                dataItem: $event.dataItem,
                dataSource: this.gridDataDataSourceName
            };

            this.dialogOpened = true;
        }
        else if ($event.item === this.l('PreviewGrid')) {
            this.previewGridSettings = _cloneDeep(this.gridSettings);

            let columnsConfig = [];
            for (var item of this.items.filter(x => x.status)) {
                columnsConfig.push(
                    {
                        field: item.databaseFieldId,
                        title: item.fieldLabelKey,
                        width: item.displayWidth,
                        filter: this.retrieveFilter(item.controlTypeId),
                        filterable: item.columnFilterable,
                        format: item.format,
                        sortable: item.columnSortable,
                        headerClass: item.headerClass,
                        hidden: !item.showColumn,
                        editor: this.retrieveEditor(item.controlTypeId),
                        locked: this.hasDetail ? false : item.columnLock
                    });
            }
            this.previewGridSettings.columnsConfig = columnsConfig;
            this.previewGridSettings.gridName = "PreviewGrid";
            this.modalGridDataDataSourceName = this.gridDataDataSourceName;
            this.dialogTitle = this.l("PreviewDialog");

            if (this.modalGridDataDataSourceName !== "" && this.previewGridSettings !== undefined) {
                this.usePreviewGrid = true;
            }

            this.dialogOpened = true;
        }
        else if ($event.item === this.l('MoveToChild')) {
            this.modalFormComponents.push({
                modalFormStructureDataSourceName: "FormStructureMoveToChild",
                modalFormDataDataSourceName: "FormDataMoveToChild",
                useParentComponentData: false,
                parentItem: {
                    action: $event.item,
                    dataItem: $event.dataItem,
                    dataSource: this.gridDataDataSourceName,
                    items: this.items
                }
            });

            this.dialogTitle = $event.item;

            this.dialogOpened = true;
        }
        else if ($event.item === this.l("MoveToParent")) {
            if (this.parentItem !== undefined) {
                let itemIndex = this.items.findIndex(x => x.id === $event.dataItem.id);
                if (itemIndex > -1) {
                    this.items.splice(itemIndex, 1);
                    this.gridSettings.gridData = process(this.items, this.gridSettings.state);

                    this.moveToParentEmitter.emit({ isSuccess: true, itemToMove: $event.dataItem });
                }
            }
        }
        else if ($event.item === this.l('MoveUp')) {
            let itemIndex = this.items.findIndex(x => x.id === $event.dataItem.id);
            if (itemIndex > 0) {
                this.items.splice(itemIndex, 1);
                this.items.splice(itemIndex - 1, 0, $event.dataItem);
                this.gridSettings.gridData = process(this.items, this.gridSettings.state);
            }

            this.disableActionButton = false;
        }
        else if ($event.item === this.l('MoveDown')) {
            let itemIndex = this.items.findIndex(x => x.id === $event.dataItem.id);
            if (itemIndex < this.items.length - 1) {
                this.items.splice(itemIndex, 1);
                this.items.splice(itemIndex + 1, 0, $event.dataItem);
                this.gridSettings.gridData = process(this.items, this.gridSettings.state);
            }

            this.disableActionButton = false;
        }
        else if ($event.item === this.l('PopupDialog')) {
            this.dialogTitle = "Pop-up Dialog";
            this.modalGridStructures = [];
            this.modalGridStructures.push(this.gridStructure);
            this.dialogOpened = true;
        }
        else {
            this.dialogOpened = true;
        }

        this.cd.markForCheck();
    }

    retrieveFilter(controlTypeId: number): string {
        switch (controlTypeId) {
            case 2: return "numeric";
            case 3: return "date";
            case 4:
            case 5: return "text";
            case 6: return "boolean";
            case 9: return "document";
            default: return "text";
        }
    }

    retrieveEditor(controlTypeId: number): string {
        switch (controlTypeId) {
            case 2: return "numeric";
            case 3: return "date";
            case 4:
            case 5: return "combobox";
            case 6: return "boolean";
            case 9: return "document";
            default: return "text";
        }
    }

    disableNumericTextBox(field: string): boolean {
        return false;
    }

    retrieveComboBoxData(databaseFieldName: string, dynamicGridColumnId: string, dataSourceName: string, dataDependentTo: any = null): any[] {
        //for combobox and dropdown validation: retrieve all the columns with combobox and dropdown
        //if validation (numeric/date)
        // retrieve all numeric/date that are in the parent grid
        //populate the input
        if (this.gridStructureDataSourceName === "GridStructureNumericControlTypeValidation") {
            let result = undefined;
            if (databaseFieldName === "numericColumnName") {
                result = this.parentItem.numericSelection?.filter(x => x.controlTypeId === AppConsts.formControlTypes.formNumeric)
                    .map(x => ({ id: x.databaseTableId + "$" + x.databaseFieldId.split("|")[0], name: x.databaseTableId + "$" + x.databaseFieldId.split("|")[0] }));
            }
            else {
                result = this.dataSourceItems.filter(x => x.dataSourceName === dataSourceName).sort((x, y) => y.dataSourceItems.length - x.dataSourceItems.length)[0]?.dataSourceItems;
                if (result !== undefined) {
                    if (this.parentItem?.gridData !== undefined) {
                        // remove the current selection and non-numeric values
                        let gridData: any[] = this.parentItem.gridData;
                        gridData = gridData.filter(x => x.controlTypeId === this.parentItem.dataItem.controlTypeId &&
                            x.databaseTableColumnId !== this.parentItem.dataItem.databaseTableColumnId);

                        result = result.filter(x => {
                            return gridData.some(y => {
                                return y.databaseTableColumnId === x.id;
                            });
                        });
                    }

                }
            }

            return result?.sort((a, b) => a.name.toString().toLowerCase() > b.name.toString().toLowerCase() ? 1 : -1).sort((a, b) => a.name.toString().toLowerCase() === "all" && dataSourceName !== "GetLocalizedKeysAndValues" ? -1 : 0);
        }
        else if (this.gridStructureDataSourceName === "GridStructureDateControlTypeValidation") {
            let result = undefined;
            if (databaseFieldName === "dateColumnName") {
                result = this.parentItem.dateSelection?.filter(x => x.controlTypeId === AppConsts.formControlTypes.formDate)
                    .map(x => ({ id: x.databaseTableId + "$" + x.databaseFieldId.split("|")[0], name: x.databaseTableId + "$" + x.databaseFieldId.split("|")[0] }));
            }
            else {
                result = this.dataSourceItems.filter(x => x.dataSourceName === dataSourceName).sort((x, y) => y.dataSourceItems.length - x.dataSourceItems.length)[0]?.dataSourceItems;
                if (result !== undefined) {
                    if (this.parentItem?.gridData !== undefined) {
                        // remove the current selection and non-date values
                        let gridData: any[] = this.parentItem.gridData;
                        gridData = gridData.filter(x => x.controlTypeId === this.parentItem.dataItem.controlTypeId &&
                            x.databaseTableColumnId !== this.parentItem.dataItem.databaseTableColumnId);

                        result = result.filter(x => {
                            return gridData.some(y => {
                                return y.databaseTableColumnId === x.id;
                            });
                        });
                    }
                }
            }

            return result?.sort((a, b) => a.name.toString().toLowerCase() > b.name.toString().toLowerCase() ? 1 : -1).sort((a, b) => a.name.toString().toLowerCase() === "all" && dataSourceName !== "GetLocalizedKeysAndValues" ? -1 : 0);
        }
        else if (this.gridStructureDataSourceName === "GridStructureFormArrayDataControlTypeValidation") {
            let result = this.dataSourceItems.filter(x => x.dataSourceName === dataSourceName).sort((x, y) => y.dataSourceItems.length - x.dataSourceItems.length)[0]?.dataSourceItems;

            if (result !== undefined) {
                if (this.parentItem?.gridData !== undefined) {
                    // remove the current selection and non-date values
                    let gridData: any[] = this.parentItem.gridData;
                    gridData = gridData.filter(x => x.controlTypeId === this.parentItem.dataItem.controlTypeId &&
                        x.databaseTableColumnId !== this.parentItem.dataItem.databaseTableColumnId);

                    result = result.filter(x => {
                        return gridData.some(y => {
                            return y.databaseTableColumnId === x.id;
                        });
                    });
                }
            }

            return result?.sort((a, b) => a.name.toString().toLowerCase() > b.name.toString().toLowerCase() ? 1 : -1).sort((a, b) => a.name.toString().toLowerCase() === "all" && dataSourceName !== "GetLocalizedKeysAndValues" ? -1 : 0);
        }
        else {
            let selectedParentValue = null;

            const dataDependentToHasValue = dataDependentTo !== "" && dataDependentTo !== undefined && dataDependentTo !== null;
            if (dataDependentToHasValue) {
                selectedParentValue = this.formGroup?.value[dataDependentTo];
            }

            const thereIsNoSelectedParentValue = (selectedParentValue === "" || selectedParentValue === undefined);
            if (thereIsNoSelectedParentValue) {
                // clear child combobox
                this.formGroup?.patchValue({ [databaseFieldName]: "" });
            }

            const isDynamicPages = this.pageName === "";
            if (isDynamicPages) {
                dataSourceName = dynamicGridColumnId;
            }

            let result = this.dataSourceItems.filter(x => x.dataSourceName === dataSourceName && x.dataSourceItems?.length > 0).sort((x, y) => y.dataSourceItems.length - x.dataSourceItems.length)[0]?.dataSourceItems;
            // let result: Array<any> = this.createRandomData(1000);

            // Since Dependency Source and Dependency Source Columns depends only on one method, we need to filter Dependency Source separately.
            if (databaseFieldName === 'dependencySource' && this.gridStructureDataSourceName === 'GridStructureDynamicGridDesignMaintenance') {
                result = result.filter(x => x.parentId === undefined);
            }

            if (this.gridStructure?.componentSettings.gridLabelKey === "OrganizationalUnitsAndEntityRelationships") {
                if (parseInt(this.formGroup?.value['erp_unit_type$name']) >= 30 && databaseFieldName === 'erp_base_unit_hierarchy$parent_base_unit_id') {
                    result = result.filter(x => x.name.includes('(Client)') || x.name.includes('(Entity)'));
                }
            }

            if (selectedParentValue !== undefined && selectedParentValue !== null && selectedParentValue !== "" && result !== undefined) {
                result = result.filter(x => x.parentId === selectedParentValue);

                const selectedValueIsNoLongerAChoice = result.filter(x => x.id === this.formGroup.value[databaseFieldName]).length === 0;
                if (selectedValueIsNoLongerAChoice) {
                    // clear child combobox
                    this.formGroup.patchValue({ [databaseFieldName]: "" });
                }
            }

            const isDatabaseSelection = databaseFieldName === "databaseFieldId" || databaseFieldName === "databaseTableFieldId";
            const isDynamicGridSelection = databaseFieldName === "dynamicGridId" || databaseFieldName === "dependentDynamicGridId";
            const isDynamicFormSelection = databaseFieldName === "dynamicFormId";
            const isDynamicActionButtonSelection = databaseFieldName === "dynamicActionButtonId";
            const isDynamicTabSelection = databaseFieldName === "dynamicTabId";
            // filters out the selected databaseFieldIds in the combobox data for the databaseFieldId input
            if (result !== undefined && isDatabaseSelection) {
                if (!this.currentComboBoxValue) {
                    this.currentComboBoxValue = this.formGroup.controls[databaseFieldName].value;
                }

                result = result.filter(x =>
                    !this.items?.filter(x =>
                        x.databaseTableId === selectedParentValue &&
                        this.currentComboBoxValue !== x[databaseFieldName]).map(x =>
                            x[databaseFieldName].split('|')[0]).includes(x.id.split('|')[0]));
            }

            // filters out the selected grids/forms/action buttons/tabs in the combobox data for the dynamicGridId/dynamicFormId/dynamicActionButtonId/dynamicTabId input
            // in the Dynamic Page Design Maintenance page.
            if (result !== undefined && (isDynamicGridSelection || isDynamicFormSelection || isDynamicActionButtonSelection || isDynamicTabSelection)) {
                if (!this.currentComboBoxValue) {
                    this.currentComboBoxValue = this.formGroup.controls[databaseFieldName].value;
                }

                if (databaseFieldName === "dynamicGridId" || databaseFieldName === "dynamicFormId" ||
                    databaseFieldName === "dynamicActionButtonId" || databaseFieldName === "dynamicTabId") {
                    result = result.filter(x =>
                        !this.items.some(y =>
                            y[databaseFieldName] === x.id &&
                            this.currentComboBoxValue !== y[databaseFieldName]));
                }
                else if (databaseFieldName === "dependentDynamicGridId") {
                    if (this.enteredDoubleClick && this.items.find(x => this.clickedDataItem?.dynamicGridId === x.childDynamicGridId) !== undefined) {
                        result = [];
                    }
                    else {
                        result = result.filter(x => this.items.some(y =>
                            this.enteredDoubleClick ?
                                this.clickedDataItem.dependentDynamicGridId !== null &&
                                    this.clickedDataItem.dependentDynamicGridId !== undefined &&
                                    this.clickedDataItem.dependentDynamicGridId !== '' ?
                                    y.dynamicGridId === x.id &&
                                    (y.dependentDynamicGridId === null || y.dependentDynamicGridId === undefined || y.dependentDynamicGridId === '') &&
                                    this.clickedDataItem?.dynamicGridId !== x.id :
                                    y.dynamicGridId === x.id &&
                                    (y.dependentDynamicGridId === null || y.dependentDynamicGridId === undefined || y.dependentDynamicGridId === '') &&
                                    this.clickedDataItem?.dynamicGridId !== x.id &&
                                    this.items.find(z => y.dynamicGridId === z.dependentDynamicGridId) === undefined :
                                y.dynamicGridId === x.id &&
                                (y.dependentDynamicGridId === null || y.dependentDynamicGridId === undefined || y.dependentDynamicGridId === '') &&
                                this.items.find(z => y.dynamicGridId === z.dependentDynamicGridId) === undefined));
                    }
                }
            }

            if (result !== undefined && this.comboboxFilterValue !== "") {
                result = result.filter(
                    (s) => s.name.toLowerCase().indexOf(this.comboboxFilterValue.toLowerCase()) !== -1
                );
            }

            if ((result === undefined || result.length === 0) || ((selectedParentValue === undefined || selectedParentValue === null) && dataDependentToHasValue)) {
                this.formGroup?.controls[databaseFieldName]?.disable();
            }
            else {
                this.formGroup?.controls[databaseFieldName]?.enable();
            }

            // 200 is the maximum number of values that the combobox can render without slowing down the application.
            if (result?.length > 200 && this.comboboxFilterValue !== "") {
                return result.slice(0, 200)?.sort((a, b) => a.name.toString().toLowerCase() > b.name.toString().toLowerCase() ? 1 : -1).sort((a, b) => a.name.toString().toLowerCase() === "all" && dataSourceName !== "GetLocalizedKeysAndValues" ? -1 : 0);
            }
            else {
                return result?.sort((a, b) => a.name.toString().toLowerCase() > b.name.toString().toLowerCase() ? 1 : -1).sort((a, b) => a.name.toString().toLowerCase() === "all" && dataSourceName !== "GetLocalizedKeysAndValues" ? -1 : 0);
            }
        }
    }

    onFilterChange(value, columnField = undefined): void {
        if (columnField !== undefined) {
            this.comboBoxData[columnField] = this.originalComboBoxData[columnField].filter(
                (s) => s.name.toLowerCase().indexOf(value.toLowerCase()) !== -1
            );
        }
        else {
            this.comboBoxData[this.focusedInputField] = this.originalComboBoxData[this.focusedInputField].filter(
                (s) => s.name.toLowerCase().indexOf(value.toLowerCase()) !== -1
            );
        }
    }

    onMultiSelectValueChange(value, col = null): void {
        this.showFeedbackMessage(col);

        this.updateComboBoxTitle();

        this.refreshComboBoxData();

        this.updateInputStyles();
    }

    onComboBoxValueChange(value, col = null): void {
        this.setParentUnitTypeCode(value, col);

        this.showFeedbackMessage(col);

        this.updateComboBoxTitle();

        this.refreshComboBoxData();

        this.updateInputStyles();
    }

    onDatePickerValueChange(col = null): void {
        this.showFeedbackMessage(col);
    }

    onCheckBoxValueChange(col = null): void {
        this.showFeedbackMessage(col);
    }

    setParentUnitTypeCode(value, col) {
        if (col !== null &&
            this.gridStructure?.componentSettings.gridLabelKey === "OrganizationalUnitsAndEntityRelationships" &&
            col.field === 'erp_base_unit_hierarchy$parent_base_unit_id') {
            var result = value.split("_");
            if (this.clickedDataItem !== undefined) {
                this.clickedDataItem['erp_base_unit_hierarchy$parent_unit_type_code'] = result[1];
            }
            else {
                this.formGroup['erp_base_unit_hierarchy$parent_unit_type_code'] = result[1];
            }
        }
    }

    private updateInputStyles() {
        this.gridSettings?.columnsConfig?.forEach(col => {
            this.inputStyles[col.field] = this.isInputRequired(col.field);
        });
    }

    showFeedbackMessage(col) {
        if (col !== null && col !== undefined && col.optionAndValidation !== null && col.optionAndValidation !== undefined) {
            if (col.optionAndValidation.feedbackMessage !== '' && col.optionAndValidation.feedbackMessage !== undefined && !this.feedbackMade[col.field]) {
                this.message.info(this.l(col.optionAndValidation.feedbackMessage));
                this.feedbackMade[col.field] = true;
            }
        }
    }

    onTextBoxValueChange(value, col = null): void {
        this.showFeedbackMessage(col);

        this.updateInputStyles();
    }

    disableComboBox(col) {
        if (col.optionAndValidation?.dataDependentTo !== undefined) {
            const parentValue = this.formGroup?.get(col.optionAndValidation.dataDependentTo).value;
            if (parentValue === null || parentValue === undefined) {
                return true;
            }
            else {
                let result = this.dataSourceItems.filter(x => x.dataSourceName === col.dataSourceName && x.dataSourceItems?.length > 0)
                    .sort((x, y) => y.dataSourceItems.length - x.dataSourceItems.length)[0]?.dataSourceItems;
                result = result?.filter(x => x.parentId === parentValue);

                return result === undefined || result.length === 0;
            }
        }
        else {
            const isDynamicPages = this.pageName === "";
            const dataSourceName = isDynamicPages ? (col.id ? col.id : col.dataSourceName) : col.dataSourceName;

            let result = this.dataSourceItems.filter(x => x.dataSourceName === dataSourceName && x.dataSourceItems?.length > 0)
                .sort((x, y) => y.dataSourceItems.length - x.dataSourceItems.length)[0]?.dataSourceItems;

            return result === undefined || result.length === 0;
        }
    }

    handleComboBoxChange(field: string) {
    }

    handleNumericOrStringChange(col: any) {
        let field = col.field;
        this.showFeedbackMessage(col);

        var columnsWithValidation = this.dynamicFields.filter(df => df.validation !== undefined && df.validation.length > 0);
        for (var column of columnsWithValidation) {
            var columnValidation = this.retrieveValidation(column);
            if (columnValidation.validationFields.findIndex(vf => vf.fieldNameToCombine === field) >= 0) {
                if (columnValidation.type === undefined) { // number//ValidationTypeDto._0
                    var total = 0;
                    var previousOperator = null;

                    for (var validationField of columnValidation.validationFields) {
                        var currentValue = this.formGroup.value[validationField.fieldNameToCombine];

                        if (previousOperator === null) {
                            total = currentValue;
                        }
                        else {
                            if (previousOperator === "+") { // plus
                                total += currentValue;
                            }
                            else if (previousOperator === "-") { // minus
                                total -= currentValue;
                            }
                            else if (previousOperator === "*") { // multiply
                                total *= currentValue;
                            }
                            else if (previousOperator === "/") { // divide
                                total /= currentValue;
                            }
                        }

                        previousOperator = validationField.operator;
                    }

                    this.formGroup.controls[column.databaseFieldName].setValue(total);
                }
                else if (columnValidation.type === undefined) { // string //ValidationTypeDtoType._1
                    var result = "";
                    var previousSeparator = null;
                    var counter = 0;

                    for (var validationField of columnValidation.validationFields) {
                        var currentValue = this.formGroup.value[validationField.fieldNameToCombine];

                        if (counter === 0) {
                            result = currentValue;
                        }
                        else {
                            result += previousSeparator + currentValue;
                        }

                        previousSeparator = validationField.operator;
                        counter++;
                    }

                    this.formGroup.controls[column.databaseFieldName].setValue(result);
                }
            }
        }

        this.updateInputStyles();
    }

    retrieveValidation(column) {
        var result = undefined;

        return result;
    }

    gotoPageLayoutDefinitions(pageLabelKey: string, pageId: number): void {
        this._router.onSameUrlNavigation = 'ignore';

        this._router.navigate(['app/main/page-layout-definitions/' + pageLabelKey, pageId]);
    }

    gotoPage(pageName: string, id: string, parentPageLabelKey: string = "", parentDetailPageId: string = ""): void {
        this._router.onSameUrlNavigation = 'ignore';

        if (id === "") {
            this._router.navigate(['app/main/admin-page/' + pageName]);
        }
        else {
            this._router.navigate(['app/main/admin-page/' + pageName, id, parentPageLabelKey, parentDetailPageId]);
        }
    }

    redirectToPage(pageId: string, detailPageId: string = ""): void {
        this._router.onSameUrlNavigation = 'ignore';

        if (this._activatedRoute.snapshot["_urlSegment"].segments[0].path === 'app' && this._activatedRoute.snapshot["_urlSegment"].segments[1].path === 'main') {
            if (detailPageId === "") {
                this._router.navigate(['app/main/dynamic-page/' + pageId]);
            }
            else {
                this.masterPageId = this._activatedRoute.snapshot["_urlSegment"].segments[3]?.path;
                this._router.navigate(['app/main/dynamic-detail-page/', pageId, detailPageId, this.pageLabelKey, this.masterPageId]);
            }
        }
        else {
            if (detailPageId === "") {
                this._router.navigate(['dynamic-page/' + pageId]);
            }
            else {
                this.masterPageId = this._activatedRoute.snapshot["_urlSegment"].segments[1]?.path;
                this._router.navigate(['dynamic-detail-page/', pageId, detailPageId, this.pageLabelKey, this.masterPageId]);
            }
        }
    }

    redirectToNewRecordForm(): void {
        this.redirectToPage(this.attachedRedirectPageId, "00000000-0000-0000-0000-000000000000");
    }

    hasAddNewRecordButton(): boolean {
        const componentSettingsExists = this.gridStructure?.componentSettings !== null && this.gridStructure?.componentSettings !== undefined;
        const hasAttachedRedirectPageId = this.gridStructure?.componentSettings?.attachedRedirectPageId !== undefined && this.gridStructure?.componentSettings?.attachedRedirectPageId !== "";
        const isFormRedirectAdd = this.gridStructure?.componentSettings?.enableAddRecord === EnableAddRecord.FormRedirectAdd;

        return (componentSettingsExists && hasAttachedRedirectPageId && isFormRedirectAdd && this.isStreamlineaPages(this.pageName));
    }

    cellClick(e): void {
        const componentSettingsExists = this.gridStructure?.componentSettings !== null && this.gridStructure?.componentSettings !== undefined;
        const hasAttachedRedirectPageId = this.gridStructure?.componentSettings?.attachedRedirectPageId !== undefined && this.gridStructure?.componentSettings?.attachedRedirectPageId !== "";
        const isRowEditable = this.gridStructure?.componentSettings?.allowEdits;
        const isRowIndexAtLeastZero = e.rowIndex > -1;

        // This boolean flag is applied according to SLD-3074
        const hasEnabledRowSelectionOptions = this.gridStructure?.componentSettings?.checkBoxOnly ||
            this.gridStructure?.componentSettings?.enableSelectAll || this.gridStructure?.componentSettings?.multipleRows;

        if (componentSettingsExists && hasAttachedRedirectPageId && this.isStreamlineaPages(this.pageName) && !hasEnabledRowSelectionOptions && !isRowEditable && isRowIndexAtLeastZero) {
            // This section only applies to created dynamic pages as per SLD-3072
            this.redirectToPage(this.attachedRedirectPageId, e.dataItem.id);
        }
        else if (componentSettingsExists && hasAttachedRedirectPageId && this.isStreamlineaPages(this.pageName) && (isRowEditable || hasEnabledRowSelectionOptions) && e.originalEvent.button === 2 && isRowIndexAtLeastZero) {
            // e.originalEvent.button === 2 means that the right mouse button was clicked
            // This scenario occurs when a grid has editable rows or has enabled row selection options, and has an attached redirect page id
            this.redirectToPage(this.attachedRedirectPageId, e.dataItem.id);
        }
        else if ((componentSettingsExists || this.useItemsInputAsData) && this.isStreamlineaPages(this.pageName) && !hasEnabledRowSelectionOptions && isRowEditable && e.originalEvent.button !== 2) {
            if (!e.isEdited) {
                this.clickedSender = e.sender;
                this.clickedIsEdited = e.isEdited;
                this.clickedRowIndex = e.rowIndex;
                this.clickedColumnIndex = e.columnIndex;
                this.clickedDataItem = e.dataItem;
                this.rightClickedDataItem = e.dataItem;
            }

            this.editCell(e);
        }
        else {
            if (!e.isEdited) {
                this.clickedSender = e.sender;
                this.clickedIsEdited = e.isEdited;
                this.clickedRowIndex = e.rowIndex;
                this.clickedColumnIndex = e.columnIndex;
                this.clickedDataItem = e.dataItem;
                this.rightClickedDataItem = e.dataItem;
            }

            this.cellClickEmitter.emit(e);
        }

        if (this.gridStructure?.componentSettings !== undefined && this.gridStructure?.componentSettings?.dependentDynamicGridId) {
            abp.event.trigger('app.grid_cell_' + this.gridStructure.componentSettings.dependentDynamicGridId + '.onClick', [e.dataItem], this.gridStructure);
        }

        this.gridContextMenuItems = this.getGridContextMenuItems(this.gridStructure?.componentSettings, this.gridStructureDataSourceName, this.parentItem, this.items, this.rightClickedDataItem);

        if (this.gridStructure?.componentSettings?.gridLabelKey?.includes("UserMessaging")) {
            this._router.navigateByUrl(this._router.url.replace(this._activatedRoute.snapshot.params['detailPageId'], e.dataItem.id));
        }
    }

    editingEntity() {
        return this.gridStructure?.componentSettings.gridLabelKey === 'OrganizationalUnitsAndEntityRelationships' && this.clickedDataItem['erp_unit_type$name'] === '30' &&
            this.gridSettings.columnsConfig[this.clickedColumnIndex].field !== 'erp_base_unit_hierarchy$parent_base_unit_id';
    }

    isColumnEditable() {
        if (this.clickedColumnIndex !== undefined) {
            const usesCheckboxOnly = this.gridStructure?.componentSettings?.checkBoxOnly;
            const columnIndex = usesCheckboxOnly === true ? this.clickedColumnIndex - 1 : this.clickedColumnIndex;

            if (this.editingEntity()) {
                return false;
            }

            if (this.gridSettings?.columnsConfig[columnIndex]?.editable !== undefined) {
                return this.gridSettings.columnsConfig[columnIndex].editable;
            }
            else if (this.gridSettings?.columnsConfig[columnIndex]?.allowEdits !== undefined) {
                return this.gridSettings.columnsConfig[columnIndex].allowEdits;
            }
            else if (this.gridSettings.columnsConfig[columnIndex].field === 'dateCreated' || this.gridSettings.columnsConfig[columnIndex].field === 'dateModified') {
                return false;
            }
            else {
                return true;
            }
        }
        else {
            return false;
        }
    }

    dblclick() {
        // This is used for filtering data in the combobox input. It determines if input is being used in add mode or edit mode.
        this.enteredDoubleClick = true;
        this.editCell();
    }

    private editCell(e = undefined) {
        // Clears up the current combo box value to properly update the combo box input
        this.currentComboBoxValue = "";
        
        if (this.allowEdits && this.isColumnEditable()) {
            if (!this.clickedIsEdited) {
                if (this.clickedSender !== undefined) {
                    this.clickedSender.closeRow(-1);
                    this.cellIsEdited = true;
                    
                    if (!['', undefined].includes(this.defaultGridSettings.columnsConfig[this.clickedColumnIndex]?.optionAndValidation?.dataDependentTo)) {
                        var column = this.defaultGridSettings.columnsConfig.find(x => x.field === this.defaultGridSettings.columnsConfig[this.clickedColumnIndex].optionAndValidation.dataDependentTo);
                        if (this.dataSourceItems?.find(x => x.dataSourceName === column?.dataSourceName)?.dataSourceItems?.find(x => x.id === this.clickedDataItem[column.field]) !== '') {
                            this.clickedSender.editCell(this.clickedRowIndex, this.clickedColumnIndex, this.createFormGroup(this.clickedDataItem));
                        }
                        else {
                            this.clickedSender.editCell(this.clickedRowIndex, column.field, this.createFormGroup(this.clickedDataItem));
                        }
                    }
                    else {
                        this.clickedSender.editCell(this.clickedRowIndex, this.clickedColumnIndex, this.createFormGroup(this.clickedDataItem));
                    }

                    // This is done in single cell clicks for Streamlinea pages (See: cellClick method)
                    if (e !== undefined) {
                        e?.originalEvent?.stopPropagation();
                    }
                    else {
                        event.stopPropagation();
                    }
                }
            }
        }
        else if (this.clickedDataItem !== undefined && this.editingEntity()) {
            this.notify.info(this.l("EditErrorForOrgUnits"));
        }

        this.refreshComboBoxData();
    }

    createFormGroup(dataItem: any): FormGroup {
        //TODO: add Validators.required
        this.applyNumericGridToBaseForm = false;
        this.applyDateGridToBaseForm = false;

        if (this.defaultGridSettings.columnsConfig.length > 0) {
            let dateFieldGridDictionary: { [dateField: string]: any } = {};
            let numericFieldGridDictionary: { [numericField: string]: any } = {};
            this.formGroup = new FormGroup({});

            this.formGroup.setControl("id", new FormControl(""));

            this.defaultGridSettings.columnsConfig.map(field => {
                let validators = []

                if (field?.optionAndValidation?.required) {
                    validators.push(Validators.required);
                }

                if (field.editor === "date") {
                    if (field.optionAndValidation?.dateGrid !== undefined && field.optionAndValidation?.dateGrid?.length > 0) {
                        const pastThanOperator = field.optionAndValidation?.dateGrid.findIndex(x => x.operator === AppConsts.dateOperators.pastThan &&
                            x.dateColumnName && x.dateColumnName !== "") > -1;
                        const futureThanOperator = field.optionAndValidation?.dateGrid.findIndex(x => x.operator === AppConsts.dateOperators.futureThan &&
                            x.dateColumnName && x.dateColumnName !== "") > -1;
                        const betweenOperator = field.optionAndValidation?.dateGrid.findIndex(x => x.operator === AppConsts.dateOperators.between &&
                            x.dateColumnName && x.dateColumnName !== "") > -1;

                        if (pastThanOperator || futureThanOperator || betweenOperator) {
                            this.applyDateGridToBaseForm = true;
                            dateFieldGridDictionary[field.field] = field.optionAndValidation?.dateGrid;
                        }
                    }

                    this.applyDateValidator(field, validators, dataItem);
                }
                else if (field.editor === "combobox") {
                    if (field.field === 'clientId' || field.field === 'erpId' || field.field === 'cultureId' || field.field === 'dynamicPageTypeId') {
                        this.formGroup.setControl(field.field, new FormControl(dataItem[field.field], validators));
                    } else {
                        this.formGroup.setControl(field.field, new FormControl(dataItem[field.field], validators));
                    }
                }
                else if (field.editor === "numeric") {
                    if (field.optionAndValidation?.numericGrid !== undefined && field.optionAndValidation?.numericGrid?.length > 0) {
                        numericFieldGridDictionary[field.field] = field.optionAndValidation?.numericGrid;
                    }

                    this.applyNumericValidator(field, validators, dataItem);
                }
                else if (field.editor === "boolean") {
                    const value = dataItem[field.field] !== undefined ? dataItem[field.field] : false;
                    this.formGroup.setControl(field.field, new FormControl(value));
                }
                else {
                    if (field.optionAndValidation?.minimumLength !== undefined) {
                        validators.push(Validators.minLength(field.optionAndValidation.minimumLength));
                    }

                    if (field.optionAndValidation?.maximumLength > 0) {
                        validators.push(Validators.maxLength(field.optionAndValidation.maximumLength));
                    }

                    this.formGroup.setControl(field.field, new FormControl(dataItem[field.field], validators));
                }
            });

            if (this.applyNumericGridToBaseForm) {
                for (let key in numericFieldGridDictionary) {
                    if (this.formGroup.validator !== null) {
                        this.formGroup.setValidators([this.numericGridValidator(key, numericFieldGridDictionary[key]), this.formGroup.validator]);
                    }
                    else {
                        this.formGroup.setValidators(this.numericGridValidator(key, numericFieldGridDictionary[key]));
                    }
                }
            }

            if (this.applyDateGridToBaseForm) {
                for (let key in dateFieldGridDictionary) {
                    let dateGrid = dateFieldGridDictionary[key];
                    if (dateGrid !== undefined && dateGrid.length > 0) {
                        // Checks if Date Grid is using the Past Than Operator
                        if (dateGrid.findIndex(x => x.operator === AppConsts.dateOperators.pastThan && x.dateColumnName && x.dateColumnName !== "") > -1) {
                            let value = dateGrid.find(x => x.operator === AppConsts.dateOperators.pastThan);

                            if (this.formGroup.validator !== null) {
                                this.formGroup.setValidators([this.dateLessThan(key, value.dateColumnName), this.formGroup.validator]);
                            }
                            else {
                                this.formGroup.setValidators(this.dateLessThan(key, value.dateColumnName));
                            }
                        }
                        // Checks if Date Grid is using the Future Than Operator
                        else if (dateGrid.findIndex(x => x.operator === AppConsts.dateOperators.futureThan && x.dateColumnName && x.dateColumnName !== "") > -1) {
                            let value = dateGrid.find(x => x.operator === AppConsts.dateOperators.futureThan);

                            if (this.formGroup.validator !== null) {
                                this.formGroup.setValidators([this.dateGreaterThan(key, value.dateColumnName), this.formGroup.validator]);
                            }
                            else {
                                this.formGroup.setValidators(this.dateGreaterThan(key, value.dateColumnName));
                            }
                        }
                        // Checks if Date Grid is using the Between Operator
                        else if (dateGrid.findIndex(x => x.operator === AppConsts.dateOperators.between && x.dateColumnName && x.dateColumnName !== "") > -1) {
                            let firstDate = dateGrid.find(x => x.operator === AppConsts.dateOperators.between).dateColumnName;
                            let secondDate = dateGrid.find(x => x.operator === AppConsts.dateOperators.noDate).dateColumnName;

                            if (this.formGroup.validator !== null) {
                                this.formGroup.setValidators([this.dateInRange(key, firstDate, secondDate), this.formGroup.validator]);
                            }
                            else {
                                this.formGroup.setValidators(this.dateInRange(key, firstDate, secondDate));
                            }
                        }
                    }
                }
            }
        }

        return this.formGroup;
    }

    addHandler(isLastActionEdit = false) {
        this.applyNumericGridToBaseForm = false;
        this.applyDateGridToBaseForm = false;

        if (this.enableAddRecord === EnableAddRecord.GridAdd) {
            let dateFieldGridDictionary: { [dateField: string]: any } = {};
            let numericFieldGridDictionary: { [numericField: string]: any } = {};
            this.formGroup = new FormGroup({});

            this.formGroup.setControl("id", new FormControl(""));

            if (this.defaultGridSettings.columnsConfig.length > 0) {
                this.defaultGridSettings.columnsConfig.map(field => {
                    let validators: ValidatorFn[] = []

                    if (field?.optionAndValidation?.required) {
                        validators.push(Validators.required);
                    }

                    if (field.editor === "date") {
                        if (field.optionAndValidation?.dateGrid !== undefined && field.optionAndValidation?.dateGrid?.length > 0) {
                            const pastThanOperator = field.optionAndValidation?.dateGrid.findIndex(x => x.operator === AppConsts.dateOperators.pastThan &&
                                x.dateColumnName && x.dateColumnName !== "") > -1;
                            const futureThanOperator = field.optionAndValidation?.dateGrid.findIndex(x => x.operator === AppConsts.dateOperators.futureThan &&
                                x.dateColumnName && x.dateColumnName !== "") > -1;
                            const betweenOperator = field.optionAndValidation?.dateGrid.findIndex(x => x.operator === AppConsts.dateOperators.between &&
                                x.dateColumnName && x.dateColumnName !== "") > -1;

                            if (pastThanOperator || futureThanOperator || betweenOperator) {
                                this.applyDateGridToBaseForm = true;
                                dateFieldGridDictionary[field.field] = field.optionAndValidation?.dateGrid;
                            }
                        }

                        this.applyDateValidator(field, validators);
                    }
                    else if (field.editor === "combobox") {
                        //NOTE: Set default value
                        if (field.field === 'clientId' || field.field === 'erpId' || field.field === 'cultureId') {
                            this.formGroup.setControl(field.field, new FormControl(0, validators));
                        } else if (field.field === 'dynamicPageTypeId') {
                            this.formGroup.setControl(field.field, new FormControl(AppConsts.pageType.parentPage, validators));
                        } else {
                            this.formGroup.setControl(field.field, new FormControl(undefined, validators));
                        }
                    }
                    else if (field.editor === "numeric") {
                        if (field.optionAndValidation?.numericGrid !== undefined && field.optionAndValidation?.numericGrid?.length > 0) {
                            numericFieldGridDictionary[field.field] = field.optionAndValidation?.numericGrid;
                        }

                        this.applyNumericValidator(field, validators);
                    }
                    else if (field.editor === "boolean") {
                        if (field.optionAndValidation?.maintenanceDefault !== undefined) {
                            this.formGroup.setControl(field.field, new FormControl(field.optionAndValidation?.maintenanceDefault));
                        }
                        else if (field.optionAndValidation?.setDefault !== undefined) {
                            this.formGroup.setControl(field.field, new FormControl(field.optionAndValidation?.setDefault));
                        }
                        else {
                            this.formGroup.setControl(field.field, new FormControl(true));
                        }
                    }
                    else {
                        if (field.optionAndValidation?.minimumLength !== undefined) {
                            validators.push(Validators.minLength(field.optionAndValidation.minimumLength));
                        }

                        if (field.optionAndValidation?.maximumLength > 0) {
                            validators.push(Validators.maxLength(field.optionAndValidation.maximumLength));
                        }

                        if (field.optionAndValidation?.regexValidation !== undefined && field.optionAndValidation?.regexValidation !== "") {
                            var parsedValidation = field.optionAndValidation.regexValidation.replace(/\\\\/g, "\\");
                            validators.push(Validators.pattern(new RegExp(parsedValidation, "gm")));
                        }

                        this.formGroup.setControl(field.field, new FormControl('', validators));
                    }
                });
            }

            if (this.applyNumericGridToBaseForm) {
                for (let key in numericFieldGridDictionary) {
                    if (this.formGroup.validator !== null) {
                        this.formGroup.setValidators([this.numericGridValidator(key, numericFieldGridDictionary[key]), this.formGroup.validator]);
                    }
                    else {
                        this.formGroup.setValidators(this.numericGridValidator(key, numericFieldGridDictionary[key]));
                    }
                }
            }

            if (this.applyDateGridToBaseForm) {
                for (let key in dateFieldGridDictionary) {
                    let dateGrid = dateFieldGridDictionary[key];
                    if (dateGrid !== undefined && dateGrid.length > 0) {
                        // Checks if Date Grid is using the Past Than Operator
                        if (dateGrid.findIndex(x => x.operator === AppConsts.dateOperators.pastThan && x.dateColumnName && x.dateColumnName !== "") > -1) {
                            let value = dateGrid.find(x => x.operator === AppConsts.dateOperators.pastThan);

                            if (this.formGroup.validator !== null) {
                                this.formGroup.setValidators([this.dateLessThan(key, value.dateColumnName), this.formGroup.validator]);
                            }
                            else {
                                this.formGroup.setValidators(this.dateLessThan(key, value.dateColumnName));
                            }
                        }
                        // Checks if Date Grid is using the Future Than Operator
                        else if (dateGrid.findIndex(x => x.operator === AppConsts.dateOperators.futureThan && x.dateColumnName && x.dateColumnName !== "") > -1) {
                            let value = dateGrid.find(x => x.operator === AppConsts.dateOperators.futureThan);

                            if (this.formGroup.validator !== null) {
                                this.formGroup.setValidators([this.dateGreaterThan(key, value.dateColumnName), this.formGroup.validator]);
                            }
                            else {
                                this.formGroup.setValidators(this.dateGreaterThan(key, value.dateColumnName));
                            }
                        }
                        // Checks if Date Grid is using the Between Operator
                        else if (dateGrid.findIndex(x => x.operator === AppConsts.dateOperators.between && x.dateColumnName && x.dateColumnName !== "") > -1) {
                            let firstDate = dateGrid.find(x => x.operator === AppConsts.dateOperators.between).dateColumnName;
                            let secondDate = dateGrid.find(x => x.operator === AppConsts.dateOperators.noDate).dateColumnName;

                            if (this.formGroup.validator !== null) {
                                this.formGroup.setValidators([this.dateInRange(key, firstDate, secondDate), this.formGroup.validator]);
                            }
                            else {
                                this.formGroup.setValidators(this.dateInRange(key, firstDate, secondDate));
                            }
                        }
                    }
                }
            }

            if ((this.grid !== undefined && this.grid !== null) && this.allowAdd) {
                const isDynamicGridDesignMaintenancePage = this.gridName === undefined && this.gridId === undefined;
                if (isDynamicGridDesignMaintenancePage) {
                    this.setDisplayWidthAndExportWidth();
                    this.setGridColumnDisplayOrder(isLastActionEdit);
                }

                if (!this.hasAddNewRecordButton()) {
                    if (this.initGrid) {
                        this.initAddRow = true;
                        this.initGrid = false;
                    }
                    else {
                        this.gridAddRow();
                    }
                }
            }
        }
    }

    private setDisplayWidthAndExportWidth(): void {
        this.formGroup.setControl("controlTypeId", new FormControl(0));
        this.formGroup.setControl("displayWidth", new FormControl(10));
        this.formGroup.setControl("exportWidth", new FormControl(10));
    }

    private setGridColumnDisplayOrder(isLastActionEdit): void {
        let displayOrder = 1;

        if (this.grid.data === null || this.grid.data === undefined) {
            if (this.useParentComponentData) {
                let parentItemTotal = this.parentItem?.length !== undefined ? this.parentItem.length : 0;
                displayOrder = parentItemTotal + 1;
            }
            else {
                displayOrder = 1;
            }
        }
        else {
            if (isLastActionEdit) {
                displayOrder = this.grid.data?.["total"] + 1;
            }
            else {
                displayOrder = this.grid.data?.["total"] + 2;
            }
        }

        this.formGroup.setControl("displayOrder", new FormControl(displayOrder));
    }

    private applyNumericValidator(field: ColumnSettings, validators: ValidatorFn[], dataItem: any = null) {
        let numericGrid: any[] = field.optionAndValidation?.numericGrid;
        if (numericGrid !== undefined && numericGrid.length > 0) {
            this.formGroup.setControl(field.field, new FormControl(dataItem !== null ? dataItem[field.field] : 0, validators));
            this.applyNumericGridToBaseForm = true;
        }
        else {
            if (field.optionAndValidation?.minimumLength !== undefined) {
                validators.push(Validators.min(field.optionAndValidation?.minimumLength));
            }

            if (field.optionAndValidation?.maximumLength > 0) {
                validators.push(Validators.max(field.optionAndValidation?.maximumLength));
            }

            this.formGroup.setControl(field.field, new FormControl(dataItem !== null ? dataItem[field.field] : 0, validators));
        }
    }

    private applyDateValidator(field: ColumnSettings, validators: ValidatorFn[], dataItem: any = null) {
        let dateGrid: any[] = field.optionAndValidation?.dateGrid;

        if (dateGrid !== undefined && dateGrid.length > 0) {
            const pastThanOperator = field.optionAndValidation?.dateGrid.findIndex(x => x.operator === AppConsts.dateOperators.pastThan &&
                x.dateColumnName && x.dateColumnName !== "") > -1;
            const futureThanOperator = field.optionAndValidation?.dateGrid.findIndex(x => x.operator === AppConsts.dateOperators.futureThan &&
                x.dateColumnName && x.dateColumnName !== "") > -1;
            const betweenOperator = field.optionAndValidation?.dateGrid.findIndex(x => x.operator === AppConsts.dateOperators.between &&
                x.dateColumnName && x.dateColumnName !== "") > -1;

            if (pastThanOperator || futureThanOperator || betweenOperator) {
                this.formGroup.setControl(field.field, new FormControl(dataItem !== null ? dataItem[field.field] : new Date(), validators));
            }
            // Checks if Date Grid is using the Minimum/Maximum Date Validation
            else {
                let minimumDate = dateGrid.find(x => x.minimumDate !== undefined && x.minimumDate !== null)?.minimumDate;
                let maximumDate = dateGrid.find(x => x.maximumDate !== undefined && x.maximumDate !== null)?.maximumDate;

                if (minimumDate && maximumDate) {
                    this.formGroup.setControl(field.field, new FormControl(dataItem !== null ? dataItem[field.field] : new Date(), [...validators,
                    this.dateInRange(field.field, minimumDate, maximumDate, true, true)]));
                }
                else if (minimumDate) {
                    this.formGroup.setControl(field.field, new FormControl(dataItem !== null ? dataItem[field.field] : new Date(), [...validators,
                    this.dateLessThan(field.field, minimumDate, true)]));
                }
                else if (maximumDate) {
                    this.formGroup.setControl(field.field, new FormControl(dataItem !== null ? dataItem[field.field] : new Date(), [...validators,
                    this.dateGreaterThan(field.field, maximumDate, true)]));
                }
            }
        }
        else {
            this.formGroup.setControl(field.field, new FormControl(dataItem !== null ? dataItem[field.field] : new Date(), validators));
        }
    }

    cellClose(args: CellCloseEvent) {
        if (this.useItemsInputAsData) {
            this.updateItemsEmitter.emit(this.items);
        }

        let cellChangeMade = false;
        const { formGroup, column, dataItem } = args;
        this.enteredDoubleClick = false; // Clear up the boolean flag
        this.currentComboBoxValue = ""; // Clears up the current combo box value to properly update the combo box input

        if (!formGroup.controls[column.field].valid && formGroup.controls[column.field].errors !== null) {
            // prevent closing the edited cell if there are invalid values.
            args.preventDefault();
        } else if (formGroup.controls[column.field].dirty) {
            // save to backend etc
            const index = this.items.indexOf(dataItem);
            const col = this.gridSettings.columnsConfig.find(x => x.field === column.field);
            cellChangeMade = dataItem[col.field] !== formGroup.value[col.field];

            if (dataItem["controlTypeId"] !== formGroup.value["controlTypeId"]) {
                dataItem["validations"] = [];
                this.formGroup?.controls["validations"]?.setValue([]);
            }

            // Clears up the corresponding databaseTableFieldId/databaseFieldId because it may not exist with the selected databaseTableId
            if (dataItem["databaseTableId"] !== formGroup.value["databaseTableId"]) {
                if (this.pageName === "DynamicGridDesignMaintenance") {
                    dataItem["databaseFieldId"] = "";
                    this.formGroup?.controls["databaseFieldId"]?.setValue("");
                }
                else {
                    dataItem["databaseTableFieldId"] = "";
                    this.formGroup?.controls["databaseTableFieldId"]?.setValue("");
                }
            }

            if (dataItem["component"] !== formGroup.value["component"]) {
                dataItem["componentId"] = "";
                this.formGroup?.controls["componentId"]?.setValue("");
            }

            dataItem[col.field] = formGroup.value[col.field];

            if (this.pageName !== "") {
                dataItem["controlTypeName"] = this.getGridControlTypeName(dataItem["controlTypeId"]);
                dataItem["formStructureDataSourceName"] = this.getFormStructureNameByGridControlType(dataItem["controlTypeId"]);
                dataItem["formDataDataSourceName"] = this.getFormDataNameByGridControlType(dataItem["controlTypeId"]);
            }

            // Update the grand total if the input is numeric
            if (this.hasGrandTotal && col.filter === "numeric") {
                this.numericGrandTotals[col.field] = this.getGrandTotal(col.field);
            }

            this.items[index] = dataItem;

            this.gridIsLoading();
            let data = {};
            data['rowValue'] = dataItem;
            let request = new SaveDynamicDataRequest();
            request.dataSourceName = this.gridDataDataSourceName;
            request.action = "update";
            request.data = data;

            if (this.isAutoSave) {
                this._dynamicDataServiceProxy.saveDynamicData(request)
                    .pipe(
                        finalize(() => {
                            this.cellIsEdited = false;
                            this.addHandler();
                            this.gridIsLoading(false);
                            this.cd.markForCheck();
                        })
                    ).subscribe(result => {
                        if (result.data !== null) {
                            if (result.data.result === true) {
                                this.gridSettings.gridData = process(this.items, this.gridSettings.state);
                            }
                        }
                    });
            }
            else {
                if (dataItem.id !== "") {
                    if (this.updatedItems.filter(x => x.id === dataItem.id).length === 0 && cellChangeMade) {
                        //NOTE: Add one item at a time, so you don't need to filter the entire items when you're doing save changes
                        //plus, if we support server-side paging in the future, it can also handle that.
                        //Only unshift / add item once.
                        this.updatedItems.unshift(dataItem);
                    }
                }

                this.gridSettings.gridData = process(this.items, this.gridSettings.state);
                this.cellIsEdited = false;

                if (this.formGroup.valid && cellChangeMade) {
                    this.disableActionButton = false;
                    this.disableActionButtonEmitter.emit(this.disableActionButton);
                }

                this.addHandler(true);
                this.gridIsLoading(false);
                this.cd.markForCheck();
            }
        }
        else {
            this.cellIsEdited = false;
            this.addHandler();
        }

        this.refreshComboBoxData();

        document.querySelectorAll(':focus').forEach(el => (el as HTMLElement).blur());

        event?.stopPropagation();
    }

    getGridControlTypeName(controlTypeId: number): string {
        var controlTypeName = "";

        if (controlTypeId === 1) {
            controlTypeName = this.l("TextBox");
        }
        else if (controlTypeId === 2) {
            controlTypeName = this.l("Numeric");
        }
        else if (controlTypeId === 3) {
            controlTypeName = this.l("Date");
        }
        else if (controlTypeId === 4) {
            controlTypeName = this.l("Combobox");
        }
        else if (controlTypeId === 5) {
            controlTypeName = this.l("Dropdown");
        }
        else if (controlTypeId === 6) {
            controlTypeName = this.l("Checkbox");
        }
        else if (controlTypeId === 7) {
            controlTypeName = this.l("Document");
        }
        else if (controlTypeId === 8) {
            controlTypeName = this.l("MaskedTextBox");
        }
        else if (controlTypeId === 10) {
            controlTypeName = "Information Only";
        }

        return controlTypeName;
    }

    getFormControlTypeName(controlTypeId: number): string {
        var controlTypeName = "";

        if (controlTypeId === 0) {
            controlTypeName = "TextBox";
        }
        else if (controlTypeId === 1) {
            controlTypeName = "Text Area";
        }
        else if (controlTypeId === 2) {
            controlTypeName = "Numeric";
        }
        else if (controlTypeId === 3) {
            controlTypeName = "Date";
        }
        else if (controlTypeId === 4) {
            controlTypeName = "Combobox";
        }
        else if (controlTypeId === 5) {
            controlTypeName = "Dropdown";
        }
        else if (controlTypeId === 6) {
            controlTypeName = "Checkbox";
        }
        else if (controlTypeId === 7) {
            controlTypeName = "Radio Button";
        }
        else if (controlTypeId === 8) {
            controlTypeName = "File Upload";
        }
        else if (controlTypeId === 9) {
            controlTypeName = "Display Image/Logo";
        }
        else if (controlTypeId === 10) {
            controlTypeName = "Information Only";
        }
        else if (controlTypeId === 11) {
            controlTypeName = "Static Text";
        }
        else if (controlTypeId === 12) {
            controlTypeName = "Spacer";
        }
        else if (controlTypeId === this.controlTypeNames.GridArray) {
            controlTypeName = "Grid Array Data";
        }
        else if (controlTypeId === this.controlTypeNames.FormArray) {
            controlTypeName = "Form Array Data";
        }

        return controlTypeName;
    }

    getFormStructureNameByGridControlType(controlTypeId: number): string {
        var formStructureName = "";

        if (controlTypeId === 1) {
            formStructureName = "FormStructureInputControlTypeOptionForGrid";
        }
        else if (controlTypeId === 2) {
            formStructureName = "FormStructureNumericControlTypeOption";
        }
        else if (controlTypeId === 3) {
            formStructureName = "FormStructureDateControlTypeOption";
        }
        else if (controlTypeId === 4) {
            formStructureName = "FormStructureComboboxControlTypeOption";
        }
        else if (controlTypeId === 5) {
            formStructureName = "FormStructureDropdownControlTypeOption";
        }
        else if (controlTypeId === 6) {
            formStructureName = "FormStructureCheckboxControlTypeOption";
        }
        else if (controlTypeId === 7 || controlTypeId === 8) {
            formStructureName = "";
        }

        return formStructureName;
    }

    getFormDataNameByGridControlType(controlTypeId: number): string {
        var formDataName = "";

        if (controlTypeId === 1) {
            formDataName = "FormDataInputControlTypeOptionForGrid";
        }
        else if (controlTypeId === 2) {
            formDataName = "FormDataNumericControlTypeOption";
        }
        else if (controlTypeId === 3) {
            formDataName = "FormDataDateControlTypeOption";
        }
        else if (controlTypeId === 4) {
            formDataName = "FormDataComboboxControlTypeOption";
        }
        else if (controlTypeId === 5) {
            formDataName = "FormDataDropdownControlTypeOption";
        }
        else if (controlTypeId === 6) {
            formDataName = "FormDataCheckboxControlTypeOption";
        }
        else if (controlTypeId === 7 || controlTypeId === 8) {
            formDataName = "";
        }

        return formDataName;
    }

    validUnitTypeSelection() {
        return Number(this.formGroup.value['erp_unit_type$name']) < 30;
    }

    checkFormGroupValues() {
        var result = false;

        Object.keys(this.formGroup.value).forEach(key => {
            if (typeof this.formGroup.value[key] === 'string' && this.formGroup.value[key]) {
                result = true;
            }
        });

        if (this.gridStructure?.componentSettings.gridLabelKey === 'OrganizationalUnitsAndEntityRelationships') {
            result = this.validUnitTypeSelection();
        }

        return result;
    }

    keydown(grid: GridComponent, e: KeyboardEvent) {
        this.currentComboBoxValue = ""; // Clears up the current combo box value to properly update the combo box input
        if (e.key === 'Enter' && this.formGroup.valid && this.checkFormGroupValues()) {
            if (this.cellIsEdited) {
                grid.closeCell();
                return;
            }
            else {
                grid.closeCell();
            }

            // for (var key in this.formGroup.value) {
            //     if (key.toLowerCase().includes("date") == true && key.includes("Id") == false) {
            //         this.formGroup.value[key] = this.formGroup.value[key] ? DateTime.fromISO(this.formGroup.value[key].toISOString()) : <any>undefined;
            //     }
            // }

            if (this.parentItem !== undefined) {
                this.formGroup.value.parentId = this.parentItem.id;

                if (this.gridDataDataSourceName === "GridDataGridDetailsAttachDynamicGrid" || this.gridDataDataSourceName === "GridDataGridDetailsAttachDynamicForm") {
                    this.formGroup.value.dynamicPageTypeId = AppConsts.pageType.gridDetailsPage;
                }
                else if (this.gridDataDataSourceName === "GridDataGridPopUpAttachDynamicGrid" || this.gridDataDataSourceName === "GridDataGridPopUpAttachDynamicForm") {
                    this.formGroup.value.dynamicPageTypeId = AppConsts.pageType.gridPopUpPage;
                }
                else if (this.gridDataDataSourceName === "GridDataFormPopUpAttachDynamicGrid" || this.gridDataDataSourceName === "GridDataFormPopUpAttachDynamicForm") {
                    this.formGroup.value.dynamicPageTypeId = AppConsts.pageType.formPopUpPage;
                }
            }

            let request = new SaveDynamicDataRequest();
            let data = {};
            data['rowValue'] = this.formGroup.value;
            request.dataSourceName = this.gridDataDataSourceName;
            request.action = "create";
            request.data = data;

            // Commented this out as this is unnecessary considering the formGroup's validity is checked before it enters here.
            //for (var key in request.data.rowValue) {
            //    if (request.data.rowValue[key] === null) {
            //        return;
            //    }
            //}

            for (let obj in request.data.rowValue) {
                if (request.data[obj] === '') {
                    delete request.data[obj];
                }

                if (obj.toLowerCase().includes("date") === true && request.data[obj] && request.data[obj].isLuxonDateTime === undefined) {
                    request.data[obj] = request.data[obj] ? DateTime.fromISO(request.data[obj].toISOString()) : <any>undefined;
                }
            }

            if (this.isAutoSave) {
                this._dynamicDataServiceProxy.saveDynamicData(request)
                    .pipe(
                        finalize(() => {
                            if (this.hasGrandTotal) {
                                this.getAllGrandTotals();
                            }

                            this.addHandler();
                            this.gridIsLoading(false);
                            this.cd.markForCheck();
                        })
                    ).subscribe(result => {
                        if (result.data !== null) {
                            for (var key in result.data) {
                                if (key.toLowerCase().includes("date") == true && key.includes("Id") == false) {
                                    result.data[key] = result.data[key] ? DateTime.fromISO(result.data[key]) : <any>undefined;
                                }
                            }

                            this.items.unshift(result.data);

                            // use setError call for validation here...

                            this.gridSettings.gridData = process(this.items, this.gridSettings.state);
                        }
                    });
            }
            else {
                if (this.blankDataStructure !== undefined) {
                    let blankDataStructureClone = _cloneDeep(this.blankDataStructure);

                    for (var key in blankDataStructureClone) {
                        for (var key2 in this.formGroup.value) {
                            if (key === key2) {
                                blankDataStructureClone[key] = this.formGroup.value[key2];
                            }
                        }
                    }

                    if (this.items === undefined) {
                        this.items = [];
                    }

                    //NOTE: Add common columns triggered by adding ActiveQuestion column
                    if (blankDataStructureClone.fieldLabelKey === "ActiveQuestion") {
                        let displayOrder = blankDataStructureClone.displayOrder;
                        let dateCreated = blankDataStructureClone.dateCreated;
                        let dateModified = blankDataStructureClone.dateModified;

                        blankDataStructureClone.fieldLabelKey = "ActiveQuestion";
                        blankDataStructureClone.controlTypeId = this.controlTypeNames.CheckBox;
                        blankDataStructureClone.displayWidth = 7;
                        blankDataStructureClone.exportWidth = 7;

                        this.items.unshift(blankDataStructureClone);
                        this.addedItems.unshift(blankDataStructureClone);

                        blankDataStructureClone = _cloneDeep(this.blankDataStructure);
                        blankDataStructureClone.fieldLabelKey = "StartDate";
                        blankDataStructureClone.id = "";
                        blankDataStructureClone.databaseTableId = "sls_client";
                        blankDataStructureClone.databaseFieldId = "sls_created|timestamp without time zone";
                        blankDataStructureClone.controlTypeId = this.controlTypeNames.Date;
                        blankDataStructureClone.displayWidth = 10;
                        blankDataStructureClone.exportWidth = 10;
                        blankDataStructureClone.displayOrder = ++displayOrder;
                        blankDataStructureClone.dateCreated = dateCreated;
                        blankDataStructureClone.dateModified = dateModified;

                        this.items.unshift(blankDataStructureClone);
                        this.addedItems.unshift(blankDataStructureClone);

                        blankDataStructureClone = _cloneDeep(this.blankDataStructure);
                        blankDataStructureClone.fieldLabelKey = "EndDate";
                        blankDataStructureClone.id = "";
                        blankDataStructureClone.databaseTableId = "sls_client";
                        blankDataStructureClone.databaseFieldId = "name|text";
                        blankDataStructureClone.controlTypeId = this.controlTypeNames.Date;
                        blankDataStructureClone.displayWidth = 10;
                        blankDataStructureClone.exportWidth = 10;
                        blankDataStructureClone.displayOrder = ++displayOrder;
                        blankDataStructureClone.dateCreated = dateCreated;
                        blankDataStructureClone.dateModified = dateModified;

                        this.items.unshift(blankDataStructureClone);
                        this.addedItems.unshift(blankDataStructureClone);

                        blankDataStructureClone = _cloneDeep(this.blankDataStructure);
                        blankDataStructureClone.fieldLabelKey = "ModifiedBy";
                        blankDataStructureClone.id = "";
                        blankDataStructureClone.databaseTableId = "sls_client";
                        blankDataStructureClone.databaseFieldId = "subdomain|text";
                        blankDataStructureClone.controlTypeId = this.controlTypeNames.TextBox;
                        blankDataStructureClone.displayWidth = 10;
                        blankDataStructureClone.exportWidth = 10;
                        blankDataStructureClone.displayOrder = ++displayOrder;
                        blankDataStructureClone.dateCreated = dateCreated;
                        blankDataStructureClone.dateModified = dateModified;
                        blankDataStructureClone.editable = false;

                        this.items.unshift(blankDataStructureClone);
                        this.addedItems.unshift(blankDataStructureClone);

                        blankDataStructureClone = _cloneDeep(this.blankDataStructure);
                        blankDataStructureClone.fieldLabelKey = "ModifiedDate";
                        blankDataStructureClone.id = "";
                        blankDataStructureClone.databaseTableId = "sls_client";
                        blankDataStructureClone.databaseFieldId = "archimedes_client_id|text";
                        blankDataStructureClone.controlTypeId = this.controlTypeNames.Date;
                        blankDataStructureClone.displayWidth = 10;
                        blankDataStructureClone.exportWidth = 10;
                        blankDataStructureClone.displayOrder = ++displayOrder;
                        blankDataStructureClone.dateCreated = dateCreated;
                        blankDataStructureClone.dateModified = dateModified;
                        blankDataStructureClone.editable = false;

                        this.items.unshift(blankDataStructureClone);
                        this.addedItems.unshift(blankDataStructureClone);
                    }
                    else {
                        this.items.unshift(blankDataStructureClone);
                        this.addedItems.unshift(blankDataStructureClone);

                        if (this.useItemsInputAsData) {
                            this.updateItemsEmitter.emit(this.items);
                        }
                    }
                }
                else {
                    if (this.items === undefined) {
                        this.items = [];
                    }

                    if (this.pageName === 'DynamicGridDesignMaintenance') {
                        this.formGroup.value["controlTypeName"] = this.getGridControlTypeName(this.formGroup.value["controlTypeId"]);
                        this.formGroup.value["formStructureDataSourceName"] = this.getFormStructureNameByGridControlType(this.formGroup.value["controlTypeId"]);
                        this.formGroup.value["formDataDataSourceName"] = this.getFormDataNameByGridControlType(this.formGroup.value["controlTypeId"]);
                    }
                    else if (this.pageName === 'DynamicNavigationMaintenance') {
                        let counter = 2;
                        this.items.forEach(item => {
                            item.displayOrder = counter;
                            counter++;
                        });

                        this.formGroup.value['displayOrder'] = 1;
                    }

                    this.items.unshift(this.formGroup.value);
                    this.addedItems.unshift(this.formGroup.value);
                }

                this.gridSettings.gridData = process(this.items, this.gridSettings.state);

                if (this.hasGrandTotal) {
                    this.getAllGrandTotals();
                }

                if (this.enableAddRecord === EnableAddRecord.GridAdd) {
                    this.addHandler();
                }
                else {
                    grid.closeRow(-1);
                }

                // APPLY Disable Button here. . .
                this.disableActionButton = false;
                this.disableActionButtonEmitter.emit(this.disableActionButton);

                this.gridIsLoading(false);
                this.cd.markForCheck();
            }

            document.querySelectorAll(':focus').forEach(el => (el as HTMLElement).blur());
        }
        else if (e.key === 'Enter' && !this.formGroup.valid) {
            let errorMessage = this.l("ErrorsEncountered") + "<br>";
            Object.keys(this.formGroup.controls).forEach(key => {
                if (this.formGroup.controls[key].errors) {
                    let retrievedColumn = this.gridSettings.columnsConfig.find(x => x.field === key);
                    errorMessage += this.l(retrievedColumn?.title) + " - ";
                    if (this.formGroup.controls[key]?.errors?.required) {
                        errorMessage += this.l("ErrorFieldRequired");
                    }

                    if (this.formGroup.controls[key]?.errors?.minlength) {
                        errorMessage += this.l("ErrorFieldMinLength", retrievedColumn.optionAndValidation.minimumLength);
                    }

                    if (this.formGroup.controls[key]?.errors?.maxlength) {
                        errorMessage += this.l("ErrorFieldMaxLength", retrievedColumn.optionAndValidation.maximumLength);
                    }

                    if (this.formGroup.controls[key]?.errors?.min) {
                        errorMessage += this.l("ErrorFieldMin", retrievedColumn.optionAndValidation.minimumLength);
                    }

                    if (this.formGroup.controls[key]?.errors?.max) {
                        errorMessage += this.l("ErrorFieldMax", retrievedColumn.optionAndValidation.maximumLength);
                    }

                    if (this.formGroup.controls[key]?.errors?.numericGrid) {
                        if (this.formGroup.controls[key]?.errors?.numericGrid?.comparatorValue === AppConsts.numericOperators.lessThan) {
                            errorMessage += this.l("ErrorFieldMin", this.formGroup.controls[key]?.errors?.numericGrid?.totalValue);
                        }
                        else {
                            errorMessage += this.l("ErrorFieldMax", this.formGroup.controls[key]?.errors?.numericGrid?.totalValue);
                        }
                    }

                    if (this.formGroup.controls[key]?.errors?.dateGrid) {
                        if (this.formGroup.controls[key]?.errors?.dateGrid?.minimumDate && this.formGroup.controls[key]?.errors?.dateGrid?.maximumDate) {
                            errorMessage += this.l("ErrorFieldRange", this.formGroup.controls[key]?.errors?.dateGrid?.minimumDate.toLocaleDateString(), this.formGroup.controls[key]?.errors?.dateGrid?.maximumDate.toLocaleDateString());
                        }
                        else if (this.formGroup.controls[key]?.errors?.dateGrid?.minimumDate) {
                            errorMessage += this.l("ErrorFieldMin", this.formGroup.controls[key]?.errors?.dateGrid?.minimumDate.toLocaleDateString());
                        }
                        else if (this.formGroup.controls[key]?.errors?.dateGrid?.maximumDate) {
                            errorMessage += this.l("ErrorFieldMax", this.formGroup.controls[key]?.errors?.dateGrid?.maximumDate.toLocaleDateString());
                        }
                    }

                    errorMessage += "<br>";
                }
            });

            this.notify.error(errorMessage.substring(0, errorMessage.length - 4));
        }
        else if (e.key === 'Enter' && !this.validUnitTypeSelection()) {
            this.notify.info(this.l("AddErrorForUnitTypes"));
        }

        event.stopPropagation();

        this.refreshComboBoxData();

        this.zone.onStable
            .pipe(take(1))
            .subscribe(() => { this.dragAndDropSubscription = this.handleDragAndDrop(); this.dragAndDropSubscriptionSet = false; });
    }

    applyTooltipError(retrievedColumn) {
        let field = retrievedColumn.field;

        if (this.formGroup?.controls[field]?.errors?.required) {
            return this.l("ErrorFieldRequired");
        }

        if (this.formGroup.controls[field]?.errors?.minlength) {
            return this.l("ErrorFieldMinLength", retrievedColumn.optionAndValidation.minimumLength);
        }

        if (this.formGroup.controls[field]?.errors?.maxlength) {
            return this.l("ErrorFieldMaxLength", retrievedColumn.optionAndValidation.maximumLength);
        }

        if (this.formGroup.controls[field]?.errors?.min) {
            return this.l("ErrorFieldMin", retrievedColumn.optionAndValidation.minimumLength);
        }

        if (this.formGroup.controls[field]?.errors?.max) {
            return this.l("ErrorFieldMax", retrievedColumn.optionAndValidation.maximumLength);
        }

        if (this.formGroup.controls[field]?.errors?.numericGrid) {
            if (this.formGroup.controls[field]?.errors?.numericGrid?.comparatorValue === AppConsts.numericOperators.lessThan) {
                return this.l("ErrorFieldMin", this.formGroup.controls[field]?.errors?.numericGrid?.totalValue);
            }
            else {
                return this.l("ErrorFieldMax", this.formGroup.controls[field]?.errors?.numericGrid?.totalValue);
            }
        }

        if (this.formGroup.controls[field]?.errors?.dateGrid) {
            if (this.formGroup.controls[field]?.errors?.dateGrid?.minimumDate && this.formGroup.controls[field]?.errors?.dateGrid?.maximumDate) {
                return this.l("ErrorFieldRange", this.formGroup.controls[field]?.errors?.dateGrid?.minimumDate.toLocaleDateString(), this.formGroup.controls[field]?.errors?.dateGrid?.maximumDate.toLocaleDateString());
            }
            else if (this.formGroup.controls[field]?.errors?.dateGrid?.minimumDate) {
                return this.l("ErrorFieldMin", this.formGroup.controls[field]?.errors?.dateGrid?.minimumDate.toLocaleDateString());
            }
            else if (this.formGroup.controls[field]?.errors?.dateGrid?.maximumDate) {
                return this.l("ErrorFieldMax", this.formGroup.controls[field]?.errors?.dateGrid?.maximumDate.toLocaleDateString());
            }
        }
    }

    open() {
        this.opened = true;
    }

    close() {
        this.opened = false;
    }

    public onPaste(data: any): void {
        if (document.activeElement.tagName !== 'INPUT') {
            let item = {};
            const format = "text/plain";
            const pasteValues = data.clipboardData.getData(format).split("\n").map(function (x) { return x.split("\t") });

            let pasteList = [];
            for (var pasteValue of pasteValues) {
                if (pasteValue.length === this.gridSettings.columnsConfig.length) {
                    let i = 0;

                    this.gridSettings.columnsConfig.forEach(column => {
                        if (column.filter === 'date') {
                            item[column.field] = new Date(pasteValue[i]);
                        }
                        else if (column.editor === 'boolean') {
                            item[column.field] = pasteValue[i] === 'true';
                        }
                        else if (column.filter === 'numeric') {
                            item[column.field] = Number(pasteValue[i]);
                        }
                        else if (column.editor === 'combobox') {
                            let selectedDataSource = this.dataSourceItems.find(x => x.dataSourceName === column.dataSourceName);
                            let retrievedValue = selectedDataSource.dataSourceItems.find(x => x.value === pasteValue[i]);
                            item[column.field] = retrievedValue ?? pasteValue[i];
                        }
                        else {
                            item[column.field] = pasteValue[i];
                        }
                        i++;
                    });
                    pasteList.push(item);
                }
                else {
                    this.notify.warn(this.l("ErrorPasteValue"));// Change to Error in Paste Value
                    return;
                }
            }

            if (pasteList.length > 0) {
                this.items.unshift(...pasteList);
                this.gridSettings.gridData = process(this.items, this.gridSettings.state);
            }
        }
    }

    closeDialog(): void {
        this.dialogOpened = false;
    }

    moveToParent($event): void {
        if ($event.isSuccess) {
            if ($event.itemToMove !== undefined) {
                $event.itemToMove.dynamicMenuParentId = this.items[0].dynamicMenuParentId;
                this.items.push($event.itemToMove);
                this.gridSettings.gridData = process(this.items, this.gridSettings.state);
            }

            this.disableActionButton = false;
            this.disableActionButtonEmitter.emit(this.disableActionButton);
        }
    }

    deleteChildItem($event): void {
        if ($event.isSuccess) {
            if (this.isChildGrid) {
                this.deleteChildItemEmitter.emit({ isSuccess: true, itemToDelete: $event.itemToDelete });
            }
            else {
                this.deletedIds.push($event.itemToDelete);
            }
        }
    }

    modalSave($event): void {
        if ($event.isSuccess) {
            if (this.isAutoSave) {
                this.getDynamicDataItems();
            }

            this.closeDialog();

            if ($event.parentId !== undefined) {
                const uniqueItem = this.updatedItems.filter(x => x.id === $event.parentId.id).length === 0;

                if (uniqueItem) {
                    this.updatedItems.unshift(this.items.filter(x => x.id === $event.parentId.id)[0]);
                }
            }

            if ($event.clonedItem !== undefined) {
                let clonedItem = this.formatItemDateWithoutId($event.clonedItem);
                this.items.unshift(clonedItem);
                this.populateGrid();
            }

            if ($event.itemToMove !== undefined) {
                let selectedItem = this.items.find(x => x.id === $event.selectedItem.id);
                let itemToMoveIndex = this.items.findIndex(x => x.id === $event.itemToMove.id);

                if (selectedItem.gridPopUpAttachedDynamicNavigationComponents === undefined) {
                    selectedItem.gridPopUpAttachedDynamicNavigationComponents = [];
                }

                selectedItem.gridPopUpAttachedDynamicNavigationComponents.push($event.itemToMove);

                let counter = 1;
                selectedItem.gridPopUpAttachedDynamicNavigationComponents.forEach(item => {
                    item.displayOrder = counter;
                    counter++;
                });

                this.items.splice(itemToMoveIndex, 1);

                this.gridSettings.gridData = process(this.items, this.gridSettings.state);
                this.gridDetailsDynamicGridComponents.forEach(component => {
                    component.gridSettings.gridData = process(component.items, component.gridSettings.state);
                });
            }

            this.disableActionButton = false;
            this.disableActionButtonEmitter.emit(this.disableActionButton);
        }
    }

    isInputRequired(field: any) {
        let column: any = this.gridSettings.columnsConfig.find(x => x.field === field);

        if (column.optionAndValidation === undefined)
            return;
        if (column.optionAndValidation.required) {
            if (this.isKendoInput(column.controlTypeId) === true || this.isKendoEditor(column.editor) === true) {
                let inputList: any = document.getElementsByClassName(field);
                if (inputList !== null) {
                    for (var input of inputList) {
                        input.children[0].style.borderLeftColor = "red";
                        input.children[0].style.borderWidth = "2px";
                        input.children[0].style.backgroundColor = "#FFF";

                        if (this.formGroup.controls[field]?.invalid && !this.formGroup.controls[field]?.dirty) {
                            input.children[0].style.backgroundColor = "#FFF";
                        }
                        else if (this.formGroup.controls[field]?.errors?.customError && this.formGroup.controls[field]?.dirty) {
                            input.children[0].style.backgroundColor = "yellow";
                        }
                        else if (this.formGroup.controls[field]?.invalid && this.formGroup.controls[field]?.dirty && this.focusedDictionary.indexOf(field) > -1) {
                            input.children[0].style.backgroundColor = "rgb(220 53 69 / 25%)";
                        }
                    }
                }
            }
            else {
                if (field === 'headerClass' && this.focusedDictionary.indexOf(field) > -1 && column.optionAndValidation.syncedWithERP && this.formGroup.controls[field].value.length > 5 && this.formGroup.controls[field].dirty) {
                    return "border-left-color: red; border-right-color: green; background-color: yellow; border-width: 2px;";
                }
                else if (field === 'headerClass' && this.formGroup?.controls[field].value.length > 5 && this.formGroup?.controls[field].dirty) {
                    return "border-left-color: red; border-right-color: red; background-color: yellow; border-width: 2px;";
                }

                if (this.formGroup?.controls[field]?.errors?.customError && this.formGroup?.controls[field]?.dirty) {
                    return "border-left-color: red; background-color: yellow; border-width: 2px;";
                }
                else if (this.formGroup?.controls[field]?.invalid && this.formGroup?.controls[field]?.dirty && this.focusedDictionary.indexOf(field) > -1) {
                    return "border-left-color: red; background-color: rgb(220 53 69 / 25%); border-width: 2px;";
                }
                else if (this.formGroup?.controls[field]?.invalid && (this.formGroup?.controls[field]?.dirty || this.formGroup?.controls[field]?.touched) && this.focusedDictionary.indexOf(field) === -1) {
                    return "border-left-color: red; background-color: #FFF; border-width: 2px;";
                }
                else if (column.editor === 'boolean') {
                    return "";
                }
                else {
                    return "border-left-color: red; background-color: #FFF; border-width: 2px;";
                }
            }
        }
    }

    onBlurChange(field: any) {
        let column: any = this.gridSettings.columnsConfig.find(x => x.field === field);

        if (column.optionAndValidation === undefined)
            return;

        if (column.optionAndValidation.required && column.optionAndValidation.syncedWithERP) {
            if (this.isKendoInput(column.controlTypeId) === true || this.isKendoEditor(column.editor) === true) {
                let inputList: any = document.getElementsByClassName(field);
                if (inputList !== null) {
                    for (var input of inputList) {
                        input.children[0].style.borderLeftColor = "red";
                        input.children[0].style.borderRightColor = "";
                        input.children[0].style.borderWidth = "2px";
                    }
                }
            }
            else {
                let input: any = document.getElementsByClassName(field);
                input[0].style = "border-left-color: red; border-width: 2px;";
            }

            let focusedIndex = this.focusedDictionary.indexOf(field);
            if (focusedIndex >= 0) {
                this.focusedDictionary.splice(focusedIndex, 1);
            }
        }
        else if (column.optionAndValidation.required) {
            if (this.isKendoInput(field.controlTypeId) === true || this.isKendoEditor(column.editor) === true) {
                let inputList: any = document.getElementsByClassName(field);
                if (inputList !== null) {
                    for (var input of inputList) {
                        input.children[0].style.borderLeftColor = "red";
                        input.children[0].style.borderWidth = "2px";
                        input.children[0].style.backgroundColor = "#FFF";
                    }
                }
            }
            else {
                let input: any = document.getElementsByClassName(field);
                input[0].style = "border-left-color: red; border-width: 2px; background-color: #FFF;";
            }

            let focusedIndex = this.focusedDictionary.indexOf(field);
            if (focusedIndex >= 0) {
                this.focusedDictionary.splice(focusedIndex, 1);
            }
        }
        else if (column.optionAndValidation.syncedWithERP) {
            if (this.isKendoInput(column.controlTypeId) === true || this.isKendoEditor(column.editor) === true) {
                let inputList: any = document.getElementsByClassName(field);
                if (inputList !== null) {
                    for (var input of inputList) {
                        input.children[0].style.borderRightColor = "";
                        input.children[0].style.borderWidth = "2px";
                        input.children[0].style.backgroundColor = "#FFF";
                    }
                }
            }
            else {
                let input: any = document.getElementsByClassName(field);
                input[0].style = "border-width: 2px; background-color: #FFF;";
            }

            let focusedIndex = this.focusedDictionary.indexOf(field);
            if (focusedIndex >= 0) {
                this.focusedDictionary.splice(focusedIndex, 1);
            }
        }
    }

    onFocusChange(field: any) {
        this.focusedInputField = field;
        this.formGroup.controls[field]?.markAsDirty();

        let column: any = this.gridSettings.columnsConfig.find(x => x.field === field);

        if (column.optionAndValidation === undefined)
            return;

        if (this.focusedDictionary.indexOf(field) === -1) {
            this.focusedDictionary = [];
            this.focusedDictionary.push(field);
        }

        if (column.optionAndValidation.required && column.optionAndValidation.syncedWithERP) {
            if (this.isKendoInput(field.controlTypeId) === true || this.isKendoEditor(column.editor) === true) {
                let inputList: any = document.getElementsByClassName(field);
                if (inputList !== null) {
                    for (var input of inputList) {
                        input.children[0].style.borderLeftColor = "red";
                        input.children[0].style.borderRightColor = "green";
                        input.children[0].style.borderWidth = "2px";
                        input.children[0].style.backgroundColor = "rgb(220 53 69 / 25%)";
                    }
                }
            }
            else {
                let input: any = document.getElementsByClassName(field);
                input[0].style = "border-left-color: red; border-right-color: green; background-color: rgb(220 53 69 / 25%); border-width: 2px;";
                if (this.formGroup.controls[field]?.invalid && this.formGroup.controls[field]?.dirty && this.focusedDictionary.indexOf(field) > -1) {
                    input[0].style = "border-left-color: red; border-right-color: green; background-color: rgb(220 53 69 / 25%); border-width: 2px;";
                }
                else if (this.formGroup.controls[field]?.invalid && (this.formGroup.controls[field]?.dirty || this.formGroup.controls[field]?.touched) && this.focusedDictionary.indexOf(field) === -1) {
                    input[0].style = "border-left-color: red; border-right-color: green; background-color: #FFF; border-width: 2px;";
                }
                else {
                    input[0].style = "border-left-color: red; border-right-color: green; background-color: #FFF; border-width: 2px;";
                }
            }
        }
        else if (column.optionAndValidation.required) {
            if (this.isKendoInput(field.controlTypeId) === true || this.isKendoEditor(column.editor) === true) {
                let inputList: any = document.getElementsByClassName(field);
                if (inputList !== null) {
                    for (var input of inputList) {
                        input.children[0].style.borderLeftColor = "red";
                        input.children[0].style.borderWidth = "2px";
                        input.children[0].style.backgroundColor = "rgb(220 53 69 / 25%)";
                    }
                }
            }
            else {
                let input: any = document.getElementsByClassName(field);
                input[0].style = "border-left-color: red; border-width: 2px; background-color: rgb(220 53 69 / 25%);";
                if (this.formGroup.controls[field]?.invalid && this.formGroup.controls[field]?.dirty && this.focusedDictionary.indexOf(field) > -1) {
                    input[0].style = "border-left-color: red; background-color: rgb(220 53 69 / 25%); border-width: 2px;";
                }
                else if (this.formGroup.controls[field]?.invalid && (this.formGroup.controls[field]?.dirty || this.formGroup.controls[field]?.touched) && this.focusedDictionary.indexOf(field) === -1) {
                    input[0].style = "border-left-color: red; background-color: #FFF; border-width: 2px;";
                }
                else {
                    input[0].style = "border-left-color: red; background-color: #FFF; border-width: 2px;";
                }
            }
        }
        else if (column.optionAndValidation.syncedWithERP) {
            if (this.isKendoInput(column.controlTypeId) === true || this.isKendoEditor(column.editor) === true) {
                let inputList: any = document.getElementsByClassName(field);
                if (inputList !== null) {
                    for (var input of inputList) {
                        input.children[0].style.borderRightColor = "green";
                        input.children[0].style.borderWidth = "2px";
                        input.children[0].style.backgroundColor = "#FFF";
                    }
                }
            }
            else {
                let input: any = document.getElementsByClassName(field);
                input[0].style = "border-right-color: green; background-color: #FFF; border-width: 2px;";
            }
        }

        this.updateInputStyles();
    }

    retrieveFileRestrictions(optionAndValidation) {
        var fileRestrictions: FileRestrictions = {};

        if (optionAndValidation?.documentType) {
            fileRestrictions.allowedExtensions = [optionAndValidation.documentType];
        }

        if (optionAndValidation?.fileSize) {
            fileRestrictions.maxFileSize = optionAndValidation.fileSize;
        }

        return fileRestrictions;
    }

    getDynamicStructure() {
        let parentId = "";
        if (this.retrieveDataBy != undefined) {
            parentId = this.retrieveDataBy;
        }

        this._dynamicDataServiceProxy.retrieveDynamicStructure(this.pageName, parentId)
            .pipe(
                finalize(() => {
                    this.gridIsLoading(false);
                    this.cd.markForCheck();
                })
            ).subscribe(result => {
                if (result.data !== null) {
                    result.data.items.forEach(item => {
                        item = this.formatItemDate(item);
                    });

                    // this.dynamicFormName = result.data.items[0]?.parentName;

                    // //NOTE: For Dynamic Pages
                    // if (this.isDynamicPages()) {
                    //     this.gridComponents = result.data.items[0]?.attachedDynamicGridComponents;
                    //     this.formComponents = result.data.items[0]?.attachedDynamicFormComponents;
                    // }

                    // //NOTE: For Streamlinea Pages
                    // if (result.data.items.length > 0) {
                    //     this.gridStructures = result.data.items[0]?.attachedDynamicGridStructures;
                    //     this.formStructures = result.data.items[0]?.attachedDynamicFormStructures;
                    // }

                    // if (this.formStructures !== undefined && this.formStructures.length > 0 && this.formStructures[0]?.componentSettings !== undefined) {
                    //     this.fieldLabelWidth = this.formStructures[0].componentSettings.fieldLabelWidth;
                    //     this.fieldControlWidth = this.formStructures[0].componentSettings.fieldControlWidth;
                    //     this.groupFieldSetWidth = this.formStructures[0].componentSettings.groupFieldSetWidth;
                    //     this.groupFieldSetGap = this.formStructures[0].componentSettings.groupFieldSetGap;
                    //     this.showBorder = this.formStructures[0].componentSettings.showBorder;
                    //     this.showGuide = this.formStructures[0].componentSettings.showGuide;
                    // }
                }
            });
    }

    setWheelPropagation(): boolean {
        if (this.pageName === "DynamicNavigationMaintenance" || this.pageName === "DynamicPageMaintenance" || this.pageName === "DynamicFormMaintenance" || this.pageName === "DynamicGridMaintenance" || this.pageName === "DynamicFormDesignMaintenance" || this.pageName === "DynamicGridDesignMaintenance") {
            return true;
        }
        else {
            return false;
        }
    }

    private handleDragAndDrop(): Subscription {
        const sub = new Subscription(() => { });
        return sub;//TODO: Temporary. Check if this will fix the hanging issue in the Dynamic Form Design Maintenance when there are a lot of records.

        let draggedItemIndex;

        const tableRows = Array.from(document.querySelectorAll(".k-grid tr"));

        // if (tableRows[1].className.includes("norecords") === false) {
        //     this.dragAndDropSubscriptionSet = true;
        // }

        tableRows.forEach((item) => {
            this.renderer.setAttribute(item, "draggable", "true");
            const dragStart = fromEvent<DragEvent>(item, "dragstart");
            const dragOver = fromEvent(item, "dragover");
            const dragEnd = fromEvent(item, "dragend");

            sub.add(
                dragStart
                    .pipe(
                        tap(({ dataTransfer }) => {
                            try {
                                const dragImgEl = document.createElement("span");
                                dragImgEl.setAttribute(
                                    "style",
                                    "position: absolute; display: block; top: 0; left: 0; width: 0; height: 0;"
                                );
                                document.body.appendChild(dragImgEl);
                                dataTransfer.setDragImage(dragImgEl, 0, 0);
                            } catch (err) {
                                // IE doesn't support setDragImage
                            }
                            try {
                                // Firefox won't drag without setting data
                                dataTransfer.setData("application/json", "");
                            } catch (err) {
                                // IE doesn't support MIME types in setData
                            }
                        })
                    )
                    .subscribe(({ target }) => {
                        const row: HTMLTableRowElement = <HTMLTableRowElement>target;
                        draggedItemIndex = row.rowIndex - 1;

                        const dataItem = this.gridSettings.gridData.data[draggedItemIndex];
                        if (dataItem?.dragging !== undefined) {
                            dataItem.dragging = true;
                        }
                    })
            );

            sub.add(
                dragOver.subscribe((e: any) => {
                    e.preventDefault();
                    const dataItem = this.gridSettings.gridData.data.splice(draggedItemIndex, 1)[0];

                    const dropIndex = closest(e.target, tableRow).rowIndex - 1;

                    const dropItem = this.gridSettings.gridData.data[dropIndex];

                    draggedItemIndex = dropIndex;
                    this.zone.run(() => {
                        this.gridSettings.gridData.data.splice(dropIndex, 0, dataItem);
                    });
                })
            );

            sub.add(
                dragEnd.subscribe((e: any) => {
                    e.preventDefault();
                    const dataItem = this.gridSettings.gridData.data[draggedItemIndex];

                    if (dataItem?.dragging !== undefined) {
                        dataItem.dragging = false;
                    }

                    this.cd.markForCheck();
                })
            );
        });

        return sub;
    }

    private handleDragAndDrop2(): Subscription {
        const sub = new Subscription(() => { });
        let draggedItemIndex;

        const tableRows = Array.from(document.querySelectorAll('.k-grid tr'));

        if (tableRows[1].className.includes("norecords") === false) {
            this.dragAndDropSubscriptionSet = true;
        }

        tableRows.forEach(item => {
            this.renderer.setAttribute(item, 'draggable', 'true');
            const dragStart = fromEvent<DragEvent>(item, 'dragstart');
            const dragOver = fromEvent(item, 'dragover');
            const dragEnd = fromEvent(item, 'dragend');

            sub.add(dragStart.subscribe(({ target }) => {
                if (this.selectedRows.length > 0 && this.detailExpandCount === 0) {
                    draggedItemIndex = this.selectedRows[0].index;
                    this.cd.markForCheck();
                }
            }));

            sub.add(dragOver.subscribe((e: any) => {
                if (this.selectedRows.length > 0 && this.detailExpandCount === 0) {
                    e.preventDefault();
                    const dataItem = this.items.splice(draggedItemIndex, 1)[0];
                    if (dataItem.id === this.selectedRows[0].dataItem.id) {
                        const dropIndex = closest(e.target, tableRow).rowIndex;
                        this.selectedRows[0].index = draggedItemIndex = dropIndex;

                        this.items.splice(dropIndex, 0, dataItem);
                        this.gridSettings.gridData = process(this.items, this.gridSettings.state);
                        this.cd.markForCheck();
                    }
                    else {
                        this.items.splice(draggedItemIndex, 0, dataItem);
                        this.gridSettings.gridData = process(this.items, this.gridSettings.state);
                        this.cd.markForCheck();
                    }
                }
            }));

            sub.add(dragEnd.subscribe((e: any) => {
                e.preventDefault();
                this.cd.markForCheck();
            }));
        });

        return sub;
    }

    disableActionButtonEmitted(e) {
        this.disableActionButton = e;
        this.disableActionButtonEmitter.emit(this.disableActionButton);
    }

    filterCategoryChange(values: any[], colField: string, filterService: FilterService): void {
        filterService.filter({
            filters: values.map(value => ({
                field: colField,
                operator: 'contains',
                value: value.id
            })),
            logic: 'and'
        });
    }

    subChildGridsUpdate() {
        abp.event.on('app.grid_cell_' + this.gridStructure.componentSettings.gridId + '.onClick', this.onCellClick);
    }

    unSubChildGridsUpdate() {
        abp.event.off('app.grid_cell_' + this.gridStructure.componentSettings.gridId + '.onClick', this.onCellClick);
    }

    onCellClick = (items, gridStructure) => {
        // UPDATE THE CONTENTS OF THE GRID . . .
        this.gridDependencyLoading = true;

        this.getDynamicDataItems(items, gridStructure);

        this.retrieveRequiredData(items, gridStructure)
    }

    hasDropDownTree(): boolean {
        const dropDownTreeKeys = ['NonChartOfAccountsFields', 'HeaderFieldsDefinition', 'DistributionGrid'];
        return dropDownTreeKeys.includes(this.gridStructure?.componentSettings?.gridLabelKey);
    }

    hasTreeView(): boolean {
        const treeViewKeys = ['OrganizationalUnitsAndEntityRelationships'];
        return treeViewKeys.includes(this.gridStructure?.componentSettings?.gridLabelKey);
    }

    gridAddRow(): void {
        if (this.hasDropDownTree()) {
            if (this.selectedTransactionTypeId !== undefined) {
                this.grid.addRow(this.formGroup);
            } else {
                this.grid.closeRow();
            }
        } else {
            this.grid.addRow(this.formGroup);
        }
    }

    noRecordsAvailableLabel(): string {
        if (this.hasDropDownTree()) {
            if (this.selectedTransactionTypeId !== undefined) {
                return this.l('NoRecordsAvailable');
            } else {
                return this.l('PleaseSelectTransactionTypeFirst');
            }
        } else {
            return this.l('NoRecordsAvailable');
        }
    }

    getComponentName(): string {
        return "DynamicGridComponent";
    }
}